<template>
  <div ref="pageTop" class="tele-sales-page py-3">
    <header class="tele-toolbar">
      <div class="tele-toolbar__title">
        <span class="tele-toolbar__accent" aria-hidden="true"></span>
        <h1>Tele Sales</h1>
      </div>
      <div class="tele-toolbar__meta">
        <span class="tele-toolbar__updated">Last updated: {{ lastUpdatedLabel }}</span>
        <CButton color="secondary" size="sm" variant="outline" :disabled="isLoading" @click="loadBootstrapData">
          {{ isLoading ? 'Loading…' : 'Refresh' }}
        </CButton>
        <CButton color="danger" size="sm" variant="outline" :disabled="isSubmitting" @click="resetForm">Reset</CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="tele-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="tele-alert">{{ successMessage }}</CAlert>

    <CRow class="tele-grid">
      <CCol lg="8" class="tele-main">
        <section class="tele-section">
          <header class="tele-section__head">
            <h2><span class="step-num">1</span> Find Contact</h2>
            <span class="tele-section__hint">Search by phone to auto-fill</span>
          </header>
          <div class="tele-section__body">
            <CForm @submit.prevent="searchContact">
              <label class="tele-label">Phone Number<span class="tele-label__required" aria-hidden="true">*</span></label>
              <div class="phone-row" data-checklist="phone">
                <AppDropdown
                  class="phone-row__country"
                  :value="countryCode"
                  :options="countryOptions"
                  searchable
                  search-placeholder="Search country…"
                  @update:value="countryCode = $event"
                />
                <AppInput class="phone-row__input" no-margin type="tel" placeholder="Phone number" :value="form.contact_phone" :maxlength="15" @update:value="setPhone($event)" />
                <CButton type="submit" color="primary" class="phone-row__btn" :disabled="isSearching">
                  {{ isSearching ? 'Searching…' : 'Search' }}
                </CButton>
              </div>
              <small :class="['phone-row__hint', searchHintClass]">{{ searchHint || ' ' }}</small>
              <div v-if="recentOrders.length" class="tele-recent-order-note" role="alert">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div class="tele-recent-order-note__text">
                  <strong>Recent order found.</strong>
                  Please verify the orders before placing a new one to avoid duplicates.
                  <span class="tele-recent-order-note__list">
                    <span v-for="(order, idx) in recentOrders" :key="order.id" class="tele-recent-order-note__chip">
                      <span class="tele-recent-order-note__chip-id">{{ order.order_number }}</span>
                      <span class="tele-recent-order-note__chip-date">{{ formatOrderDate(order.order_date) }}</span><span v-if="idx &lt; recentOrders.length - 1">,</span>
                    </span>
                  </span>
                </div>
              </div>
              <div v-if="highPriorityCall" class="tele-high-priority-note" role="alert">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                </svg>
                <div class="tele-high-priority-note__text">
                  This customer belongs to the high priority of — <strong>{{ highPriorityCall.caller || 'Unknown staff' }}</strong>
                </div>
              </div>
            </CForm>
          </div>
        </section>

        <section v-if="hasContactHistory" class="tele-section history-section">
          <header
            class="tele-section__head history-section__head"
            role="button"
            tabindex="0"
            :aria-expanded="!historyCollapsed"
            @click="historyCollapsed = !historyCollapsed"
            @keydown.enter.prevent="historyCollapsed = !historyCollapsed"
            @keydown.space.prevent="historyCollapsed = !historyCollapsed"
          >
            <h2>Contact History</h2>
            <div class="history-section__head-meta">
              <span class="tele-section__hint">{{ historyCollapsed ? historyCondensedSummary : 'Past activity for this number' }}</span>
              <span :class="['history-section__chevron', { 'is-open': !historyCollapsed }]" aria-hidden="true">▾</span>
            </div>
          </header>
          <div v-show="!historyCollapsed" class="tele-section__body">
            <div class="history-stats">
              <div class="history-stat">
                <span class="history-stat__label">Calls</span>
                <span class="history-stat__value">{{ recentCalls.length }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Orders</span>
                <span class="history-stat__value">{{ contactOrders.length }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Lifetime</span>
                <span class="history-stat__value">{{ formatCurrency(lifetimeSpend) }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Last Call</span>
                <span class="history-stat__value history-stat__value--small">{{ lastCallSummary }}</span>
              </div>
            </div>

            <div class="history-grid">
              <div class="history-col">
                <h3 class="history-title">Recent Calls</h3>
                <div v-if="recentCalls.length" class="history-list">
                  <div v-for="(call, idx) in recentCalls" :key="`call-${idx}`" :class="['history-item', 'history-item--call', callStatusClass(call.status), { 'is-high-priority': highPriorityCall === call, 'is-sold-match': isMatchedSaleCall(call) }]">
                    <div class="history-item__line">
                      <span class="history-item__date">
                        {{ formatCallDate(call.date) }}
                        <span v-if="formatCallTime(call)" class="history-item__time">{{ formatCallTime(call) }}</span>
                      </span>
                      <span :class="['history-pill', callStatusClass(call.status)]">{{ call.status }}</span>
                    </div>
                    <div class="history-item__sub">{{ call.caller || 'Unknown caller' }}</div>
                  </div>
                </div>
                <div v-else class="history-empty">No previous calls.</div>
              </div>

              <div class="history-col">
                <h3 class="history-title">Recent Orders</h3>
                <div v-if="contactOrders.length" class="history-list">
                  <div v-for="order in contactOrders" :key="order.id" :class="['history-item', { 'is-recent-order': recentOrderIdSet.has(Number(order.id)), 'is-sold-match': matchedSaleOrderIds.has(Number(order.id)) }]">
                    <div class="history-item__line">
                      <span class="history-item__strong">{{ order.order_number }}</span>
                      <span class="history-item__amount">{{ formatCurrency(order.grand_total) }}</span>
                    </div>
                    <div class="history-item__sub">
                      {{ formatOrderDate(order.order_date) }}
                      <span class="history-dot">·</span>
                      <span :class="paymentStatusClass(order.payment_status)">{{ formatLabel(order.payment_status) }}</span>
                      <span class="history-dot">·</span>
                      {{ formatLabel(order.order_status) }}
                    </div>
                    <ul v-if="order.order_items && order.order_items.length" class="history-products">
                      <li v-for="item in order.order_items" :key="item.id">
                        <span class="history-products__name">{{ item.name }}</span>
                        <span class="history-products__qty">× {{ item.quantity }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div v-else class="history-empty">No previous orders.</div>
              </div>

              <div class="history-col">
                <h3 class="history-title">Patient Visits</h3>
                <div v-if="contactHmsVisits.length" class="history-list">
                  <div v-for="visit in contactHmsVisits" :key="visit.id" class="history-item">
                    <div class="history-item__line">
                      <span class="history-item__date">{{ formatOrderDate(visit.reg_date) }}</span>
                      <span v-if="visit.op_no" class="history-pill is-info">OP {{ visit.op_no }}</span>
                    </div>
                    <div class="history-item__sub">
                      <span v-if="visit.patient_name">{{ visit.patient_name }}</span>
                      <!-- <template v-if="visit.age || visit.gender">
                        <span v-if="visit.patient_name" class="history-dot">·</span>{{ formatPatientMeta(visit) }}
                      </template> -->
                      <!-- <template v-if="visit.place">
                        <span class="history-dot">·</span>{{ visit.place }}
                      </template> -->
                    </div>
                  </div>
                </div>
                <div v-else class="history-empty">No previous visits.</div>
              </div>
            </div>
          </div>
        </section>

        <section class="tele-section call-section d-lg-none">
          <header class="tele-section__head">
            <h2>Call Control</h2>
            <span :class="['call-dot', callRunning ? 'is-on' : 'is-off']" :title="callRunning ? 'Call active' : 'Idle'"></span>
          </header>
          <div class="tele-section__body call-body">
            <div class="call-timer">{{ formattedTimer }}</div>
            <div class="call-actions">
              <CButton color="primary" class="w-100" :disabled="isCallActive || callRunning" @click="startCall">Start Call</CButton>
              <CButton color="danger" class="w-100" :disabled="!callRunning" @click="endCall">End Call</CButton>
            </div>
          </div>
        </section>

        <section class="tele-section">
          <header class="tele-section__head">
            <h2><span class="step-num">2</span> Customer Information</h2>
            <label class="tele-head-toggle">
              <input type="checkbox" :checked="includeGstin" @change="toggleGstin($event.target.checked)">
              <span>Include GST IN</span>
            </label>
          </header>
          <div class="tele-section__body">
            <CRow v-if="includeGstin">
              <CCol md="12">
                <AppInput
                  label="GST IN"
                  placeholder="Enter GST IN"
                  :value="form.contact_gstn"
                  @update:value="form.contact_gstn = String($event || '').trim()"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol :md="showDemographics ? 6 : 8" data-checklist="name">
                <AppInput label="Full Name" required placeholder="Enter Full Name" :value="form.contact_name" @update:value="form.contact_name = $event" />
              </CCol>
              <CCol :md="showDemographics ? 3 : 4" data-checklist="alt_phone">
                <AppInput
                  label="Alternate Phone"
                  required
                  type="tel"
                  :maxlength="15"
                  placeholder="Backup number"
                  :value="form.contact_phone_2"
                  @update:value="setAlternatePhone($event)"
                />
              </CCol>
              <CCol v-if="showDemographics" md="3" ref="demographicsAnchor">
                <AppInput label="Age" type="number" :value="form.age" @update:value="form.age = $event" />
              </CCol>
            </CRow>
            <CRow>
              <CCol v-if="showDemographics" md="3" class="form-group">
                <label class="tele-label">Gender</label>
                <AppDropdown :value="form.gender" :options="genderOptions" placeholder="Select" @update:value="form.gender = $event" />
              </CCol>
              <CCol v-if="showDemographics" md="3" class="form-group">
                <label class="tele-label">Marital Status</label>
                <AppDropdown :value="form.marital_status" :options="maritalStatusOptions" placeholder="Select" @update:value="form.marital_status = $event" />
              </CCol>
              <CCol :md="showDemographics ? 3 : 6" class="form-group">
                <label class="tele-label">Call Type</label>
                <AppDropdown :value="form.call_type" :options="callTypeOptions" placeholder="Select" @update:value="onCallTypeChange" />
              </CCol>
              <CCol :md="showDemographics ? 3 : 6" class="form-group" data-checklist="call_status">
                <label class="tele-label">Call Status</label>
                <AppDropdown :value="form.call_status" :options="visibleCallStatusOptions" placeholder="Select" @update:value="onCallStatusChange" />
              </CCol>
            </CRow>
            <CRow>
              <CCol v-if="showFollowupGrade" md="6" class="form-group">
                <label class="tele-label">Followup Grade</label>
                <AppDropdown :value="form.followup_grade" :options="followupGradeOptions" placeholder="Select" @update:value="form.followup_grade = $event" />
              </CCol>
              <CCol :md="showFollowupGrade ? 6 : 12" class="form-group">
                <label class="tele-label">Schedule Next Call</label>
                <SingleDatePicker :value="form.next_call" @input="form.next_call = $event" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4" data-checklist="pincode" :class="{ 'is-attention-pulse': pincodeAttentionPulse }">
                <AppInput ref="contactPincodeInput" label="Pincode" placeholder="6-digit pincode" :maxlength="6" :value="form.contact_pincode" @update:value="onPincodeInput($event)" />
              </CCol>
              <CCol md="8" class="form-group">
                <label class="tele-label">
                  Post Office
                  <span v-if="isPincodeLoading" class="tele-label__status">Looking up…</span>
                  <span v-else-if="form.contact_pincode.length === 6 && !postOfficeOptions.length" class="tele-label__status tele-label__status--warn">No matches</span>
                </label>
                <AppDropdown :value="form.contact_postofficename" :options="postOfficeDropdownOptions" :placeholder="postOfficeOptions.length ? 'Select a post office' : 'Enter pincode to auto-fill'" @update:value="onPostOfficeSelect($event)" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4">
                <AppInput label="District" :value="form.contact_city" disabled />
              </CCol>
              <CCol md="4">
                <AppInput label="State" :value="form.contact_state" disabled />
              </CCol>
              <CCol md="4">
                <AppInput label="Ward No" type="number" :value="form.ward_no" @update:value="form.ward_no = Number($event || 0)" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12" class="form-group">
                <label class="tele-label">Local Body</label>
                <AppDropdown :value="selectedLocalBodyName" :options="localBodyDropdownOptions" :placeholder="localBodyOptions.length ? 'Search and select a local body' : 'Auto-loads after pincode'" searchable search-placeholder="Search local body…" @update:value="onLocalBodySelect($event)" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12" data-checklist="address">
                <AppInput label="Address" required multiline :rows="2" placeholder="Enter Address" :value="form.contact_address" @update:value="form.contact_address = $event" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12">
                <AppInput label="Comments" multiline :rows="2" placeholder="Notes from this call" :value="form.comments" @update:value="form.comments = $event" />
              </CCol>
            </CRow>
          </div>
        </section>

        <section class="tele-section">
          <header class="tele-section__head">
            <h2>Billing Address</h2>
            <label class="tele-head-toggle">
              <input type="checkbox" :checked="addBillingAddress" @change="toggleBilling($event.target.checked)">
              <span>{{ addBillingAddress ? 'Enabled' : 'Disabled' }}</span>
            </label>
          </header>
          <div v-if="addBillingAddress" class="tele-section__body">
            <CRow>
              <CCol md="12">
                <AppInput
                  label="Billing Name"
                  placeholder="Billing Name"
                  :value="form.billing_name"
                  @update:value="form.billing_name = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12">
                <AppInput
                  label="Billing Address"
                  multiline
                  :rows="2"
                  placeholder="Billing Address"
                  :value="form.billing_address"
                  @update:value="form.billing_address = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="6">
                <AppInput
                  label="Billing Pincode"
                  placeholder="6-digit pincode"
                  :maxlength="6"
                  :value="form.billing_pincode"
                  @update:value="form.billing_pincode = String($event || '').replace(/\D/g, '').slice(0, 6)"
                />
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Billing Post Office"
                  placeholder="Billing Post Office"
                  :value="form.billing_postofficename"
                  @update:value="form.billing_postofficename = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="6">
                <AppInput
                  label="Billing District"
                  placeholder="Billing District"
                  :value="form.billing_district"
                  @update:value="form.billing_district = String($event || '')"
                />
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Billing State"
                  placeholder="Billing State"
                  :value="form.billing_state"
                  @update:value="form.billing_state = String($event || '')"
                />
              </CCol>
            </CRow>
          </div>
          <div v-else class="tele-section__body">
            <div class="tele-empty">Toggle on to capture a separate billing address.</div>
          </div>
        </section>

        <section v-if="showMedicalSection" class="tele-section">
          <header class="tele-section__head">
            <h2>Medical Information</h2>
          </header>
          <div class="tele-section__body" data-checklist="medical">

            <div v-if="medicalNotesBlocked" class="med-warning" role="status">
              <span class="med-warning__icon" aria-hidden="true">i</span>
              <div class="med-warning__body">
                <strong class="med-warning__title">Demographics missing</strong>
                <p class="med-warning__text">{{ medicalBlockReason }}</p>
              </div>
              <button type="button" class="med-warning__action" @click="scrollToMedicalDemographics">Fill now</button>
            </div>
            <div v-if="patientSessions.length" class="med-history">
              <div class="med-history__head">
                <h3 class="med-history__title">Case History</h3>
                <span class="med-history__count">{{ patientSessions.length }} previous {{ patientSessions.length === 1 ? 'case' : 'cases' }}</span>
              </div>
              <ul class="med-history__list">
                <li
                  v-for="(session, sIdx) in visibleSessions"
                  :key="`med-${session.id || sIdx}`"
                  :class="['med-history__item', { 'is-open': isSessionOpen(session.id) }]"
                >
                  <button
                    type="button"
                    class="med-history__item-head"
                    :aria-expanded="isSessionOpen(session.id)"
                    @click="toggleSession(session.id)"
                  >
                    <div class="med-history__item-line">
                      <div class="med-history__item-meta">
                        <span class="med-history__date">{{ formatSessionDate(session.created_at) || 'Undated' }}</span>
                        <span class="med-history__author">{{ sessionAuthor(session) ? `by ${sessionAuthor(session)}` : 'by unknown' }}</span>
                      </div>
                      <span :class="['med-history__chevron', { 'is-open': isSessionOpen(session.id) }]" aria-hidden="true">▾</span>
                    </div>
                    <div v-if="!isSessionOpen(session.id)" class="med-history__preview">{{ sessionPreview(session) }}</div>
                  </button>
                  <div v-show="isSessionOpen(session.id)" class="med-history__item-body">
                    <div v-if="session.presenting_complaints" class="med-history__field">
                      <span class="med-history__label">Presenting Symptoms</span>
                      <p class="med-history__text">{{ session.presenting_complaints }}</p>
                    </div>
                    <div v-if="session.medical_history" class="med-history__field">
                      <span class="med-history__label">Medical History</span>
                      <p class="med-history__text">{{ session.medical_history }}</p>
                    </div>
                    <div v-if="session.treatment_history" class="med-history__field">
                      <span class="med-history__label">Treatment History</span>
                      <p class="med-history__text">{{ session.treatment_history }}</p>
                    </div>
                    <div v-if="sessionAttachments(session).length" class="med-history__field">
                      <span class="med-history__label">Reports &amp; Attachments</span>
                      <div class="med-gallery">
                        <div v-for="(file, aIdx) in sessionAttachments(session)" :key="file.key || aIdx" class="med-thumb">
                          <button type="button" class="med-thumb__btn" :title="file.name || 'Attachment'" @click="openPreview(sessionAttachments(session), aIdx)">
                            <img v-if="!isPdfAttachment(file)" :src="file.url" :alt="file.name || 'Report image'" class="med-thumb__img" loading="lazy" />
                            <span v-else class="med-thumb__pdf">
                              <span class="med-thumb__pdf-badge">PDF</span>
                              <span class="med-thumb__pdf-name">{{ file.name || 'Document' }}</span>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="!session.presenting_complaints && !session.medical_history && !session.treatment_history && !sessionAttachments(session).length" class="med-history__empty">
                      No notes recorded for this case.
                    </div>
                  </div>
                </li>
              </ul>
              <button
                v-if="patientSessions.length > collapsedSessionLimit"
                type="button"
                class="med-history__more"
                @click="showAllSessions = !showAllSessions"
              >
                {{ showAllSessions ? 'Show fewer' : `Show all ${patientSessions.length} cases` }}
              </button>
            </div>

            <div :class="['med-current', { 'med-current--with-history': patientSessions.length }]">
              <h3 v-if="patientSessions.length" class="med-current__title">New Case</h3>
              <CRow>
                <CCol md="12">
                  <AppInput label="Presenting Symptoms" multiline :rows="3" placeholder="What the customer is currently experiencing" :value="form.presenting_symptoms" @update:value="form.presenting_symptoms = $event" />
                </CCol>
              </CRow>
              <CRow>
                <CCol md="12">
                  <AppInput label="Medical History" multiline :rows="3" placeholder="Relevant prior conditions, allergies, etc." :value="form.medical_history" @update:value="form.medical_history = $event" />
                </CCol>
              </CRow>
              <CRow>
                <CCol md="12">
                  <AppInput label="Treatment History" multiline :rows="3" placeholder="Past or ongoing treatments and medication" :value="form.treatment_history" @update:value="form.treatment_history = $event" />
                </CCol>
              </CRow>
              <CRow>
                <CCol md="12">
                  <label class="tele-label">
                    Reports &amp; Attachments
                    <span class="tele-label__status">Images or PDF, up to 10MB each</span>
                  </label>
                  <div class="med-attach">
                    <label class="med-attach__picker" :class="{ 'is-busy': uploadingMedicalReports > 0 }">
                      <input
                        ref="medicalReportInput"
                        type="file"
                        class="med-attach__input"
                        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                        multiple
                        @change="onMedicalReportsSelected"
                      />
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>{{ uploadingMedicalReports > 0 ? `Uploading ${uploadingMedicalReports}…` : 'Upload reports' }}</span>
                    </label>

                    <p v-if="medicalUploadError" class="med-attach__error">{{ medicalUploadError }}</p>

                    <div v-if="form.medical_attachments.length" class="med-gallery">
                      <div v-for="(file, fIdx) in form.medical_attachments" :key="file.key || fIdx" class="med-thumb">
                        <button type="button" class="med-thumb__btn" :title="file.name || 'Attachment'" @click="openPreview(form.medical_attachments, fIdx)">
                          <img v-if="!isPdfAttachment(file)" :src="file.url" :alt="file.name || 'Report image'" class="med-thumb__img" loading="lazy" />
                          <span v-else class="med-thumb__pdf">
                            <span class="med-thumb__pdf-badge">PDF</span>
                            <span class="med-thumb__pdf-name">{{ file.name || 'Document' }}</span>
                          </span>
                        </button>
                        <button type="button" class="med-thumb__remove" :aria-label="`Remove ${file.name || 'attachment'}`" @click.stop="removeMedicalAttachment(fIdx)">×</button>
                      </div>
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>

            <div class="tele-section__head" style="margin-top: 32px; padding: 0;">
              <h3 style="font-size: 1rem; color: #1e293b; font-weight: 600; margin: 0;">Disease Tag</h3>
            </div>
            <div class="form-group" data-checklist="disease" style="margin-top: 16px;">
              <label class="tele-label">Diseases<span v-if="medicalDetailsRequired" class="tele-label__required" aria-hidden="true">*</span></label>
              <AppDropdown
                :value="''"
                :options="availableDiseaseOptions"
                :placeholder="availableDiseaseOptions.length ? 'Select a disease to add' : 'All diseases added'"
                searchable
                search-placeholder="Search disease…"
                @update:value="addDiseaseByName($event)"
              />
              <div v-if="allDiseases.length" class="crop-chips">
                <span v-for="(disease, index) in allDiseases" :key="`disease-${index}`" class="crop-chip">
                  <span class="crop-chip__name">{{ disease }}</span>
                  <button type="button" class="crop-chip__remove" :aria-label="`Remove ${disease}`" @click="removeDisease(index)">
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </span>
              </div>
              <div v-else class="tele-empty crop-empty">No diseases tagged yet.</div>
            </div>
          </div>
        </section>

        <section v-if="showVetSection" class="tele-section">
          <header class="tele-section__head">
            <h2>Disease Information</h2>
            <span class="tele-section__hint">Optional</span>
          </header>
          <div class="tele-section__body">
            <div v-if="patientSessions.length" class="med-history">
              <div class="med-history__head">
                <h3 class="med-history__title">Case History</h3>
                <span class="med-history__count">{{ patientSessions.length }} previous {{ patientSessions.length === 1 ? 'case' : 'cases' }}</span>
              </div>
              <ul class="med-history__list">
                <li
                  v-for="(session, sIdx) in visibleSessions"
                  :key="`vet-${session.id || sIdx}`"
                  :class="['med-history__item', { 'is-open': isSessionOpen(session.id) }]"
                >
                  <button
                    type="button"
                    class="med-history__item-head"
                    :aria-expanded="isSessionOpen(session.id)"
                    @click="toggleSession(session.id)"
                  >
                    <div class="med-history__item-line">
                      <div class="med-history__item-meta">
                        <span class="med-history__date">{{ formatSessionDate(session.created_at) || 'Undated' }}</span>
                        <span class="med-history__author">{{ sessionAuthor(session) ? `by ${sessionAuthor(session)}` : 'by unknown' }}</span>
                      </div>
                      <span :class="['med-history__chevron', { 'is-open': isSessionOpen(session.id) }]" aria-hidden="true">▾</span>
                    </div>
                    <div v-if="!isSessionOpen(session.id)" class="med-history__preview">{{ sessionPreview(session) }}</div>
                  </button>
                  <div v-show="isSessionOpen(session.id)" class="med-history__item-body">
                    <div v-if="session.presenting_complaints" class="med-history__field">
                      <span class="med-history__label">Disease Detail</span>
                      <p class="med-history__text">{{ session.presenting_complaints }}</p>
                    </div>
                    <div v-else class="med-history__empty">
                      No disease detail recorded for this case.
                    </div>
                  </div>
                </li>
              </ul>
              <button
                v-if="patientSessions.length > collapsedSessionLimit"
                type="button"
                class="med-history__more"
                @click="showAllSessions = !showAllSessions"
              >
                {{ showAllSessions ? 'Show fewer' : `Show all ${patientSessions.length} cases` }}
              </button>
            </div>

            <div :class="['med-current', { 'med-current--with-history': patientSessions.length }]">
              <h3 v-if="patientSessions.length" class="med-current__title">New Case</h3>
              <CRow>
                <CCol md="12">
                  <AppInput label="Disease Detail" multiline :rows="4" placeholder="Veterinary disease details for this case" :value="form.presenting_symptoms" @update:value="form.presenting_symptoms = $event" />
                </CCol>
              </CRow>
            </div>
          </div>
        </section>

        <section class="tele-section">
          <header class="tele-section__head">
            <h2><span class="step-num">3</span> Order Items</h2>
          </header>
          <div class="tele-section__body" data-checklist="items">
            <CRow>
              <CCol md="12" class="form-group">
                <label class="tele-label">Search Product</label>
                <AppDropdown :value="productDraft.item_id" :options="itemOptions" placeholder="Select a product" searchable search-placeholder="Search product…" @update:value="productDraft.item_id = $event" />
              </CCol>
            </CRow>

            <div class="product-add">
              <div class="product-add__field">
                <label class="tele-label">Price</label>
                <AppInput no-margin :value="productDraftPrice" disabled />
              </div>
              <div class="product-add__field product-add__field--qty">
                <label class="tele-label">Qty</label>
                <AppInput no-margin type="number" :value="productDraft.quantity" @update:value="productDraft.quantity = Number($event || 0)" />
              </div>
              <div class="product-add__field">
                <label class="tele-label">Total</label>
                <AppInput no-margin :value="productDraftTotal" disabled />
              </div>
              <div class="product-add__action">
                <CButton color="primary" class="w-100" :disabled="!productDraft.item_id || Number(productDraft.quantity || 0) <= 0" @click="addSelectedProduct">+ Add</CButton>
              </div>
            </div>

            <div v-if="!form.order_items.length" class="tele-empty">
              No items added yet. Pick a product above to add it to the order.
            </div>

            <ul v-else class="item-list">
              <li v-for="(item, index) in form.order_items" :key="`item-${index}`" class="item-list__row">
                <div class="item-list__thumb">
                  <img v-if="item.image" :src="item.image" :alt="item.name" class="item-list__thumb-img" loading="lazy" />
                  <span v-else class="item-list__thumb-img item-list__thumb-img--placeholder" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </span>
                </div>
                <div class="item-list__main">
                  <div class="item-list__name">{{ item.name }}</div>
                  <div class="item-list__meta">{{ formatCurrency(item.price) }} each</div>
                </div>
                <div class="item-list__qty">
                  <label class="tele-label-inline">Qty</label>
                  <AppInput no-margin type="number" :value="item.quantity" @update:value="updateItemQuantity(index, $event)" />
                </div>
                <div class="item-list__total">
                  <span class="tele-label-inline">Total</span>
                  <strong>{{ formatCurrency(item.total) }}</strong>
                </div>
                <CButton color="danger" variant="outline" size="sm" class="item-list__remove" @click="removeItem(index)">Remove</CButton>
              </li>
            </ul>

            <CRow v-if="form.order_items.length && eligibleDiscountTier" class="mt-3">
              <CCol md="12">
                <div class="preset-banner">
                  <div class="preset-banner__info">
                    <span class="preset-banner__dot" aria-hidden="true"></span>
                    <div>
                      <div class="preset-banner__title">
                        Eligible: {{ eligibleDiscountTierLabel }}
                        <button
                          type="button"
                          class="preset-banner__info-btn"
                          aria-label="Show discount chart"
                          title="Discount chart"
                          @click="showDiscountChart = true"
                        >
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="11" x2="12" y2="17" />
                            <line x1="12" y1="7" x2="12.01" y2="7" />
                          </svg>
                        </button>
                      </div>
                      <div class="preset-banner__sub">Saves {{ formatCurrency(eligibleDiscountAmount) }} on this order</div>
                    </div>
                  </div>
                  <label class="preset-banner__toggle" :class="{ 'is-on': applyPreset }">
                    <input type="checkbox" :checked="applyPreset" @change="onApplyPresetToggle($event.target.checked)" />
                    <span class="preset-banner__track" aria-hidden="true">
                      <span class="preset-banner__thumb"></span>
                    </span>
                    <span class="preset-banner__label">{{ applyPreset ? 'Applied' : 'Apply' }}</span>
                  </label>
                </div>
              </CCol>
            </CRow>

            <!-- Over the parcel cap. Surfaced here, next to the cart, rather
                 than only at submit: the agent is usually still on the call
                 and can decide between splitting and trimming the order. -->
            <CRow v-if="needsParcelSplit" class="mt-3">
              <CCol col="12">
                <div class="split-banner">
                  <span class="split-banner__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="7" width="9" height="9" rx="1" />
                      <rect x="13" y="7" width="9" height="9" rx="1" />
                      <path d="M12 3v3M12 18v3" />
                    </svg>
                  </span>
                  <div class="split-banner__text">
                    <strong>Ships as {{ parcelSplitCount }} parcels.</strong>
                    This cart is {{ parcelCartMeasure.units }} bottles / {{ (parcelCartMeasure.weightGrams / 1000).toFixed(2) }} kg,
                    over the {{ parcelLimitLabel }} {{ formatLabel(form.shipping_method) }} limit.
                    Converting to sale creates {{ parcelSplitCount }} orders — the discount is still worked out on the
                    whole cart, but shipping is charged per parcel.
                  </div>
                </div>
              </CCol>
            </CRow>

            <CRow v-if="form.order_items.length" class="mt-3">
              <CCol md="6" class="form-group">
                <label class="tele-label">Discount</label>
                <div class="discount-row" :class="{ 'is-locked': discountFieldsLocked }">
                  <AppDropdown class="discount-row__mode" :value="discountMode" :options="discountModeOptions" :disabled="discountFieldsLocked" @update:value="discountMode = $event" />
                  <AppInput no-margin type="number" :value="discountInput" :disabled="discountFieldsLocked" @update:value="onDiscountInput($event)" />
                </div>
                <small class="text-muted">
                  After discount: {{ formatCurrency(totalAfterDiscount) }}
                  <span v-if="applyPreset" class="preset-pill">Preset applied</span>
                  <span v-else-if="!canOverrideCharges" class="preset-pill preset-pill--muted">Locked — use the toggle above</span>
                </small>
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Shipping Charge (₹)"
                  type="number"
                  :value="form.shipping_charge"
                  :disabled="shippingFieldLocked"
                  @update:value="onShippingChargeInput($event)"
                  class="mb-0"
                />
                <small v-if="shippingFieldLocked && !shippingZeroReason" class="text-muted">
                  <span class="preset-pill preset-pill--muted">Locked — auto-calculated</span>
                </small>
                <div v-if="shippingZeroReason" class="tele-shipping-zero" role="alert">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <div class="tele-shipping-zero__body">
                    <strong>Shipping is ₹0 — sale can't be placed.</strong>
                    <span>{{ shippingZeroMessage }}</span>
                  </div>
                  <button type="button" class="tele-shipping-zero__btn" @click="focusContactPincode">
                    {{ shippingZeroReason === 'post_office_missing' ? 'Pick post office' : 'Fix pincode' }}
                  </button>
                </div>
              </CCol>
            </CRow>
          </div>
        </section>

        <section v-if="showCropsSection" class="tele-section">
          <header class="tele-section__head">
            <h2>Tag the Crops</h2>
          </header>
          <div class="tele-section__body" data-checklist="crops">
            <CRow>
              <CCol md="6" class="form-group">
                <label class="tele-label">Customer Type<span v-if="cropDetailsRequired" class="tele-label__required" aria-hidden="true">*</span></label>
                <AppDropdown
                  :value="form.customer_type"
                  :options="customerTypeOptions"
                  placeholder="Tag the type of customer"
                  @update:value="form.customer_type = $event"
                />
                <small class="tele-field-hint">Helps classify who you're selling to.</small>
              </CCol>
            </CRow>
            <label class="tele-label">Customer's Crops<span v-if="cropDetailsRequired" class="tele-label__required" aria-hidden="true">*</span></label>
            <AppDropdown
              :value="''"
              :options="availableCropOptions"
              :placeholder="availableCropOptions.length ? 'Select a crop to add' : 'All crops added'"
              searchable
              search-placeholder="Search crop…"
              @update:value="addCropByName($event)"
            />
            <div v-if="allCrops.length" class="crop-chips">
              <span v-for="(crop, index) in allCrops" :key="`crop-${index}`" class="crop-chip">
                <span class="crop-chip__name">{{ crop.crop }}</span>
                <button type="button" class="crop-chip__remove" :aria-label="`Remove ${crop.crop}`" @click="removeCrop(index)">
                  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            </div>
            <div v-else class="tele-empty crop-empty">No crops selected yet.</div>
          </div>
        </section>

        <section v-if="showVetSection" class="tele-section">
          <header class="tele-section__head">
            <h2>Tag the Animals</h2>
          </header>
          <div class="tele-section__body" data-checklist="animals">
            <CRow>
              <CCol md="6" class="form-group">
                <label class="tele-label">Customer Type<span v-if="animalDetailsRequired" class="tele-label__required" aria-hidden="true">*</span></label>
                <AppDropdown
                  :value="form.customer_type"
                  :options="customerTypeOptions"
                  placeholder="Tag the type of customer"
                  @update:value="form.customer_type = $event"
                />
                <small class="tele-field-hint">Pet owner, small-scale or commercial farm.</small>
              </CCol>
            </CRow>
            <label class="tele-label">Animals<span v-if="animalDetailsRequired" class="tele-label__required" aria-hidden="true">*</span></label>
            <AppDropdown
              :value="''"
              :options="availableAnimalOptions"
              :placeholder="availableAnimalOptions.length ? 'Select an animal to add' : 'All animals added'"
              searchable
              search-placeholder="Search animal…"
              @update:value="addAnimalByName($event)"
            />
            <div v-if="allAnimals.length" class="crop-chips">
              <span v-for="(animal, index) in allAnimals" :key="`animal-${index}`" class="crop-chip">
                <span class="crop-chip__name">{{ animal }}</span>
                <button type="button" class="crop-chip__remove" :aria-label="`Remove ${animal}`" @click="removeAnimal(index)">
                  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            </div>
            <div v-else class="tele-empty crop-empty">No animals selected yet.</div>
          </div>
        </section>

        <section class="tele-section">
          <header class="tele-section__head">
            <h2><span class="step-num">4</span> Payment &amp; Shipping</h2>
          </header>
          <div class="tele-section__body">
            <CRow>
              <CCol md="4" class="form-group" data-checklist="payment">
                <label class="tele-label">Payment Method<span class="tele-label__required" aria-hidden="true">*</span></label>
                <AppDropdown :value="form.payment_method" :options="visiblePaymentMethods" placeholder="Select payment method" @update:value="onPaymentMethodChange" />
                <div v-if="codBlocked" class="tele-cod-block-note" role="status">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>COD unavailable — this customer has a prior returned COD order. Prepaid only.</span>
                </div>
              </CCol>
              <CCol md="4" class="form-group" data-checklist="shipping">
                <label class="tele-label">Shipping Method<span class="tele-label__required" aria-hidden="true">*</span></label>
                <AppDropdown :value="form.shipping_method" :options="visibleShippingMethods" placeholder="Select shipping method" @update:value="onShippingMethodChange" />
              </CCol>
            </CRow>
            <div class="amount-due">
              <div class="amount-due__body">
                <span class="amount-due__label">Amount To Pay</span>
                <span class="amount-due__value">{{ amountToPayLabel }}</span>
              </div>
              <span class="amount-due__hint">Includes GST &amp; shipping</span>
            </div>

            <div
              v-if="showPaymentScreenshotUpload"
              data-checklist="payment_screenshot"
              :class="['pay-proof', paymentProofProvided ? 'pay-proof--ready' : 'pay-proof--empty']"
            >
              <div class="pay-proof__head">
                <span class="pay-proof__badge" aria-hidden="true">
                  <svg v-if="!paymentProofProvided" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <div class="pay-proof__head-text">
                  <h3 class="pay-proof__title">
                    Payment Proof
                    <span v-if="!paymentProofProvided" class="pay-proof__pill pay-proof__pill--req">Required</span>
                    <span v-else-if="form.payment_screenshots.length" class="pay-proof__pill pay-proof__pill--ok">{{ form.payment_screenshots.length }} attached</span>
                    <span v-else class="pay-proof__pill pay-proof__pill--ok">Note added</span>
                  </h3>
                  <p class="pay-proof__subtitle">
                    This order goes to <strong>Inbox → Payment Approvals</strong>. Attach the bank/UPI transfer screenshot — <strong>or</strong>, if the customer didn't send one, write a note instead (UPI reference, "paid at counter", etc.) so an accountant can still confirm it.
                  </p>
                </div>
              </div>
              <div class="med-attach pay-proof__attach">
                <label class="med-attach__picker pay-proof__picker" :class="{ 'is-busy': uploadingPaymentScreenshots > 0 }">
                  <input
                    ref="paymentScreenshotInput"
                    type="file"
                    class="med-attach__input"
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    multiple
                    @change="onPaymentScreenshotsSelected"
                  />
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>{{ uploadingPaymentScreenshots > 0 ? `Uploading ${uploadingPaymentScreenshots}…` : (form.payment_screenshots.length ? 'Add another screenshot' : 'Upload screenshot') }}</span>
                </label>

                <p v-if="paymentScreenshotError" class="med-attach__error">{{ paymentScreenshotError }}</p>

                <div v-if="form.payment_screenshots.length" class="med-gallery">
                  <div v-for="(file, fIdx) in form.payment_screenshots" :key="file.key || fIdx" class="med-thumb">
                    <button type="button" class="med-thumb__btn" :title="file.name || 'Screenshot'" @click="openPreview(form.payment_screenshots, fIdx)">
                      <img v-if="!isPdfAttachment(file)" :src="file.url" :alt="file.name || 'Payment screenshot'" class="med-thumb__img" loading="lazy" />
                      <span v-else class="med-thumb__pdf">
                        <span class="med-thumb__pdf-badge">PDF</span>
                        <span class="med-thumb__pdf-name">{{ file.name || 'Document' }}</span>
                      </span>
                    </button>
                    <button type="button" class="med-thumb__remove" :aria-label="`Remove ${file.name || 'screenshot'}`" @click.stop="removePaymentScreenshot(fIdx)">×</button>
                  </div>
                </div>
              </div>

              <div class="pay-proof__or" aria-hidden="true"><span>OR</span></div>

              <div class="pay-proof__note">
                <label class="tele-label">
                  No screenshot? Write a note instead
                  <span class="tele-label__status">e.g. "Paid via UPI, ref 4012 7788 9921" or "Paid cash at counter — no screenshot".</span>
                </label>
                <AppInput
                  no-margin
                  multiline
                  :rows="2"
                  placeholder="Type the payment reference or reason the screenshot isn't available…"
                  :value="form.payment_proof_note"
                  @update:value="form.payment_proof_note = $event"
                />
              </div>
            </div>
          </div>
        </section>
      </CCol>

      <CCol lg="4" class="tele-aside">
        <div class="tele-sticky">
          <section class="tele-section call-section d-none d-lg-block">
            <header class="tele-section__head">
              <h2>Call Control</h2>
              <span :class="['call-dot', callRunning ? 'is-on' : 'is-off']" :title="callRunning ? 'Call active' : 'Idle'"></span>
            </header>
            <div class="tele-section__body call-body">
              <div class="call-timer">{{ formattedTimer }}</div>
              <div class="call-actions">
                <CButton color="primary" class="w-100" :disabled="isCallActive || callRunning" @click="startCall">Start Call</CButton>
                <CButton color="danger" class="w-100" :disabled="!callRunning" @click="endCall">End Call</CButton>
              </div>
            </div>
          </section>

          <section class="tele-section summary-section">
            <header class="tele-section__head">
              <h2>Order Summary</h2>
            </header>
            <div class="tele-section__body">
              <div class="summary-line"><span>Items</span><strong>{{ totalQuantity }}</strong></div>
              <div class="summary-line"><span>Subtotal (incl. GST)</span><strong>{{ formatCurrency(form.total) }}</strong></div>
              <div class="summary-line"><span>Discount</span><strong>−{{ formatCurrency(form.discount) }}</strong></div>
              <div class="summary-line">
                <span class="summary-line__label">
                  Shipping
                  <button
                    v-if="shippingRateChipLabel"
                    type="button"
                    class="summary-distance-chip"
                    :title="shippingRateChipTitle"
                    aria-label="View shipping rate chart"
                    @click="showShippingTariff = true"
                  >
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {{ shippingRateChipLabel }}
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                </span>
                <strong>{{ formatCurrency(form.shipping_charge) }}</strong>
              </div>
              <div v-if="form.cod_fee > 0" class="summary-line"><span>COD Fee</span><strong>{{ formatCurrency(form.cod_fee) }}</strong></div>
              <div class="summary-line summary-line--total"><span>Grand Total</span><strong>{{ formatCurrency(form.grand_total) }}</strong></div>
              <div v-if="form.gst_amt > 0" class="summary-line summary-line--note"><span>Includes {{ gstSummaryLabel }}</span><strong>{{ formatCurrency(form.gst_amt) }}</strong></div>

              <div class="summary-actions">
                <CButton color="info" class="w-100" :disabled="isSavingLog" @click="saveCallLog">
                  {{ isSavingLog ? 'Saving…' : 'Save Call Log' }}
                </CButton>
                <CButton color="success" class="w-100 summary-actions__primary" :class="{ 'summary-actions__primary--pending': !isFormReady }" :disabled="isSubmitting" @click="convertToSale">
                  {{ isSubmitting ? 'Submitting…' : 'Convert to Sale' }}
                </CButton>
                <button
                  v-if="hasEstimateItems && form.shipping_method"
                  type="button"
                  class="estimate-cta__btn w-100"
                  :disabled="isGeneratingEstimate"
                  @click="openEstimatePreview"
                >
                  <svg v-if="!isGeneratingEstimate" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.64 1.44h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.42zM12.04 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.72 9.72 0 0 1-1.49-5.18c0-5.37 4.37-9.74 9.74-9.74 2.6 0 5.05 1.01 6.89 2.85a9.68 9.68 0 0 1 2.85 6.89c0 5.37-4.37 9.83-9.66 9.83zm5.34-7.27c-.29-.15-1.73-.86-2-.96-.27-.1-.46-.15-.66.15-.2.29-.76.96-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.51.07-.78.37s-1.03 1.01-1.03 2.46c0 1.45 1.05 2.86 1.2 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.73-.71 1.98-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
                  </svg>
                  <span v-if="isGeneratingEstimate" class="estimate-cta__spinner" aria-hidden="true"></span>
                  {{ isGeneratingEstimate ? 'Preparing estimate…' : 'Send Estimate to Customer' }}
                </button>
                <CButton color="danger" variant="outline" size="sm" class="w-100 summary-actions__reset" @click="resetForm">Reset</CButton>
              </div>
            </div>
          </section>
        </div>
      </CCol>
    </CRow>

    <!--
      Off-screen estimate card. Rendered (not display:none) so html2canvas can
      lay it out and snapshot it to a PNG in sendEstimateWhatsapp(). Kept on
      html2canvas-safe CSS only — solid colors, system fonts, no remote images
      (product thumbnails are cross-origin and would taint the canvas).
    -->
    <div ref="estimateCard" class="estimate-capture" :class="{ 'estimate-capture--blue': estimateIsDrugs }" aria-hidden="true">
      <div class="est-head">
        <div class="est-brand">{{ estimateBrandName }}</div>
        <div class="est-badge">ESTIMATE</div>
      </div>
      <div class="est-sub">
        <span>{{ estimateDateLabel }}</span>
        <span v-if="estimateCustomerName">For: {{ estimateCustomerName }}</span>
        <span v-if="estimateCustomerPhone">{{ estimateCustomerPhone }}</span>
      </div>

      <table class="est-table">
        <thead>
          <tr>
            <th class="est-c-sl">#</th>
            <th class="est-c-name">Product</th>
            <th class="est-c-qty">Qty</th>
            <th class="est-c-rate">Rate</th>
            <th class="est-c-amt">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in estimateLineItems" :key="`est-${idx}`">
            <td class="est-c-sl">{{ idx + 1 }}</td>
            <td class="est-c-name">{{ item.name }}</td>
            <td class="est-c-qty">{{ item.quantity }}</td>
            <td class="est-c-rate">{{ formatCurrency(item.price) }}</td>
            <td class="est-c-amt">{{ formatCurrency(item.total) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="est-totals">
        <div class="est-total-row"><span>Subtotal (incl. GST)</span><span>{{ formatCurrency(form.total) }}</span></div>
        <div v-if="Number(form.discount) > 0" class="est-total-row est-total-row--discount"><span>Discount</span><span>− {{ formatCurrency(form.discount) }}</span></div>
        <div v-if="estimateShippingLabel" class="est-total-row"><span>Shipping ({{ estimateShippingLabel }})</span><span>{{ Number(form.shipping_charge) > 0 ? formatCurrency(form.shipping_charge) : 'Free' }}</span></div>
        <div v-if="Number(form.cod_fee) > 0" class="est-total-row"><span>COD Fee</span><span>{{ formatCurrency(form.cod_fee) }}</span></div>
        <div class="est-total-row est-total-row--grand"><span>Grand Total</span><span>{{ formatCurrency(form.grand_total) }}</span></div>
      </div>

      <div class="est-foot">
       Shipping charges are extra and will be applied based on the customer's selected delivery partner, including India Post, Professional Couriers, or other available logistics providers.
      </div>
    </div>

    <CModal
      :show.sync="showEstimateModal"
      size="md"
      :centered="true"
      title="Customer Estimate"
      class="estimate-modal"
      @update:show="(val) => { if (!val) closeEstimateModal() }"
    >
      <div class="estimate-modal__body">
        <img v-if="estimatePreviewUrl" :src="estimatePreviewUrl" alt="Estimate preview" class="estimate-modal__img" />
        <p class="estimate-modal__hint">
          Send the image on WhatsApp, or download it to share manually.
          On phones, “Send via WhatsApp” attaches the image straight to the chat.
        </p>
      </div>
      <template #footer>
        <button type="button" class="estimate-modal__btn estimate-modal__btn--ghost" @click="closeEstimateModal">Close</button>
        <button type="button" class="estimate-modal__btn estimate-modal__btn--ghost" @click="downloadEstimateImage">Download Image</button>
        <button type="button" class="estimate-modal__btn estimate-modal__btn--primary" @click="shareEstimateWhatsapp">Send via WhatsApp</button>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="showConvertConfirm"
      title="Convert this lead to a sale?"
      :message="convertConfirmMessage"
      confirm-label="Convert to Sale"
      busy-label="Submitting…"
      :busy="isSubmitting"
      variant="info"
      :close-on-backdrop="!isSubmitting"
      @confirm="confirmConvertToSale"
      @cancel="cancelConvertConfirm"
    />

    <AppConfirmDialog
      :visible="showCallLogConfirm"
      title="Save this call log?"
      :message="callLogConfirmMessage"
      confirm-label="Save Call Log"
      busy-label="Saving…"
      :busy="isSavingLog"
      variant="info"
      :close-on-backdrop="!isSavingLog"
      @confirm="confirmSaveCallLog"
      @cancel="cancelCallLogConfirm"
    />

    <AppConfirmDialog
      :visible="showCallLogSuccess"
      title="Call log saved successfully"
      confirm-label="Done"
      cancel-label=""
      variant="success"
      @confirm="closeCallLogSuccess"
      @cancel="closeCallLogSuccess"
    >
      <div v-if="lastSavedCallLog" class="cls-success">
        <p class="cls-success__lead">
          The call with <strong>{{ lastSavedCallLog.contactName }}</strong> has been recorded.
        </p>
        <dl class="cls-success__meta">
          <div v-if="lastSavedCallLog.phone" class="cls-success__row"><dt>Phone</dt><dd>{{ lastSavedCallLog.phone }}</dd></div>
          <div v-if="lastSavedCallLog.status" class="cls-success__row"><dt>Status</dt><dd>{{ lastSavedCallLog.status }}</dd></div>
          <div class="cls-success__row"><dt>Call duration</dt><dd>{{ lastSavedCallLog.duration }}</dd></div>
          <div v-if="lastSavedCallLog.nextCall" class="cls-success__row"><dt>Follow-up on</dt><dd>{{ lastSavedCallLog.nextCall }}</dd></div>
        </dl>
        <p class="cls-success__note">The form has been cleared for your next call.</p>
      </div>
    </AppConfirmDialog>

    <!-- Feedback calls get their own celebration instead of the plain
         "Call log saved successfully" dialog — an applause stream rises
         behind the panel (Google-Meet style) and the copy is written per
         organisation (MDI / MVP / MAP). -->
    <transition name="fb-success-fade">
      <div
        v-if="showFeedbackSuccess"
        class="fb-success"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fb-success-title"
        @mousedown.self="closeFeedbackSuccess"
      >
        <EmojiRise ref="applause" :emojis="['👏🏻']" />
        <div class="fb-success__panel" @mousedown.stop>
          <button type="button" class="fb-success__close" aria-label="Close" @click="closeFeedbackSuccess">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="fb-success__clap" aria-hidden="true">
            <span class="fb-success__clap-ring"></span>
            <span class="fb-success__clap-ring fb-success__clap-ring--delay"></span>
            <span class="fb-success__clap-glyph">👏🏻</span>
          </div>

          <h3 id="fb-success-title" class="fb-success__title">Feedback call saved</h3>
          <p class="fb-success__message">{{ feedbackSuccessMessage }}</p>

          <dl v-if="lastSavedCallLog" class="fb-success__meta">
            <div class="fb-success__row"><dt>Customer</dt><dd>{{ lastSavedCallLog.contactName }}</dd></div>
            <div v-if="lastSavedCallLog.phone" class="fb-success__row"><dt>Phone</dt><dd>{{ lastSavedCallLog.phone }}</dd></div>
            <div v-if="lastSavedCallLog.status" class="fb-success__row"><dt>Status</dt><dd>{{ lastSavedCallLog.status }}</dd></div>
            <div class="fb-success__row"><dt>Call duration</dt><dd>{{ lastSavedCallLog.duration }}</dd></div>
            <div v-if="lastSavedCallLog.nextCall" class="fb-success__row"><dt>Follow-up on</dt><dd>{{ lastSavedCallLog.nextCall }}</dd></div>
          </dl>

          <p class="fb-success__note">The form has been cleared for your next call.</p>

          <div class="fb-success__actions">
            <button type="button" class="fb-success__btn" @click="closeFeedbackSuccess">Done</button>
          </div>
        </div>
      </div>
    </transition>

    <FormChecklistModal
      :visible="showChecklistModal"
      :title="checklistTitle"
      :subtitle="checklistSubtitle"
      :items="activeChecklist"
      @close="closeChecklist"
      @locate="locateChecklistField"
    />

    <transition name="sale-success-fade">
      <div v-if="showSuccessModal" class="sale-success" role="dialog" aria-modal="true" aria-labelledby="sale-success-title" @mousedown.self="closeSuccessModal">
        <ConfettiRain ref="confetti" />
        <div class="sale-success__panel" @mousedown.stop>
          <button type="button" class="sale-success__close" aria-label="Close" @click="closeSuccessModal">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="sale-success__animation" aria-hidden="true">
            <span class="sale-success__ring"></span>
            <span class="sale-success__ring sale-success__ring--delay"></span>
            <svg class="sale-success__check" viewBox="0 0 64 64">
              <circle class="sale-success__check-circle" cx="32" cy="32" r="28" />
              <path class="sale-success__check-tick" d="M20 33 L29 42 L46 24" />
            </svg>
          </div>

          <h3 id="sale-success-title" class="sale-success__title">
            {{ lastSavedRequiresApproval ? 'Submitted for Approval' : 'Sale Confirmed!' }}
          </h3>
          <p class="sale-success__subtitle">
            <template v-if="lastSaved && lastSaved.parcelCount > 1">
              Orders <strong>{{ lastSaved.splitOrderNumbers.join(', ') }}</strong> · {{ lastSaved.contactName }}
            </template>
            <template v-else-if="lastSaved">Order <strong>{{ lastSaved.orderNumber }}</strong> · {{ lastSaved.contactName }}</template>
          </p>
          <!-- The sale became several orders. Say so plainly — the agent has
               to tell the customer to expect more than one parcel, and packing
               needs to know the labels belong together. -->
          <p v-if="lastSaved && lastSaved.parcelCount > 1" class="sale-success__notice">
            Over the parcel limit, so this sale went out as
            <strong>{{ lastSaved.parcelCount }} orders</strong> — one parcel, label and tracking id each.
            The discount was applied to the whole cart and divided between them.
          </p>
          <p v-if="lastSavedRequiresApproval" class="sale-success__notice">
            This order is held for approval until a privileged user signs off from the Inbox. Invoice + WhatsApp will be available once approved.
          </p>

          <div v-if="!lastSavedRequiresApproval && userSaleCount > 0" class="sale-success__celebrate">
            <div class="sale-success__celebrate-badge">
              <span class="sale-success__celebrate-emoji" aria-hidden="true">🎉</span>
              <span class="sale-success__celebrate-text">
                <span class="sale-success__celebrate-line">That's your <strong>{{ ordinal(userSaleCount) }}</strong> sale!</span>
                <span class="sale-success__celebrate-sub">Way to go{{ userFirstName ? ', ' + userFirstName : '' }} — keep the momentum going.</span>
              </span>
            </div>
            <p class="sale-success__quote">&ldquo;{{ saleQuote }}&rdquo;</p>
          </div>

          <dl v-if="lastSaved" class="sale-success__meta">
            <div class="sale-success__meta-row">
              <dt>Items</dt><dd>{{ lastSaved.itemCount }}</dd>
            </div>
            <div class="sale-success__meta-row">
              <dt>Grand Total</dt><dd>{{ formatCurrency(lastSaved.grandTotal) }}</dd>
            </div>
            <div class="sale-success__meta-row">
              <dt>Phone</dt><dd>+{{ lastSaved.dialCode || '91' }} {{ lastSaved.contactPhone }}</dd>
            </div>
          </dl>

          <div class="sale-success__actions">
            <button type="button" class="sale-success__btn sale-success__btn--ghost" :disabled="!(lastSaved && lastSaved.orderId) || lastSavedRequiresApproval" @click="downloadInvoice">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Invoice
            </button>
            <button type="button" class="sale-success__btn sale-success__btn--primary" :disabled="!(lastSaved && lastSaved.contactPhone) || lastSavedRequiresApproval" @click="sendInvoiceWhatsapp">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.64 1.44h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.42zM12.04 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.72 9.72 0 0 1-1.49-5.18c0-5.37 4.37-9.74 9.74-9.74 2.6 0 5.05 1.01 6.89 2.85a9.68 9.68 0 0 1 2.85 6.89c0 5.37-4.37 9.83-9.66 9.83zm5.34-7.27c-.29-.15-1.73-.86-2-.96-.27-.1-.46-.15-.66.15-.2.29-.76.96-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.51.07-.78.37s-1.03 1.01-1.03 2.46c0 1.45 1.05 2.86 1.2 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.73-.71 1.98-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
              </svg>
              Send to WhatsApp
            </button>
          </div>

          <div class="sale-success__actions">
            <button type="button" class="sale-success__btn sale-success__btn--accounts" :disabled="!(lastSaved && lastSaved.accountsMessage)" @click="copyAccountsTeamMessage">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy For Accounts Team
            </button>
          </div>
        </div>
      </div>
    </transition>

    <ProfessionalCourierTariffModal
      v-if="form.shipping_method === 'professional_courier'"
      :visible="showShippingTariff"
      :state-name="form.contact_state"
      @close="showShippingTariff = false"
    />
    <ShippingTariffModal
      v-else
      :visible="showShippingTariff"
      :distance-km="shippingDistanceKm"
      :zone="shippingZone"
      :zone-label="shippingZoneLabel"
      @close="showShippingTariff = false"
    />

    <DiscountChartModal
      :show.sync="showDiscountChart"
      :current-organisation-id="activeOrganisationId"
    />

    <SplitParcelsModal
      :show="showSplitPreview"
      :parcels="splitParcels"
      :method-label="formatLabel(form.shipping_method)"
      :limit-label="parcelLimitLabel"
      :single-parcel-shipping="Number(form.shipping_charge || 0) + Number(form.cod_fee || 0)"
      :discount-tier-label="eligibleDiscountTierLabel"
      :is-quoting="isQuotingParcels"
      :is-submitting="isSubmitting"
      :error-message="splitError"
      @cancel="cancelSplitPreview"
      @confirm="confirmSplitSale"
    />

    <!-- <CustomerHistoryModal
      :show.sync="showHistoryModal"
      :name="form.contact_name"
      :phone="fullContactPhone"
      provided
      :calls="recentCalls"
      :orders="contactOrders"
      :recent-order-ids="recentOrders.map((order) => order.id)"
    /> -->

    <transition name="med-preview-fade">
      <div v-if="showPreview && previewFile" class="med-preview" role="dialog" aria-modal="true" @click.self="closePreview">
        <div class="med-preview__dialog">
          <header class="med-preview__bar">
            <span class="med-preview__title" :title="previewFile.name || 'Attachment'">{{ previewFile.name || 'Attachment' }}</span>
            <div class="med-preview__bar-actions">
              <a :href="previewFile.url" target="_blank" rel="noopener" class="med-preview__icon-btn" title="Open in new tab" aria-label="Open in new tab">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
              <button type="button" class="med-preview__icon-btn" aria-label="Close preview" @click="closePreview">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </header>
          <div class="med-preview__stage">
            <button v-if="previewGallery.length > 1" type="button" class="med-preview__nav med-preview__nav--prev" aria-label="Previous" @click="stepPreview(-1)">‹</button>
            <img v-if="!isPdfAttachment(previewFile)" :src="previewFile.url" :alt="previewFile.name || 'Report image'" class="med-preview__img" />
            <iframe v-else :src="previewFile.url" class="med-preview__pdf" title="PDF preview"></iframe>
            <button v-if="previewGallery.length > 1" type="button" class="med-preview__nav med-preview__nav--next" aria-label="Next" @click="stepPreview(1)">›</button>
          </div>
          <footer v-if="previewGallery.length > 1" class="med-preview__count">{{ previewIndex + 1 }} / {{ previewGallery.length }}</footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import FormChecklistModal from '@/components/FormChecklistModal.vue'
import ShippingTariffModal from '@/components/ShippingTariffModal.vue'
import ProfessionalCourierTariffModal from '@/components/ProfessionalCourierTariffModal.vue'
import DiscountChartModal from '@/components/DiscountChartModal.vue'
import ConfettiRain from '@/components/ConfettiRain.vue'
import EmojiRise from '@/components/EmojiRise.vue'
import CustomerHistoryModal from '@/components/CustomerHistoryModal.vue'
import SplitParcelsModal from '@/components/SplitParcelsModal.vue'
import { getStoredRoles } from '@/auth/access.js'
import { countryDropdownOptions, dialCodeFor, findCountryByDialCode, maxPhoneLengthFor, isValidPhoneLength, phoneLengthsFor, DEFAULT_COUNTRY_NAME } from '@/utils/countries.js'
import { eligibleDiscountBuckets, eligibleDiscountTotal, eligibleDiscountLabel, lineDiscountBreakdown } from '@/utils/discountPresets.js'
import { DEFAULT_PARCEL_LIMITS, limitsForMethod, fitsInOneParcel, oversizedUnits, splitCartIntoParcels, apportionDiscount, measureCart } from '@/utils/parcelSplit.js'

// Mirrors DiscountPresetController::PRIVILEGED_ROLES on the server.
// Gates both the discount field and the shipping-charge field — only
// these roles may manually edit either value; everyone else gets the
// auto-calc / preset-applied number. Kept in sync with OrderForm.vue.
const CHARGE_OVERRIDE_ROLES = ['superadmin', 'admin', 'accountant', 'finance_manager', 'senior_accountant_2']

// Shipping methods whose charge is fixed by tariff (auto-calculated) and must
// stay locked for non-privileged staff: India Post (Speed + Registered Parcel).
// Professional Courier rates vary, so its charge is left editable. Every other
// method also lets telesales staff enter the charge manually.
const RATE_LOCKED_SHIPPING_METHODS = ['ip_speed', 'ip_registered_parcel']

// Payment methods that need an accountant's sign-off before the order
// counts as paid. Orders using one of these are created as
// `payment_pending` and surface in the Inbox → Payment Approvals queue
// (an accountant flips them to received once the credit is verified).
// Manual bank / UPI transfers always need the sign-off, whoever keys the
// order. Keep both lists in sync with the backend in
// orderController::paymentApprovals.
const BANK_APPROVAL_METHODS = [
  'federal_bank_team',
  'map_federal_areekode',
  'mr_federal_areekode',
  'hdfc_bank_team',
  'hdfc_bank_areekode',
  'google_pay_team',
  'google_pay',
  'upi'
]

// Gateway / marketplace / postal methods that need the sign-off ONLY when
// a telesales-role user places the order through THIS form — a telesales
// agent claiming a razorpay / marketplace / VPP payment must have an
// accountant verify it. The same methods keyed via OrderForm/POS, by other
// roles, or by system imports settle through their own reconciliation and
// never enter the approval queue (the backend matches these methods only
// on orders carrying this form's linked Call row).
const TELESALES_APPROVAL_METHODS = [
  'website_razorpay',
  'razorpay_pages',
  'instamojo',
  'phonepe_payment_solutions',
  'amazon',
  'flipkart',
  'vpp'
]

// Feature flag — make the payment screenshot MANDATORY for bank/UPI
// approval-queue methods. While false the uploader is shown and encouraged
// (amber "Required" card) but never blocks "Convert to Sale". Flip this to
// `true` to enforce it: the convert checklist + isFormReady automatically
// pick up the gated item below, so no other change is needed here.
// (If you also want the server to reject screenshot-less approval orders,
// add the matching check in orderController::store.)
const REQUIRE_PAYMENT_SCREENSHOT = false

// Rotating sales motivation shown alongside the confetti when a telesale
// is converted. Indexed by the agent's running sale count so the same
// "Nth sale" always pairs with the same quote — no flicker, no repeats
// inside a short streak.
const SALE_QUOTES = [
 'Closed it. That\'s how it\'s done.',
'Another one in the books — you earned that.',
'Deal\'s done. Go ring the bell.',
'That\'s a yes. Take the win, then take the next call.',
'Boom. One more name on the board.',
'You called it, you closed it. Respect.',
'Signed, sealed, sold. Nice work.',
'That close didn\'t happen by accident. Well played.',
'Win logged. Momentum\'s yours now.',
'From dial to deal — clean execution.'
]

const SALE_COUNT_STORAGE_PREFIX = 'tele_sales_count_v1:'

// Applause copy shown after a feedback call is logged, keyed by the
// organisation the agent is working under (1 = MAP · Manakkal Agro
// Pharma, 2 = MDI · Manakkal Drugs India, 4 = MVP · Manakkal Veterinary
// Pharma — same ids as ORGANISATION_ID_TO_NAME in users/userHelpers.js).
// Each org's success stories look different, so the ask does too.
const FEEDBACK_SUCCESS_MESSAGES = {
  1: '👏🏻 Great job! Every healthy crop and successful harvest is a story worth sharing. If today’s feedback was positive, kindly share the voice recording, photos, videos, or other supporting media in the Success Stories Group.',
  2: '👏🏻 Great job! You’ve made someone’s healing journey even more meaningful. Every healing story brings hope to another patient. If today’s feedback was positive, kindly share the voice recording, photos, or other supporting media in the Success Stories Group.',
  4: '👏🏻 Great job! Every healthy animal is a success story that inspires others. If today’s feedback was positive, kindly share the voice recording, photos, videos, or other supporting media in the Success Stories Group.'
}

const FEEDBACK_SUCCESS_FALLBACK = '👏🏻 Great job! Every piece of feedback makes the next customer’s experience better. If today’s feedback was positive, kindly share the voice recording, photos, videos, or other supporting media in the Success Stories Group.'

export default {
  name: 'TeleSales',
  components: { AppDropdown, AppInput, SingleDatePicker, AppConfirmDialog, FormChecklistModal, ShippingTariffModal, ProfessionalCourierTariffModal, DiscountChartModal, ConfettiRain, EmojiRise, CustomerHistoryModal, SplitParcelsModal },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    return {
      isLoading: false,
      isSearching: false,
      isSubmitting: false,
      isSavingLog: false,
      isGeneratingEstimate: false,
      showEstimateModal: false,
      estimatePreviewUrl: '',
      estimateBlob: null,
      estimateFileName: 'Estimate.png',
      isPincodeLoading: false,
      // Flips on for ~2.5s when we scroll-to-pincode after a ₹0-shipping
      // block, to draw the eye to the field that needs attention.
      pincodeAttentionPulse: false,
      postOfficeRecords: [],
      postOfficeOptions: [],
      localBodyOptions: [],
      selectedLocalBodyName: '',
      activePanel: 'products',
      productFilter: '',
      errorMessage: '',
      successMessage: '',
      showConvertConfirm: false,
      // "What's missing" checklist modal — shown when an agent clicks
      // Convert to Sale / Save Call Log while mandatory fields are blank
      // or wrongly formatted, so they see exactly what to fix instead of
      // hunting a silently-disabled button. `checklistMode` selects which
      // requirement set (convert vs call log) is rendered.
      showChecklistModal: false,
      checklistMode: 'convert',
      showSuccessModal: false,
      lastSaved: null,
      lastSavedRequiresApproval: false,
      // Save Call Log flow: confirm before writing, then a success modal
      // (with a snapshot of the saved call, since resetForm wipes the form).
      showCallLogConfirm: false,
      showCallLogSuccess: false,
      // Feedback calls swap the plain success dialog for the applause
      // celebration (see FEEDBACK_SUCCESS_MESSAGES); only one of the two
      // is ever open at a time.
      showFeedbackSuccess: false,
      lastSavedCallLog: null,
      // Per-agent running sale count, persisted in localStorage and
      // surfaced in the success modal as "your Nth sale!". Hydrated
      // from storage on mount; bumped (and re-saved) on every
      // successful conversion that isn't held for approval.
      userSaleCount: 0,
      saleQuote: '',
      lastUpdatedAt: null,
      searchHint: '',
      countryCode: DEFAULT_COUNTRY_NAME,
      countryOptions: countryDropdownOptions(),
      callRunning: false,
      timerSeconds: 0,
      timerHandle: null,
      // Debounce handle for re-fetching the shipping/COD estimate after
      // the cart changes. Coalesces rapid quantity edits into one call.
      chargeEstimateTimer: null,
      itemOptions: [],
      replyOptions: [],
      callTypeOptions: [],
      callStatusOptions: [],
      followupGradeOptions: [],
      paymentMethodOptions: [],
      shippingMethodOptions: [],
      productOptions: [],
      contactCrops: [],
      contactCalls: [],
      contactOrders: [],
      contactHmsVisits: [],
      // Auto-opens after a phone search when this contact has history, so
      // the agent sees the customer summary up front. Closeable.
      showHistoryModal: false,
      patientSessions: [],
      openSessionIds: [],
      showAllSessions: false,
      collapsedSessionLimit: 5,
      // Medical report uploads (images / PDFs) for the current new case.
      // Each entry: { url, key, name, mime, size }. Files are pushed to
      // R2 the moment they're picked; the references travel in
      // patients_data and persist on the PatientSession at save time.
      uploadingMedicalReports: 0,
      medicalUploadError: '',
      // Payment screenshot uploads — same lifecycle as medical reports but
      // scoped to the Payment & Shipping section. Counter drives the busy
      // label; the error string surfaces inline under the picker.
      uploadingPaymentScreenshots: 0,
      paymentScreenshotError: '',
      // Lightbox preview state for the medical report gallery. previewGallery
      // is the list currently being viewed (either the new-case uploads or a
      // past session's attachments) and previewIndex points into it.
      showPreview: false,
      previewGallery: [],
      previewIndex: 0,
      // Set true when /api/contacts/phone returns cod_blocked=1, i.e.
      // this customer has a prior returned (or partially-returned)
      // COD order. Used to hide COD from the payment dropdown and
      // surface a banner. Server still validates on submit.
      codBlocked: false,
      historyCollapsed: false,
      genderOptions: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' }
      ],
      maritalStatusOptions: [
        { value: 'married', label: 'Married' },
        { value: 'unmarried', label: 'Unmarried' }
      ],
      discountModeOptions: [
        { value: 'amount', label: '₹' },
        { value: 'percent', label: '%' }
      ],
      cropOptions: [],
      existingCrops: [],
      // Org-4 (Vet) animal multi-select. Fixed vocabulary of domestic /
      // livestock animals — there's no master table, the chosen names are
      // stored as-is on contact_has_animals.
      animalOptions: [
        'Cow', 'Buffalo', 'Goat', 'Sheep', 'Hen (Poultry)', 'Duck',
        'Dog', 'Cat', 'Horse', 'Pig', 'Rabbit', 'Camel', 'Donkey',
        'Ox / Bullock', 'Calf', 'Turkey', 'Pigeon', 'Quail', 'Cattle',
        'Fish', 'Elephant'
      ],
      contactAnimals: [],
      existingAnimals: [],
      // Org-2 (MDI, homoeopathy) disease multi-select vocabulary. This array is
      // the source of truth for the dropdown (availableDiseaseOptions reads it
      // directly). Grouped by body system for maintainability; order only affects
      // display and the field is searchable. Stored as-is on contact_has_diseases.
      diseaseOptions: [
        // Respiratory
        'Asthma', 'COPD', 'Pneumonia', 'Bronchitis', 'Chronic Bronchitis',
        'Allergic Rhinitis', 'Sinusitis', 'Chronic Sinusitis', 'Tonsillitis',
        'Pharyngitis', 'Sleep Apnea', 'Pulmonary Fibrosis', 'Emphysema',
        'Nasal Polyps', 'Whooping Cough',
        // Cardiovascular
        'Coronary Heart Disease', 'Hypertension', 'Heart Attack', 'Stroke',
        'Heart Failure', 'Arrhythmia', 'Atrial Fibrillation', 'Angina',
        'Varicose Veins', 'Deep Vein Thrombosis', 'High Cholesterol',
        'Peripheral Artery Disease', 'Rheumatic Heart Disease',
        // Diabetes (types & complications)
        'Diabetes', 'Type 1 Diabetes', 'Type 2 Diabetes', 'Gestational Diabetes',
        'Prediabetes', 'Insulin Resistance', 'Diabetic Neuropathy',
        'Diabetic Retinopathy', 'Diabetic Nephropathy', 'Diabetic Foot Ulcer',
        'Diabetic Ketoacidosis', 'Hyperglycemia', 'Hypoglycemia',
        'Diabetes Insipidus',
        // Endocrine & metabolic
        'Thyroid Disorders', 'Hypothyroidism', 'Hyperthyroidism',
        'Goitre', 'Hashimoto\'s Thyroiditis', 'PCOS', 'Obesity',
        'Metabolic Syndrome', 'Gout', 'Cushing\'s Syndrome', 'Addison\'s Disease',
        // Gastrointestinal & hepatic
        'Haemorrhoids', 'Fistula', 'Anal Fissure', 'Hernia', 'Gastritis',
        'Acid Reflux / GERD', 'Peptic Ulcer', 'Irritable Bowel Syndrome',
        'Inflammatory Bowel Disease', 'Crohn\'s Disease', 'Ulcerative Colitis',
        'Chronic Constipation', 'Fatty Liver', 'Liver Cirrhosis', 'Hepatitis',
        'Jaundice', 'Gallstones', 'Pancreatitis', 'Appendicitis',
        'Celiac Disease', 'Indigestion',
        // Renal & urinary
        'Kidney Failure', 'Chronic Kidney Disease', 'Kidney Stones',
        'Urinary Tract Infection', 'Nephrotic Syndrome', 'Prostate Enlargement',
        'Prostatitis', 'Urinary Incontinence',
        // Neurological
        'Migraine', 'Epilepsy', 'Alzheimer\'s Disease', 'Dementia',
        'Parkinson\'s Disease', 'Multiple Sclerosis', 'Sciatica', 'Vertigo',
        'Neuralgia', 'Trigeminal Neuralgia', 'Bell\'s Palsy',
        'Peripheral Neuropathy', 'Restless Leg Syndrome', 'Tension Headache',
        'Cluster Headache', 'Insomnia',
        // Musculoskeletal
        'Arthritis', 'Rheumatoid Arthritis', 'Osteoarthritis', 'Osteoporosis',
        'Back Pain', 'Cervical Spondylosis', 'Lumbar Spondylosis',
        'Frozen Shoulder', 'Fibromyalgia', 'Ankylosing Spondylitis',
        'Slipped Disc', 'Carpal Tunnel Syndrome', 'Joint Pain', 'Muscle Cramps',
        // Skin & hair
        'Eczema', 'Psoriasis', 'Acne', 'Vitiligo', 'Fungal Infection',
        'Ringworm', 'Warts', 'Urticaria', 'Dermatitis', 'Rosacea',
        'Hair Loss / Alopecia', 'Dandruff', 'Scabies', 'Boils', 'Lichen Planus',
        'Skin Allergy', 'Melasma',
        // Mental health
        'Depression', 'Anxiety', 'Stress', 'Panic Disorder',
        'Obsessive Compulsive Disorder', 'Bipolar Disorder', 'Schizophrenia',
        'ADHD', 'Autism Spectrum Disorder',
        // Women's & reproductive health
        'Prolapse', 'Uterine Fibroids', 'Endometriosis', 'Menstrual Disorders',
        'Irregular Periods', 'Dysmenorrhea', 'Menopause Symptoms', 'Leucorrhoea',
        'Ovarian Cyst', 'Infertility',
        // Men's health
        'Erectile Dysfunction', 'Premature Ejaculation', 'Low Libido',
        // Oncology — general
        'Cancer', 'Metastatic Cancer',
        // Oncology — women's / reproductive
        'Breast Cancer', 'Cervical Cancer', 'Ovarian Cancer', 'Uterine Cancer',
        'Endometrial Cancer', 'Vaginal Cancer', 'Vulvar Cancer',
        'Fallopian Tube Cancer',
        // Oncology — men's / reproductive
        'Prostate Cancer', 'Testicular Cancer', 'Penile Cancer',
        // Oncology — digestive
        'Liver Cancer', 'Gallbladder Cancer', 'Pancreatic Cancer', 'Stomach Cancer',
        'Esophageal Cancer', 'Colon Cancer', 'Colorectal Cancer', 'Rectal Cancer',
        'Anal Cancer', 'Small Intestine Cancer',
        // Oncology — respiratory / thoracic
        'Lung Cancer', 'Mesothelioma', 'Thymoma',
        // Oncology — head, neck & oral
        'Oral Cancer', 'Tongue Cancer', 'Lip Cancer', 'Throat Cancer',
        'Laryngeal Cancer', 'Nasopharyngeal Cancer', 'Salivary Gland Cancer',
        'Thyroid Cancer',
        // Oncology — urinary
        'Bladder Cancer', 'Kidney Cancer', 'Ureteral Cancer', 'Wilms Tumor',
        // Oncology — skin
        'Skin Cancer', 'Melanoma', 'Basal Cell Carcinoma',
        'Squamous Cell Carcinoma', 'Merkel Cell Carcinoma',
        // Oncology — brain & nervous system
        'Brain Tumor', 'Glioblastoma', 'Meningioma', 'Neuroblastoma',
        'Retinoblastoma', 'Spinal Tumor', 'Pituitary Tumor',
        // Oncology — bone & soft tissue
        'Bone Cancer', 'Osteosarcoma', 'Ewing Sarcoma', 'Soft Tissue Sarcoma',
        'Gastrointestinal Stromal Tumor',
        // Oncology — blood & lymphatic
        'Leukemia', 'Acute Myeloid Leukemia', 'Acute Lymphoblastic Leukemia',
        'Chronic Myeloid Leukemia', 'Chronic Lymphocytic Leukemia',
        'Lymphoma', 'Hodgkin Lymphoma', 'Non-Hodgkin Lymphoma', 'Multiple Myeloma',
        // Oncology — endocrine
        'Adrenal Cancer',
        // Infectious diseases
        'HIV/AIDS', 'Tuberculosis', 'Malaria', 'Dengue', 'Chikungunya', 'Typhoid',
        'Cholera', 'Influenza', 'COVID-19', 'Common Cold', 'Viral Fever',
        'Chickenpox', 'Measles', 'Mumps', 'Herpes', 'Shingles', 'Leprosy',
        'Filariasis',
        // Blood & immune
        'Anemia', 'Thalassemia', 'Hemophilia', 'Lupus (SLE)',
        // Eye, ear, nose & throat
        'Cataract', 'Glaucoma', 'Conjunctivitis', 'Dry Eye Syndrome',
        'Hearing Loss', 'Tinnitus', 'Ear Infection',
        // General & lifestyle
        'Chronic Fatigue Syndrome', 'Allergies', 'Bed Wetting'
      ],
      contactDiseases: [],
      existingDiseases: [],
      productDraft: {
        item_id: '',
        quantity: 1
      },
      discountMode: 'amount',
      discountInput: 0,
      discountPresets: [],
      applyPreset: false,
      // Parcel splitting. India Post won't book one Speed Post consignment
      // above 100 bottles / 20 kg, so an oversized cart ships as several
      // orders — previewed in SplitParcelsModal before anything is created.
      // Limits come from GET /api/shipping/parcel-limits so the form and the
      // server enforce the same numbers; the constant is only the fallback
      // until that answers.
      parcelLimitsMap: DEFAULT_PARCEL_LIMITS,
      showSplitPreview: false,
      splitParcels: [],
      isQuotingParcels: false,
      splitError: '',
      formOrganisationId: null,
      amountToPay: 0,
      // Distance (km) from the warehouse to the customer's pincode,
      // returned by /api/cal_cod_charges. Surfaced under the shipping
      // charge field so staff can explain to a customer why the
      // auto-calculated number is what it is.
      shippingDistanceKm: null,
      // Which zone column priced this quote, when India Post is on the zone
      // chart. The legacy km chart leaves these empty and fills distance.
      shippingZone: null,
      shippingZoneLabel: '',
      showShippingTariff: false,
      showDiscountChart: false,
      addBillingAddress: false,
      includeGstin: false,
      // GSTIN found on the searched contact's history (address/last order).
      // Used to prefill the GST IN field ONLY when staff tick "Include GST IN"
      // — never to auto-enable the toggle, which silently made orders B2B.
      contactGstinOnFile: '',
      form: {
        contact_name: '',
        age: '',
        gender: '',
        marital_status: '',
        customer_type: '',
        call_type: '',
        call_status: '',
        contact_phone: '',
        contact_phone_2: '',
        contact_pincode: '',
        contact_city: '',
        contact_state: '',
        contact_postofficename: '',
        contact_address: '',
        local_body_name: '',
        local_body_type_name: '',
        ward_no: 0,
        billing_name: '',
        billing_address: '',
        billing_pincode: '',
        billing_postofficename: '',
        billing_district: '',
        billing_state: '',
        contact_gstn: '',
        order_type: 'tele',
        payment_method: '',
        payment_date: '',
        shipping_method: '',
        quantity: 1,
        discount: 0,
        shipping_charge: 0,
        cod_fee: 0,
        total: 0,
        grand_total: 0,
        gst_percentage: 5,
        gst_amt: 0,
        gst_state_code: '',
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0,
        order_status: 'confirmed',
        comments: '',
        follow_up: true,
        next_call: today,
        followup_grade: '',
        message_type: '',
        message_product_id: '',
        presenting_symptoms: '',
        medical_history: '',
        treatment_history: '',
        medical_attachments: [],
        // Bank/UPI transfer proof screenshots (images / PDFs) for orders that
        // land in Inbox → Payment Approvals. Each entry: { url, key, name,
        // mime, size }. Pushed to R2 the moment they're picked and travel in
        // the order payload at convert time.
        payment_screenshots: [],
        // Written fallback when the customer didn't send a screenshot — a UPI
        // reference, "paid at counter", etc. Either this or a screenshot
        // satisfies the proof requirement.
        payment_proof_note: '',
        order_items: []
      }
    }
  },
  computed: {
    activeOrganisationId () {
      return Number(this.$store?.state?.activeOrganisationId || 0)
    },
    previewFile () {
      return this.previewGallery[this.previewIndex] || null
    },
    // Applause copy for the feedback-call celebration, picked from the
    // org the agent is logged in under.
    feedbackSuccessMessage () {
      return FEEDBACK_SUCCESS_MESSAGES[this.activeOrganisationId] || FEEDBACK_SUCCESS_FALLBACK
    },
    // True when the selected payment method routes THIS order to Inbox →
    // Payment Approvals: bank/UPI transfers for everyone, and the gateway /
    // marketplace / VPP methods only when a telesales-role user is keying
    // the order (mirrors the backend approval list).
    paymentMethodNeedsApproval () {
      if (BANK_APPROVAL_METHODS.includes(this.form.payment_method)) return true
      return this.isTelesalesRole && TELESALES_APPROVAL_METHODS.includes(this.form.payment_method)
    },
    // Show the payment-proof uploader for every method that lands the order
    // in Inbox → Payment Approvals. Cash/COD orders — and gateway methods
    // keyed by non-telesales roles — skip it.
    showPaymentScreenshotUpload () {
      return this.paymentMethodNeedsApproval
    },
    // Proof is "provided" if the staff attached at least one screenshot OR
    // typed a written note. Either satisfies the requirement, so the card
    // flips to its confirmed (green) state and — when the mandatory flag is
    // on — the convert checklist item passes.
    paymentProofProvided () {
      return this.form.payment_screenshots.length > 0 || Boolean(this.form.payment_proof_note.trim())
    },
    showCropsSection () {
      return this.activeOrganisationId === 1
    },
    showMedicalSection () {
      return this.activeOrganisationId === 2
    },
    // org-4 (Manakkal Veterinary Pharma): a single "Disease Detail"
    // textarea plus a case-by-case history, reusing the same
    // PatientSession plumbing as the org-2 medical section but without
    // demographics or report attachments. The disease detail is stored
    // on the session's presenting_complaints column.
    showVetSection () {
      return this.activeOrganisationId === 4
    },
    showDemographics () {
      return ![1, 4, 5].includes(this.activeOrganisationId)
    },
    allCrops () {
      return [...this.contactCrops, ...this.existingCrops]
    },
    // Crop names not yet picked, in {value,label} form for the dropdown.
    // Already-selected crops drop out of the list so the multi-select can't
    // add the same crop twice.
    availableCropOptions () {
      const selected = new Set(this.allCrops.map((crop) => String(crop.crop || '').toLowerCase()))
      return this.cropOptions
        .filter((name) => !selected.has(String(name).toLowerCase()))
        .map((name) => ({ value: name, label: name }))
    },
    // Customer-type vocabulary differs by org: org-1 (agro) tags the farmer
    // scale, org-4 (vet) tags the kind of animal owner. The same
    // contacts.customer_type column stores whichever slug applies.
    customerTypeOptions () {
      if (this.showVetSection) {
        return [
          { value: 'pet_owner', label: 'Pet Owner' },
          { value: 'small_scale_farm', label: 'Small Scale Farm' },
          { value: 'commercial_farm', label: 'Commercial Farm' }
        ]
      }
      return [
        { value: 'small_scale_farmer', label: 'Small Scale Farmer' },
        { value: 'kitchen_gardener', label: 'Kitchen Gardener' },
        { value: 'large_scale_farmer', label: 'Large Scale Farmer' }
      ]
    },
    allAnimals () {
      return [...this.contactAnimals, ...this.existingAnimals]
    },
    // Animal names not yet picked, in {value,label} form for the dropdown.
    availableAnimalOptions () {
      const selected = new Set(this.allAnimals.map((name) => String(name || '').toLowerCase()))
      return this.animalOptions
        .filter((name) => !selected.has(String(name).toLowerCase()))
        .map((name) => ({ value: name, label: name }))
    },
    allDiseases () {
      return [...this.contactDiseases, ...this.existingDiseases]
    },
    // Disease names not yet picked, in {value,label} form for the dropdown.
    availableDiseaseOptions () {
      const selected = new Set(this.allDiseases.map((name) => String(name || '').toLowerCase()))
      return this.diseaseOptions
        .filter((name) => !selected.has(String(name).toLowerCase()))
        .map((name) => ({ value: name, label: name }))
    },
    filteredProducts () {
      const filter = String(this.productFilter || '').trim().toLowerCase()
      if (!filter) return this.productOptions
      return this.productOptions.filter((product) => String(product.label || '').toLowerCase().includes(filter))
    },
    // ---- Estimate (WhatsApp pricing image) ----
    // Only cart rows that actually resolve to a product with a quantity make
    // it onto the estimate, so half-typed rows don't print a blank line.
    estimateLineItems () {
      return this.form.order_items.filter((item) => item.item_id && Number(item.quantity || 0) > 0)
    },
    hasEstimateItems () {
      return this.estimateLineItems.length > 0
    },
    estimateBrandName () {
      return this.$store?.getters?.activeOrganisationDisplayName || 'Estimate'
    },
    // Manakkal Drugs India (org 2) gets a blue accent on the estimate; Agro
    // Pharma (1) and Veterinary Pharma (4) keep the default brand green.
    estimateIsDrugs () {
      return this.activeOrganisationId === 2
    },
    estimateCustomerName () {
      return String(this.form.contact_name || '').trim()
    },
    estimateCustomerPhone () {
      const phone = String(this.form.contact_phone || '').trim()
      return phone ? `+${this.dialCode} ${phone}` : ''
    },
    estimateDateLabel () {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      return `${dd}/${mm}/${today.getFullYear()}`
    },
    estimateShippingLabel () {
      return this.form.shipping_method ? this.formatLabel(this.form.shipping_method) : ''
    },
    lastUpdatedLabel () {
      return this.lastUpdatedAt ? this.lastUpdatedAt.toLocaleString() : 'Not fetched yet'
    },
    amountToPayLabel () {
      return this.formatCurrency(this.amountToPay)
    },
    shippingDistanceLabel () {
      if (this.shippingDistanceKm === null) return ''
      const km = Number(this.shippingDistanceKm)
      if (!Number.isFinite(km) || km <= 0) return ''
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`
    },
    // The rate-chart chip. Reads "~123 km" while India Post prices on distance
    // and "Within State" while it prices on zone — whichever the backend
    // actually used for this quote.
    shippingRateChipLabel () {
      if (this.shippingDistanceLabel) return `~${this.shippingDistanceLabel}`
      return this.shippingZoneLabel || ''
    },
    shippingRateChipTitle () {
      if (this.shippingDistanceLabel) {
        return `~${this.shippingDistanceLabel} from warehouse — click to view rate chart`
      }
      return `${this.shippingZoneLabel} delivery zone — click to view rate chart`
    },
    // TeleSales hides cash + adjustment regardless of role — the
    // backend dropdown is shared with POS/OrderForm. COD is hidden
    // additionally when the customer has a prior returned COD on file.
    visiblePaymentMethods () {
      const hidden = new Set(['cash', 'adjustment'])
      if (this.codBlocked) hidden.add('cod')
      return this.paymentMethodOptions.filter((opt) => !hidden.has(opt.value))
    },
    visibleShippingMethods () {
      const hidden = new Set(['direct'])
      return this.shippingMethodOptions.filter((opt) => !hidden.has(opt.value))
    },
    // Feedback calls (call_type `feedback_call`) work a dedicated set of
    // "SL …" call statuses (SL Not Attended, SL Stock Available, etc.).
    // When that call type is picked, restrict the Call Status dropdown to
    // just those SL-prefixed statuses; every other call type hides the SL
    // statuses so the two vocabularies never mix.
    visibleCallStatusOptions () {
      const isSlStatus = (opt) => /^sl\b/i.test(String(opt.label || '').trim())
      if (this.form.call_type === 'feedback_call') {
        return this.callStatusOptions.filter(isSlStatus)
      }
      return this.callStatusOptions.filter((opt) => !isSlStatus(opt))
    },
    // Followup Grade only applies to high-priority callbacks — show the
    // field exclusively when the call status is "Call Again - High
    // Priority" (slug `call_again_high`).
    showFollowupGrade () {
      return this.form.call_status === 'call_again_high'
    },
    gstSummaryLabel () {
      return this.form.gst_state_code === 'KL' ? `CGST + SGST (${this.form.gst_percentage}%)` : `IGST (${this.form.gst_percentage}%)`
    },
    dialCode () {
      return dialCodeFor(this.countryCode)
    },
    // Phone-length validity is country-aware: India is a strict 10,
    // Saudi/Gulf numbers are 8–9, etc. (see utils/countries.js `nsn`).
    // Used in place of the old hardcoded `=== 10` checks so a pasted
    // +966 number — which peels to 9 local digits — can still be
    // searched, saved as a call log, and converted to a sale.
    phoneValid () {
      return isValidPhoneLength(this.countryCode, this.form.contact_phone)
    },
    altPhoneValid () {
      return isValidPhoneLength(this.countryCode, this.form.contact_phone_2)
    },
    // Full E.164-style number (e.g. "+919876543210") stored on the
    // contact / order rows. The form input itself holds only the local
    // subscriber number (length is country-aware — 10 for India, 9 for
    // Saudi, etc.); the country code is prepended at submit / search time.
    fullContactPhone () {
      return this.form.contact_phone ? `+${this.dialCode}${this.form.contact_phone}` : ''
    },
    fullAlternatePhone () {
      return this.form.contact_phone_2 ? `+${this.dialCode}${this.form.contact_phone_2}` : ''
    },
    convertConfirmMessage () {
      const name = this.form.contact_name || 'this customer'
      const phone = this.form.contact_phone ? ` (+${this.dialCode} ${this.form.contact_phone})` : ''
      const items = this.form.order_items.length
      const total = this.formatCurrency(this.form.grand_total)
      return `${name}${phone} · ${items} item${items === 1 ? '' : 's'} · ${total}. Once converted, the order is created and stock is committed.`
    },
    callLogConfirmMessage () {
      const name = this.form.contact_name || 'this contact'
      const phone = this.form.contact_phone ? ` (+${this.dialCode} ${this.form.contact_phone})` : ''
      const status = (this.callStatusOptions.find((o) => o.value === this.form.call_status) || {}).label
      const statusPart = status ? ` Status: ${status}.` : ''
      return `Record this call for ${name}${phone}.${statusPart} You'll be credited for the call and the form will be cleared for the next one.`
    },
    isCallActive () {
      return !this.form.contact_phone || this.isSearching
    },
    // A real full name is required for shipping labels — agents were
    // slipping orders through with a single "-" (or ".", "na") in the name
    // field, which then can't be shipped. Require at least two letters so
    // pure-punctuation/placeholder values are rejected.
    contactNameValid () {
      const name = String(this.form.contact_name || '').trim()
      return (name.match(/\p{L}/gu) || []).length >= 2
    },
    isFormReady () {
      const hasValidItems = this.form.order_items.some((item) => item.item_id && Number(item.quantity || 0) > 0)
      return Boolean(this.contactNameValid && this.form.contact_phone && this.altPhoneValid && this.form.contact_address && this.form.payment_method && this.form.shipping_method && hasValidItems && this.cropDetailsComplete && this.animalDetailsComplete && this.medicalDetailsComplete)
    },
    // Org-1 (MAP) only: crop tagging is mandatory for the org — the agent
    // must tag the customer type AND pick at least one crop before either
    // converting to a sale OR saving a call log. Not gated on products, so
    // it applies to every org-1 interaction.
    cropDetailsRequired () {
      return this.showCropsSection
    },
    cropDetailsComplete () {
      if (!this.cropDetailsRequired) return true
      return Boolean(this.form.customer_type) && this.allCrops.length > 0
    },
    // Org-4 (MVP) only: animal tagging mirrors crop tagging — customer type
    // AND at least one animal, required on every org-4 interaction.
    animalDetailsRequired () {
      return this.showVetSection
    },
    animalDetailsComplete () {
      if (!this.animalDetailsRequired) return true
      return Boolean(this.form.customer_type) && this.allAnimals.length > 0
    },
    // Org-2 (MDI) only: only the disease tag is mandatory on every org-2
    // interaction (>=1 tagged disease). The case notes (presenting / medical /
    // treatment history) are stored session-by-session and entered fresh when
    // relevant, so they — along with demographics and the report upload —
    // stay optional and never block Save / Convert.
    medicalDetailsRequired () {
      return this.showMedicalSection
    },
    medicalDetailsComplete () {
      if (!this.medicalDetailsRequired) return true
      return this.allDiseases.length > 0
    },
    isCallLogReady () {
      return Boolean(this.form.contact_phone && this.phoneValid && this.form.call_status)
    },
    // Human-readable expected phone length for the selected country, used
    // in the checklist hints — e.g. "10 digits" for India, "8 or 9
    // digits" for the UAE. Mirrors the country `nsn` table.
    phoneLengthHint () {
      const lengths = phoneLengthsFor(this.countryCode)
      if (!lengths.length) return 'valid'
      if (lengths.length === 1) return `${lengths[0]} digits`
      return `${lengths.slice(0, -1).join(', ')} or ${lengths[lengths.length - 1]} digits`
    },
    // Requirement checklist for Convert to Sale. Each row carries `ok`
    // (filled correctly) plus a `hint` describing what to do / the right
    // format when it isn't. The same predicates back `isFormReady`, so
    // the list and the button can never disagree.
    convertChecklist () {
      const f = this.form
      const phoneOk = Boolean(f.contact_phone) && this.phoneValid
      const items = [
        {
          key: 'name',
          label: 'Full Name',
          ok: this.contactNameValid,
          hint: !String(f.contact_name || '').trim()
            ? 'Enter the customer\'s full name.'
            : 'Enter the customer\'s real full name — a placeholder like "-" can\'t be shipped.',
          target: '[data-checklist="name"]'
        },
        {
          key: 'phone',
          label: 'Phone Number',
          ok: phoneOk,
          hint: !f.contact_phone
            ? 'Enter the customer\'s phone number.'
            : `This number looks incomplete — enter a valid ${this.phoneLengthHint} number for ${this.countryCode}.`,
          target: '[data-checklist="phone"]'
        },
        {
          key: 'alt_phone',
          label: 'Alternate Phone',
          ok: this.altPhoneValid,
          hint: !f.contact_phone_2
            ? `A second contact number is required — enter a valid ${this.phoneLengthHint} number.`
            : `The alternate number looks incomplete — enter a valid ${this.phoneLengthHint} number.`,
          target: '[data-checklist="alt_phone"]'
        },
        { key: 'address', label: 'Delivery Address', ok: Boolean(f.contact_address), hint: 'Enter the full delivery address.', target: '[data-checklist="address"]' },
        {
          key: 'items',
          label: 'At least one product',
          ok: this.form.order_items.some((item) => item.item_id && Number(item.quantity || 0) > 0),
          hint: 'Add at least one product with a quantity above zero.',
          target: '[data-checklist="items"]'
        },
        { key: 'payment', label: 'Payment Method', ok: Boolean(f.payment_method), hint: 'Choose how the customer will pay.', target: '[data-checklist="payment"]' },
        { key: 'shipping', label: 'Shipping Method', ok: Boolean(f.shipping_method), hint: 'Choose a shipping method.', target: '[data-checklist="shipping"]' }
      ]
      items.push(...this.orgTagChecklistItems)
      if (this.shippingZeroReason) {
        items.push({ key: 'shipping_zero', label: 'Shipping Charge', ok: false, hint: this.shippingZeroMessage, target: '[data-checklist="pincode"]' })
      }
      // Gated behind REQUIRE_PAYMENT_SCREENSHOT — only enforced when the flag
      // is on AND the selected method routes the order to Payment Approvals.
      if (REQUIRE_PAYMENT_SCREENSHOT && this.showPaymentScreenshotUpload) {
        items.push({
          key: 'payment_screenshot',
          label: 'Payment Proof',
          ok: this.paymentProofProvided,
          hint: 'Attach the transfer screenshot — or write a note if the customer didn\'t send one.',
          target: '[data-checklist="payment_screenshot"]'
        })
      }
      return items
    },
    // Org-specific tagging requirements (crops / animals / medical) shared by
    // BOTH the Convert to Sale and Save Call Log checklists, so the two lists
    // can never disagree. Each row carries a `target` so "Take me there"
    // scrolls straight to the offending block.
    orgTagChecklistItems () {
      const items = []
      if (this.cropDetailsRequired) {
        items.push({ key: 'crops', label: 'Crop Details', ok: this.cropDetailsComplete, hint: 'Select the customer type and add at least one crop.', target: '[data-checklist="crops"]' })
      }
      if (this.animalDetailsRequired) {
        items.push({ key: 'animals', label: 'Animal Details', ok: this.animalDetailsComplete, hint: 'Select the customer type and add at least one animal.', target: '[data-checklist="animals"]' })
      }
      if (this.medicalDetailsRequired) {
        items.push({ key: 'medical', label: 'Disease Tag', ok: this.medicalDetailsComplete, hint: 'Tag at least one disease for this customer.', target: '[data-checklist="disease"]' })
      }
      return items
    },
    // Requirement checklist for Save Call Log — a valid phone number and a
    // call status, plus the same org tagging the sale requires (per the
    // "always, for every call log" rule).
    callLogChecklist () {
      const f = this.form
      const phoneOk = Boolean(f.contact_phone) && this.phoneValid
      return [
        {
          key: 'phone',
          label: 'Phone Number',
          ok: phoneOk,
          hint: !f.contact_phone
            ? 'Enter the customer\'s phone number.'
            : `This number looks incomplete — enter a valid ${this.phoneLengthHint} number for ${this.countryCode}.`,
          target: '[data-checklist="phone"]'
        },
        {
          key: 'call_status',
          label: 'Call Status',
          ok: Boolean(f.call_status),
          hint: 'Select how this call ended (e.g. Interested, Not Reachable).',
          target: '[data-checklist="call_status"]'
        },
        ...this.orgTagChecklistItems
      ]
    },
    activeChecklist () {
      return this.checklistMode === 'callLog' ? this.callLogChecklist : this.convertChecklist
    },
    checklistTitle () {
      const missing = this.activeChecklist.filter((item) => !item.ok).length
      if (this.checklistMode === 'callLog') {
        return missing === 1 ? 'One thing before saving the call log' : 'A couple of things before saving'
      }
      return missing === 1 ? 'One thing before converting to sale' : 'A few things before converting to sale'
    },
    checklistSubtitle () {
      const ready = this.activeChecklist.filter((item) => item.ok).length
      const total = this.activeChecklist.length
      const left = total - ready
      return `${ready} of ${total} ready — finish the highlighted ${left === 1 ? 'field' : 'fields'} to continue.`
    },
    productDraftItem () {
      return this.itemOptions.find((option) => option.value === String(this.productDraft.item_id)) || null
    },
    productDraftPrice () {
      return Number(this.productDraftItem?.price || 0)
    },
    productDraftTotal () {
      return Number(this.productDraftPrice || 0) * Number(this.productDraft.quantity || 0)
    },
    totalQuantity () {
      return this.form.order_items.reduce((acc, item) => acc + Number(item.quantity || 0), 0)
    },
    // Sum of qty × per-item shipping weight (g). The backend uses this to
    // pick the right tariff slab; without it, every item falls back to the
    // 200 g flat assumption and heavy SKUs (e.g. Agro Yield Super 1 kg)
    // are billed at the 51–200 g rate. Prefer shipping_weight over weight,
    // fall back to the org flat (200 g for non-medical, 700 g for org 2)
    // only when neither is populated on the catalogue row.
    orderTotalWeightGrams () {
      const fallback = this.activeOrganisationId === 2 ? 700 : 200
      const byId = new Map(this.itemOptions.map((opt) => [String(opt.value), opt]))
      return this.form.order_items.reduce((sum, line) => {
        const opt = byId.get(String(line.item_id))
        const perItem = Number(opt?.shipping_weight || opt?.weight || 0) || fallback
        return sum + (Number(line.quantity || 0) * perItem)
      }, 0)
    },
    totalAfterDiscount () {
      return Number(this.form.total || 0) - Number(this.form.discount || 0)
    },
    // Discount presets — mirror of the OrderForm wiring. Tele-sales is
    // the primary non-privileged user of the discount field, so the
    // lock+toggle here is the main UX surface for the enforcement.
    // Resolution rules live in [src/utils/discountPresets.js].
    activeDiscountTiers () {
      return (Array.isArray(this.discountPresets) ? this.discountPresets : [])
        .filter((t) => t && Boolean(t.active))
        .slice()
        .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity))
    },
    eligibleDiscountBuckets () {
      const lines = this.form.order_items.map((l) => ({
        item_id: l.item_id,
        quantity: l.quantity,
        subtotal: l.total,
      }))
      return eligibleDiscountBuckets(this.activeDiscountTiers, lines)
    },
    eligibleDiscountTier () {
      // Kept for v-if compatibility: truthy when any bucket qualifies.
      return this.eligibleDiscountBuckets[0]?.tier || null
    },
    eligibleDiscountAmount () {
      return eligibleDiscountTotal(this.eligibleDiscountBuckets)
    },
    eligibleDiscountTierLabel () {
      return eligibleDiscountLabel(this.eligibleDiscountBuckets)
    },
    // ── Parcel splitting ────────────────────────────────────────────────
    // Cart lines in the shape utils/parcelSplit.js works in: quantity, unit
    // price and per-unit shipping weight. Weight falls back to the org flat
    // the same way orderTotalWeightGrams does, so the split and the shipping
    // quote can never disagree about how heavy the cart is.
    parcelCartLines () {
      const fallback = this.activeOrganisationId === 2 ? 700 : 200
      const byId = new Map(this.itemOptions.map((opt) => [String(opt.value), opt]))
      return this.form.order_items
        .filter((line) => Number(line.quantity || 0) > 0)
        .map((line) => {
          const opt = byId.get(String(line.item_id))
          return {
            item_id: line.item_id,
            name: line.name,
            sku: line.sku,
            shortcode: line.shortcode,
            quantity: Number(line.quantity || 0),
            price: Number(line.price || 0),
            shipping_weight: Number(opt?.shipping_weight || opt?.weight || 0) || fallback
          }
        })
    },
    activeParcelLimits () {
      return limitsForMethod(this.parcelLimitsMap, this.form.shipping_method)
    },
    // True once the cart is past the cap for the CHOSEN method. Methods with
    // no cap (Professional Courier, direct) never trip this.
    needsParcelSplit () {
      if (!this.parcelCartLines.length) return false
      const limits = this.activeParcelLimits
      if (!limits.maxUnits && !limits.maxWeightGrams) return false
      return !fitsInOneParcel(limits, this.parcelCartLines)
    },
    // A single unit heavier than the whole cap can't be packed at all — no
    // split rescues it, the method has to change.
    unshippableParcelItems () {
      return oversizedUnits(this.activeParcelLimits, this.parcelCartLines)
    },
    parcelLimitLabel () {
      const { maxUnits, maxWeightGrams } = this.activeParcelLimits
      const parts = []
      if (maxUnits) parts.push(`${maxUnits} bottles`)
      if (maxWeightGrams) parts.push(`${Number((maxWeightGrams / 1000).toFixed(2))} kg`)
      return parts.join(' / ')
    },
    // How many parcels the cart currently needs — drives the banner copy
    // before the preview modal is ever opened.
    parcelSplitCount () {
      if (!this.needsParcelSplit) return 1
      return splitCartIntoParcels(this.activeParcelLimits, this.parcelCartLines).length
    },
    parcelCartMeasure () {
      return measureCart(this.parcelCartLines)
    },
    canOverrideCharges () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => CHARGE_OVERRIDE_ROLES.includes(r))
    },
    isTelesalesRole () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.includes('telesales')
    },
    // Telesales staff must never submit a `tele` (telesale) sale with ₹0
    // shipping — the most common cause is a missed pincode/post office.
    // Other roles and order types (store, agency, etc.) legitimately
    // ship ₹0, so the rule is scoped here. Empty carts are exempt because
    // the shipping calc only runs once items exist.
    shippingZeroReason () {
      if (!this.isTelesalesRole) return null
      if (this.form.order_type !== 'tele') return null
      if (!this.form.order_items.length) return null
      if (Number(this.form.shipping_charge || 0) > 0) return null
      if (!this.form.contact_pincode || this.form.contact_pincode.length !== 6) return 'pincode_missing'
      if (!this.form.contact_postofficename) return 'post_office_missing'
      return 'lookup_failed'
    },
    shippingZeroMessage () {
      switch (this.shippingZeroReason) {
        case 'pincode_missing':
          return 'Pincode is missing or incomplete. Shipping is auto-calculated from the pincode — enter a valid 6-digit pincode.'
        case 'post_office_missing':
          return 'Post office is not selected. Pick one from the dropdown so shipping can be calculated.'
        case 'lookup_failed':
          return "Shipping couldn't be auto-calculated for this pincode. Re-enter the pincode or pick a different post office."
        default:
          return ''
      }
    },
    discountFieldsLocked () {
      if (this.applyPreset) return true
      return !this.canOverrideCharges
    },
    shippingFieldLocked () {
      // Privileged roles can always override the shipping charge.
      if (this.canOverrideCharges) return false
      // For everyone else (telesales staff), the charge stays locked by
      // default. It only unlocks once a method *other than* the rate-fixed
      // ones — India Post or Professional Courier, whose charge is
      // auto-calculated from the tariff — is explicitly selected. The
      // auto-calc handler still sets form.shipping_charge programmatically,
      // which :disabled doesn't block.
      const method = this.form.shipping_method
      return !method || RATE_LOCKED_SHIPPING_METHODS.includes(method)
    },
    searchHintClass () {
      if (!this.searchHint) return 'text-muted'
      if (this.searchHint.toLowerCase().includes('not found')) return 'text-warning'
      if (this.searchHint.toLowerCase().includes('found')) return 'text-success'
      if (this.searchHint.toLowerCase().includes('unable')) return 'text-danger'
      return 'text-muted'
    },
    formattedTimer () {
      const total = Math.max(0, Number(this.timerSeconds || 0))
      const minutes = String(Math.floor(total / 60)).padStart(2, '0')
      const seconds = String(total % 60).padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    // Calls scoped to the org the staff is currently working in. The contact
    // search runs cross-org (cross_org=1) so the raw list mixes calls logged
    // by sibling orgs' teams; the Recent Calls panel and every cue derived
    // from it must only reflect this org's call history. Filter preserves the
    // API's id-DESC order, so [0] stays the org's most recent call.
    recentCalls () {
      const orgId = this.activeOrganisationId
      if (!orgId) return this.contactCalls
      return this.contactCalls.filter((call) => Number(call.organisation_id) === orgId)
    },
    hasContactHistory () {
      return this.recentCalls.length > 0 || this.contactOrders.length > 0 || this.contactHmsVisits.length > 0
    },
    lifetimeSpend () {
      return this.contactOrders.reduce((sum, order) => sum + Number(order.grand_total || 0), 0)
    },
    recentOrders () {
      if (!Array.isArray(this.contactOrders) || !this.contactOrders.length) return []
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000
      const orgId = this.activeOrganisationId
      return this.contactOrders.filter((order) => {
        if (!order || !order.order_date) return false
        // The duplicate-order warning must only fire for orders placed in
        // the org the staff is working in — cross-org search returns sibling
        // orgs' orders, which aren't duplicates of a new order here.
        if (orgId && Number(order.organisation_id) !== orgId) return false
        const ts = new Date(order.order_date).getTime()
        return Number.isFinite(ts) && ts >= cutoff
      })
    },
    // Ids of orders placed within the recent window — used to flag the matching
    // order card in the Recent Orders list with a red duplicate-order warning.
    recentOrderIdSet () {
      return new Set(this.recentOrders.map((order) => Number(order.id)))
    },
    lastCallSummary () {
      if (!this.recentCalls.length) return '—'
      const last = this.recentCalls[0]
      return last.status || '—'
    },
    // How fresh a "Call Again High" call must be to count as high priority,
    // per org: Agro (1) and Vet (4) work a 15-day window, Drugs (2) a 45-day
    // window. Other orgs default to 45 days.
    highPriorityWindowDays () {
      const byOrg = { 1: 15, 2: 45, 4: 15 }
      return byOrg[this.activeOrganisationId] || 45
    },
    // The most-recent org call (recentCalls[0], id-DESC) flagged "Call Again
    // High" and dialled within the org's high-priority window. When set, the
    // contact is an active high-priority lead owned by call.caller, and the
    // UI surfaces a red banner under the phone search + tints that call row.
    highPriorityCall () {
      const last = this.recentCalls[0]
      if (!last) return null
      // Owned by a staff member who has since left — the lead no longer
      // belongs to an active rep, so suppress the high-priority cue.
      if (last.caller_active === false) return null
      // The API maps status `call_again_high` → "Call Again High" via
      // ucwords(str_replace('_',' ',…)), so match on the spaced label.
      const status = String(last.status || '').toLowerCase().replace(/\s+/g, '_')
      if (status !== 'call_again_high') return null
      const ts = this.parseCallDate(last.date)
      if (!Number.isFinite(ts)) return null
      const ageDays = (Date.now() - ts) / (24 * 60 * 60 * 1000)
      if (ageDays >= this.highPriorityWindowDays) return null
      return last
    },
    // Order ids that have BOTH a recent order row AND a linked "sold" call
    // for this number. A sold call carries the order_id it closed (set when
    // the order is created), so a match means the order was won on a call —
    // both the order and the call render green to visually pair them.
    matchedSaleOrderIds () {
      const orderIds = new Set(this.contactOrders.map((order) => Number(order.id)))
      const matched = new Set()
      this.recentCalls.forEach((call) => {
        const status = String(call.status || '').toLowerCase()
        const orderId = Number(call.order_id || 0)
        if (status === 'sold' && orderId && orderIds.has(orderId)) {
          matched.add(orderId)
        }
      })
      return matched
    },
    postOfficeDropdownOptions () {
      return this.postOfficeOptions.map((name) => ({ value: name, label: name }))
    },
    localBodyDropdownOptions () {
      return this.localBodyOptions.map((body) => ({
        value: body.local_body_name,
        label: body.local_body_name
      }))
    },
    visibleSessions () {
      if (this.showAllSessions) return this.patientSessions
      return this.patientSessions.slice(0, this.collapsedSessionLimit)
    },
    hasMedicalNotes () {
      return Boolean(
        String(this.form.presenting_symptoms || '').trim()
        || String(this.form.medical_history || '').trim()
        || String(this.form.treatment_history || '').trim()
      )
    },
    medicalDemographicsComplete () {
      return Number(this.form.age || 0) > 0
        && String(this.form.gender || '').trim() !== ''
        && String(this.form.marital_status || '').trim() !== ''
    },
    medicalNotesBlocked () {
      // org-2 only — when the agent typed medical context but didn't
      // fill the demographics the `patients` NOT NULL columns require,
      // the save silently dropped the medical fields. Surface the
      // block in the UI and prevent submit so the data is never lost.
      return this.showMedicalSection
        && this.hasMedicalNotes
        && !this.medicalDemographicsComplete
    },
    medicalBlockReason () {
      if (!this.medicalNotesBlocked) return ''
      const missing = []
      if (!(Number(this.form.age || 0) > 0)) missing.push('Age')
      if (!String(this.form.gender || '').trim()) missing.push('Gender')
      if (!String(this.form.marital_status || '').trim()) missing.push('Marital Status')
      return `Heads up — ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} missing. Your medical notes will still save, but filling these makes future calls easier.`
    },
    historyCondensedSummary () {
      const callCount = this.recentCalls.length
      const orderCount = this.contactOrders.length
      const visitCount = this.contactHmsVisits.length
      const parts = []
      parts.push(`${callCount} call${callCount === 1 ? '' : 's'}`)
      parts.push(`${orderCount} order${orderCount === 1 ? '' : 's'}`)
      parts.push(`${visitCount} visit${visitCount === 1 ? '' : 's'}`)
      if (orderCount > 0) parts.push(this.formatCurrency(this.lifetimeSpend))
      return parts.join(' · ')
    },
    userFirstName () {
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        const name = user && typeof user.name === 'string' ? user.name.trim() : ''
        if (!name) return ''
        return name.split(/\s+/)[0]
      } catch (e) {
        return ''
      }
    }
  },
  created () {
    this.loadBootstrapData()
    this.userSaleCount = this.readStoredSaleCount()
    // Allow links like /telesales?phone=9876543210 (used by the Call Queue
    // page) to prefill the phone field and auto-trigger the contact search.
    const prefilledPhone = this.$route && this.$route.query ? this.$route.query.phone : ''
    if (prefilledPhone) {
      this.setPhone(prefilledPhone)
      this.$nextTick(() => {
        if (this.form.contact_phone && this.phoneValid) {
          this.searchContact()
        }
      })
    }
    // Start at the top of the form on first mount.
    this.scrollPageToTop(false)
  },
  // Inside <keep-alive>, so created() only fires once. On every revisit
  // (e.g. arriving from the Call Queue's "Call" button) reset the scroll
  // to the top so the form doesn't inherit the previous page's position.
  activated () {
    this.scrollPageToTop(false)
  },
  beforeDestroy () {
    if (this.timerHandle) clearInterval(this.timerHandle)
    if (this.chargeEstimateTimer) clearTimeout(this.chargeEstimateTimer)
    window.removeEventListener('keydown', this.onPreviewKey)
  },
  methods: {
    getInputValue (input) {
      return input && typeof input === 'object' && input.target ? input.target.value : input
    },
    formatCurrency (value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? `₹${numeric.toFixed(2)}` : '₹0.00'
    },
    formatCallDate (date) {
      if (!date) return ''
      const parts = String(date).split('-')
      if (parts.length !== 3) return String(date)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const day = Number(parts[0])
      const month = months[Number(parts[1]) - 1] || parts[1]
      return `${day} ${month} ${parts[2]}`
    },
    // Clock time the call was logged, always in IST. The API sends a
    // pre-formatted `time` ("03:24 PM") off the same created_at as `date`;
    // fall back to converting a raw created_at so cached/older payloads
    // without the field still render a time instead of a blank.
    formatCallTime (call) {
      if (!call) return ''
      if (call.time) return String(call.time)
      if (!call.created_at) return ''
      const ts = new Date(call.created_at)
      if (Number.isNaN(ts.getTime())) return ''
      return ts.toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    },
    // Call dates arrive as the API's `d-m-Y` string (e.g. "06-06-2026").
    // new Date("06-06-2026") is invalid in JS, so parse the parts by hand;
    // returns a ms timestamp, or NaN when the value can't be parsed.
    parseCallDate (date) {
      if (!date) return NaN
      const parts = String(date).split('-')
      if (parts.length !== 3) {
        const fallback = new Date(date).getTime()
        return Number.isFinite(fallback) ? fallback : NaN
      }
      const day = Number(parts[0])
      const month = Number(parts[1])
      const year = Number(parts[2])
      if (!day || !month || !year) return NaN
      return new Date(year, month - 1, day).getTime()
    },
    formatOrderDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return String(iso)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatPatientMeta (visit) {
      const bits = []
      if (visit.age !== null && visit.age !== undefined && String(visit.age) !== '') bits.push(`${visit.age}y`)
      if (visit.gender) bits.push(this.formatLabel(visit.gender))
      return bits.join(' · ')
    },
    callStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('call again') && s.includes('high')) return 'is-danger'
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended')) return 'is-warn'
      if (s.includes('feedback')) return 'is-info'
      if (s.includes('follow')) return 'is-info'
      return 'is-default'
    },
    // True when this call is a "sold" call whose order is also in the
    // recent-orders list — used to green-highlight the matching pair.
    isMatchedSaleCall (call) {
      if (!call) return false
      const status = String(call.status || '').toLowerCase()
      const orderId = Number(call.order_id || 0)
      return status === 'sold' && orderId > 0 && this.matchedSaleOrderIds.has(orderId)
    },
    paymentStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'text-success'
      if (s.includes('pending')) return 'text-warning'
      return ''
    },
    setPhone (value) {
      const next = this.normalizePhoneInput(value, { syncCountry: true })
      // Customer is changing — a stale codBlocked from the previous
      // lookup must not gate the new customer's options. The flag
      // re-resolves when searchContact runs against the new number.
      if (next !== this.form.contact_phone) {
        this.codBlocked = false
      }
      this.form.contact_phone = next
    },
    setAlternatePhone (value) {
      // Peel any country-code prefix on the alternate phone too, but
      // don't sync the country dropdown from it — the primary phone
      // is the canonical source of the customer's country, and a
      // stray paste here (e.g. an Indian alt for a Saudi customer)
      // shouldn't silently flip the whole form's country.
      this.form.contact_phone_2 = this.normalizePhoneInput(value, { syncCountry: false })
    },
    normalizePhoneInput (value, { syncCountry = false } = {}) {
      // Strip non-digits, then handle pasted numbers that include a
      // country-code prefix (e.g. "+919207948134", "+966500000000").
      // Peel the matching dial code so the field shows only the
      // local subscriber number, and — when syncCountry is on —
      // switch the country dropdown to match so staff don't have to
      // re-select it manually.
      //
      // Trigger conditions: explicit "+" prefix in the raw input
      // (clear paste signal) OR more digits than the selected country's
      // local number can hold (a typed local number can't exceed that,
      // so the excess is almost always a pasted country code). Falls
      // back to the last `maxLen` digits when no known country code
      // matches, preserving recovery for common malformed pastes
      // (0-leader, ISD code, etc.). `maxLen` is country-aware — 10 for
      // India, 9 for Saudi (+966), etc. — so Gulf numbers aren't
      // truncated to an Indian-sized 10.
      const raw = String(value || '')
      let digits = raw.replace(/\D/g, '')

      let maxLen = maxPhoneLengthFor(this.countryCode)
      const shouldPeel = digits.length > maxLen || raw.trim().startsWith('+')

      if (shouldPeel) {
        const match = findCountryByDialCode(digits)
        if (match) {
          const codeDigits = match.code.replace(/^\+/, '')
          digits = digits.slice(codeDigits.length)
          // Re-cap to the matched country's local length so the peeled
          // subscriber number isn't clipped to the old country's size.
          maxLen = maxPhoneLengthFor(match.name)
          if (syncCountry && this.countryCode !== match.name) {
            this.countryCode = match.name
          }
        } else if (digits.length > maxLen) {
          digits = digits.slice(-maxLen)
        }
      }

      return digits.slice(0, maxLen)
    },
    toggleGstin (checked) {
      this.includeGstin = Boolean(checked)
      if (!this.includeGstin) {
        this.form.contact_gstn = ''
        this.form.gst_percentage = 0
      } else {
        this.form.gst_percentage = 5
        // Prefill from the searched customer's stored GSTIN, but only when
        // staff opt in by ticking the box and the field is still empty.
        if (!this.form.contact_gstn && this.contactGstinOnFile) {
          this.form.contact_gstn = this.contactGstinOnFile
        }
      }
      this.recalculateTotals()
    },
    toggleBilling (checked) {
      this.addBillingAddress = Boolean(checked)
      if (!this.addBillingAddress) {
        this.form.billing_name = ''
        this.form.billing_address = ''
        this.form.billing_pincode = ''
        this.form.billing_postofficename = ''
        this.form.billing_district = ''
        this.form.billing_state = ''
      }
    },
    buildDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: entry.slug || entry.value || entry.id || '',
        label: entry.name || entry.text || entry.slug || ''
      })).filter((entry) => entry.value || entry.label)
    },
    normalizeResponseArray (value) {
      return Array.isArray(value) ? value : []
    },
    calculateTotals () {
      let total = 0
      this.form.order_items.forEach((item) => {
        item.total = Number(item.quantity || 0) * Number(item.price || 0)
        total += Number(item.total || 0)
      })

      this.form.total = total

      // Preset toggle ON: sync the discount inputs from the eligible
      // buckets so the user sees the value the server will accept. The
      // applied discount is always a flat AMOUNT — the sum across
      // buckets (one per product-scoped line + at most one for the
      // aggregated org-wide bucket). Auto-disengage when nothing
      // qualifies.
      if (this.applyPreset) {
        const amount = this.eligibleDiscountAmount
        if (amount <= 0) {
          this.applyPreset = false
          this.discountMode = 'amount'
          this.discountInput = 0
        } else {
          this.discountMode = 'amount'
          this.discountInput = amount
        }
      }

      if (this.discountMode === 'percent') {
        this.form.discount = (total * Number(this.discountInput || 0)) / 100
      } else {
        this.form.discount = Number(this.discountInput || 0)
      }

      this.form.total = total
      const totalAfterDiscount = total - Number(this.form.discount || 0)
      // GST applies only when staff opt in via "Include GST IN" (B2B). MRP is
      // GST-inclusive, so a 0% means no tax is extracted for reporting.
      const gstPercent = this.includeGstin ? Number(this.form.gst_percentage || 5) : 0
      this.form.gst_percentage = gstPercent
      // Product prices are MRP — already inclusive of GST. Extract the tax
      // component for reporting (cgst/sgst/igst), but do NOT add it to
      // grand_total; that would double-count.
      this.form.gst_amt = Number(((totalAfterDiscount * gstPercent) / (100 + gstPercent)).toFixed(2))

      const isKerala = String(this.form.contact_state || '').trim().toLowerCase() === 'kerala'
      this.form.gst_state_code = isKerala ? 'KL' : ''
      if (isKerala) {
        this.form.cgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.sgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.igst_amt = 0
      } else {
        this.form.cgst_amt = 0
        this.form.sgst_amt = 0
        this.form.igst_amt = this.form.gst_amt
      }

      this.form.grand_total = Number((totalAfterDiscount + Number(this.form.shipping_charge || 0) + Number(this.form.cod_fee || 0)).toFixed(2))
      this.amountToPay = this.form.grand_total
    },
    onDiscountInput (event) {
      this.discountInput = Number(this.getInputValue(event) || 0)
      this.calculateTotals()
    },
    onApplyPresetToggle (checked) {
      this.applyPreset = Boolean(checked)
      if (!this.applyPreset) {
        this.discountMode = 'amount'
        this.discountInput = 0
      }
      this.calculateTotals()
    },
    loadDiscountPresets (organisationId) {
      const orgId = Number(organisationId) || null
      this.formOrganisationId = orgId
      if (!orgId) {
        this.discountPresets = []
        return Promise.resolve([])
      }
      return axios.get(`${this.$apiAdress}/api/discount-presets/by-org/${orgId}`, { timeout: 10000 })
        .then((response) => {
          this.discountPresets = Array.isArray(response.data) ? response.data : []
          return this.discountPresets
        })
        .catch(() => {
          this.discountPresets = []
          return []
        })
    },
    onShippingChargeInput (event) {
      this.form.shipping_charge = Number(this.getInputValue(event) || 0)
      this.calculateTotals()
    },
    loadBootstrapData () {
      this.errorMessage = ''
      this.isLoading = true
      // TeleSales fetches the catalogue with the implicit user-org
      // scope on the backend; use the same active-org id to pull the
      // matching discount tiers so the toggle reflects the data the
      // submission will be validated against.
      const orgId = Number(this.$store?.state?.activeOrganisationId) || null
      this.loadDiscountPresets(orgId)
      this.loadParcelLimits()
      // Scope the items catalogue to the active org explicitly — the
      // backend would otherwise fall back to Auth::user()->organisation_id,
      // which lags the in-flight change_my_org POST when staff switch
      // orgs in the sidebar and shows the previous org's products.
      const formDataParams = orgId ? { organisation_id: orgId } : {}
      axios.get(`${this.$apiAdress}/api/order_form_data`, { params: formDataParams })
        .then((response) => {
          const data = response.data || {}
          this.callTypeOptions = this.buildDropdown(data.drop_down_items?.call_type)
          this.callStatusOptions = this.buildDropdown(data.drop_down_items?.call_status)
          this.paymentMethodOptions = this.buildDropdown(data.drop_down_items?.payment_method)
          this.shippingMethodOptions = this.buildDropdown(data.drop_down_items?.shipping_method)
            .filter((opt) => opt.value !== 'ip_registered_parcel')
          this.itemOptions = this.normalizeResponseArray(data.items).map((item) => ({
            value: String(item.id),
            label: item.name,
            sku: item.sku || '',
            shortcode: item.shortcode || '',
            image: item.image || '',
            price: Number(item.selling_price ?? item.price ?? item.rate ?? item.sale_price ?? item.unit_price ?? item.mrp ?? 0),
            weight: Number(item.weight || 0),
            shipping_weight: Number(item.shipping_weight || 0)
          }))
          this.productOptions = this.normalizeResponseArray(data.items).map((item) => ({
            value: String(item.id),
            label: item.name
          }))
          this.replyOptions = this.normalizeResponseArray(data.drop_down_items?.message_type || data.drop_down_items?.replies).map((item) => ({
            value: item.slug || item.id || item.name,
            label: item.name || item.slug || ''
          }))
          this.cropOptions = this.normalizeResponseArray(data.crops).map((crop) => crop.name).filter(Boolean)
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load telesales bootstrap data.'
        })
        .finally(() => {
          this.isLoading = false
        })

      axios.get(`${this.$apiAdress}/api/followup_grade_list`)
        .then((response) => {
          this.followupGradeOptions = this.buildDropdown(this.normalizeResponseArray(response.data))
        })
        .catch((error) => {
          console.log(error)
        })
    },
    // Shipping/COD is priced from the cart (weight, quantity, total) AND
    // the pincode, so any change to the order items must re-fetch the
    // estimate — not just recompute totals off the stale charge. Debounced
    // so typing a quantity doesn't fire a request per keystroke. Without
    // this, staff had to re-enter the pincode to make shipping reflect a
    // cart they'd already edited.
    scheduleChargeEstimate () {
      if (this.chargeEstimateTimer) clearTimeout(this.chargeEstimateTimer)
      this.chargeEstimateTimer = setTimeout(() => {
        this.chargeEstimateTimer = null
        this.loadChargeEstimate()
      }, 350)
    },
    // Wipes the "why is shipping this much" context. Called wherever the
    // charge is zeroed or the estimate fails, so a stale zone / distance chip
    // can never outlive the quote it came from.
    clearShippingRateContext () {
      this.shippingDistanceKm = null
      this.shippingZone = null
      this.shippingZoneLabel = ''
    },
    applyShippingRateContext (data) {
      const dist = Number(data?.distance_km)
      this.shippingDistanceKm = Number.isFinite(dist) && dist > 0 ? dist : null
      this.shippingZone = data?.zone || null
      this.shippingZoneLabel = data?.zone_label || ''
    },
    loadChargeEstimate () {
      if (!this.form.contact_pincode || !this.form.order_items.length) {
        this.form.shipping_charge = 0
        this.form.cod_fee = 0
        this.clearShippingRateContext()
        this.calculateTotals()
        return
      }

      axios.post(`${this.$apiAdress}/api/cal_cod_charges`, {
        organizationId: this.activeOrganisationId,
        pincode: this.form.contact_pincode,
        quantity: this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        total: Number(this.form.total || 0),
        qtyAYS: 0,
        total_weight_grams: this.orderTotalWeightGrams,
        paymentMethod: this.form.payment_method,
        shippingMethod: this.form.shipping_method
      })
        .then((response) => {
          this.form.shipping_charge = Number(response.data?.base_shipping ?? response.data?.cod_charge ?? 0)
          this.form.cod_fee = Number(response.data?.cod_fee || 0)
          this.applyShippingRateContext(response.data)
          this.calculateTotals()
        })
        .catch((error) => {
          console.log(error)
          this.form.shipping_charge = 0
          this.form.cod_fee = 0
          this.clearShippingRateContext()
          this.calculateTotals()
        })
    },
    searchContact () {
      if (!this.form.contact_phone || !this.phoneValid) {
        this.searchHint = 'Enter a valid phone number.'
        return
      }

      this.isSearching = true
      this.searchHint = 'Searching contact...'

      axios.get(`${this.$apiAdress}/api/contacts/phone/${encodeURIComponent(this.fullContactPhone)}?cross_org=1`)
        .then((response) => {
          const data = response.data || {}
          const contact = Array.isArray(data.contact) && data.contact.length ? data.contact[0] : null
          const orders = Array.isArray(data.orders) ? data.orders : []
          this.contactCrops = this.normalizeCropList(data.crops)
          this.existingCrops = []
          this.contactAnimals = Array.isArray(data.animals) ? data.animals.filter(Boolean) : []
          this.existingAnimals = []
          this.contactDiseases = Array.isArray(data.diseases) ? data.diseases.filter(Boolean) : []
          this.existingDiseases = []
          // Re-hydrate the agro customer tag so a re-search shows the
          // type set on a previous call. Only overwrite when the contact
          // actually carries one — never blank out an in-progress pick.
          if (contact && contact.customer_type) {
            this.form.customer_type = contact.customer_type
          }
          this.contactCalls = Array.isArray(data.calls) ? data.calls : []
          this.contactOrders = orders
          this.contactHmsVisits = Array.isArray(data.hms_patients) ? data.hms_patients : []
          // TEMP: COD-block-on-prior-return is disabled for now. Keep COD
          // available regardless of `data.cod_blocked`. Re-enable by
          // restoring: this.codBlocked = Boolean(data.cod_blocked)
          this.codBlocked = false
          // Restore demographics + latest medical session into the
          // form. Without this, resetForm() after a save leaves these
          // fields blank on re-search, even though the Patient and
          // PatientSession rows exist — staff then assume their
          // previous save didn't persist and re-type the same data.
          this.applyPatientDetails(data.patient || null)
          // If the agent had already picked COD before the lookup
          // resolved, snap it off so the form can't submit something
          // the backend will reject. loadChargeEstimate runs inside
          // onPaymentMethodChange to clear the now-stale COD fee.
          if (this.codBlocked && this.form.payment_method === 'cod') {
            this.onPaymentMethodChange('')
          }

          // Each save with a new pincode appends an Address row keyed
          // by (contact_id, pincode). The default hasMany returns them
          // id-ASC, so the original `find()` picked the OLDEST row and
          // hid recent updates — sort id-DESC first so the latest
          // pincode the agent just typed is what shows on re-search.
          const addresses = contact && Array.isArray(contact.address)
            ? contact.address.slice().sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
            : []
          const primaryAddress = addresses.find((address) => Number(address.pincode || 0) > 0) || addresses[0] || null
          const latestOrder = orders[0] || null

          const resolvedName = (contact && contact.name) || (latestOrder && latestOrder.contact_name) || ''
          const resolvedAddress = (primaryAddress && primaryAddress.address) || (latestOrder && latestOrder.contact_address) || ''
          const resolvedPincode = (primaryAddress && primaryAddress.pincode) || (latestOrder && latestOrder.contact_pincode) || ''
          const resolvedPostOffice = (primaryAddress && primaryAddress.post_office_name) || (latestOrder && latestOrder.contact_postofficename) || ''
          const resolvedCity = (primaryAddress && primaryAddress.district) || (latestOrder && latestOrder.contact_district) || ''
          const resolvedState = (primaryAddress && primaryAddress.state) || (latestOrder && latestOrder.contact_state) || ''
          const resolvedLocalBody = (primaryAddress && primaryAddress.local_body_name) || (latestOrder && latestOrder.local_body_name) || ''
          const resolvedLocalBodyType = (primaryAddress && primaryAddress.local_body_type_name) || (latestOrder && latestOrder.local_body_type_name) || ''
          const resolvedWardNo = (primaryAddress && primaryAddress.ward_no) || (latestOrder && latestOrder.ward_no) || 0
          const resolvedGstin = (primaryAddress && primaryAddress.gstin) || (latestOrder && latestOrder.contact_gstn) || ''

          // Remember the customer's on-file GSTIN, but never auto-enable the
          // toggle from it — auto-filling silently flipped orders to B2B. We
          // only prefill the field when staff have already ticked the box;
          // toggleGstin() handles the tick-after-search case.
          this.contactGstinOnFile = resolvedGstin

          // Alternate phone — pick the latest Address row that has a
          // non-empty `address_phone`, regardless of pincode. This
          // covers two cases the primaryAddress lookup missed:
          //  1. The agent saved just an alt phone (no pincode) — the
          //     backend writes a pincode=0 placeholder Address, which
          //     primaryAddress (find pincode>0) would skip.
          //  2. The contact has a real address row AND a placeholder;
          //     we want the most-recent address_phone across both.
          // Falls back to the order's frozen contact_phone_2 only
          // when no Address has ever carried this customer's alt
          // phone.
          const altPhoneSource = addresses.find((address) => address.address_phone) || null
          const rawAltPhone = (altPhoneSource && altPhoneSource.address_phone)
            || (latestOrder && latestOrder.contact_phone_2)
            || ''
          // Peel any stored country-code prefix and cap to the local
          // length (country-aware — 9 for Saudi, 10 for India) rather
          // than blindly keeping the last 10 digits, which would mangle
          // a sub-10-digit Gulf number.
          const resolvedAltPhone = this.normalizePhoneInput(rawAltPhone, { syncCountry: false })

          if (resolvedName || resolvedAddress || resolvedPincode) {
            this.form.contact_name = resolvedName || this.form.contact_name
            this.form.contact_phone_2 = resolvedAltPhone || this.form.contact_phone_2
            this.form.contact_address = resolvedAddress || this.form.contact_address
            this.form.contact_pincode = resolvedPincode ? String(resolvedPincode) : this.form.contact_pincode
            this.form.contact_postofficename = resolvedPostOffice || this.form.contact_postofficename
            this.form.contact_city = resolvedCity || this.form.contact_city
            this.form.contact_state = resolvedState || this.form.contact_state
            this.form.local_body_name = resolvedLocalBody || this.form.local_body_name
            this.form.local_body_type_name = resolvedLocalBodyType || this.form.local_body_type_name
            this.form.ward_no = Number(resolvedWardNo || 0)
            if (this.includeGstin && resolvedGstin) {
              this.form.contact_gstn = resolvedGstin
            }
            this.selectedLocalBodyName = this.form.local_body_name || ''
            if (this.form.contact_pincode && this.form.contact_pincode.length === 6) {
              this.searchPincode(true)
            }
            this.searchHint = 'Contact found.'
          } else {
            this.searchHint = 'Contact not found.'
          }

          // Restore the latest call's comments so the agent can verify
          // what was previously noted (and edit it). Scope to this org's
          // calls (recentCalls, id-DESC) so a sibling org's note doesn't
          // bleed into this org's new call.
          const latestCall = (this.recentCalls && this.recentCalls.length) ? this.recentCalls[0] : null
          if (latestCall && latestCall.comments) {
            this.form.comments = latestCall.comments
          }

          // Surface the customer summary the moment a known contact is
          // found, so the agent reviews past orders/calls before placing
          // a new order. The inline Contact History section stays as the
          // persistent reference; this modal is the up-front prompt.
          if (this.hasContactHistory) {
            this.showHistoryModal = true
          }
        })
        .catch((error) => {
          console.log(error)
          this.searchHint = 'Unable to fetch contact details.'
          this.contactCalls = []
          this.contactOrders = []
          this.contactHmsVisits = []
          this.patientSessions = []
          this.openSessionIds = []
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    onPincodeInput (value) {
      const next = String(this.getInputValue(value) || '').replace(/\D/g, '').slice(0, 6)
      const changed = next !== this.form.contact_pincode
      this.form.contact_pincode = next
      if (next.length < 6) {
        this.postOfficeRecords = []
        this.postOfficeOptions = []
        return
      }
      if (changed && !this.isPincodeLoading) {
        this.searchPincode(true)
      }
    },
    searchPincode (loadLocalBodies = true) {
      if (this.form.contact_pincode.length !== 6) return

      this.isPincodeLoading = true
      axios.get(`${this.$apiAdress}/api/pincodes/${this.form.contact_pincode}`, { timeout: 15000 })
        .then((response) => {
          const results = Array.isArray(response.data) ? response.data : []
          this.postOfficeRecords = results
          this.postOfficeOptions = [...new Set(results.map((row) => row.OfficeName).filter(Boolean))]

          if (this.postOfficeOptions.length > 0 && !this.postOfficeOptions.includes(this.form.contact_postofficename)) {
            this.form.contact_postofficename = this.postOfficeOptions[0]
          }

          this.applyPostOfficeDetails()

          if (loadLocalBodies) {
            this.loadDistrictLocalBodies()
          }

          this.loadChargeEstimate()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Pincode lookup failed.'
          this.postOfficeRecords = []
          this.postOfficeOptions = []
        })
        .finally(() => {
          this.isPincodeLoading = false
        })
    },
    onPostOfficeSelect (value) {
      this.form.contact_postofficename = value
      this.applyPostOfficeDetails()
      this.loadDistrictLocalBodies()
      this.loadChargeEstimate()
    },
    applyPostOfficeDetails () {
      if (!this.postOfficeRecords.length) return
      const selected = this.postOfficeRecords.find((row) => row.OfficeName === this.form.contact_postofficename) || this.postOfficeRecords[0]
      this.form.contact_postofficename = selected?.OfficeName || ''
      this.form.contact_pincode = String(selected?.Pincode || this.form.contact_pincode || '')
      this.form.contact_city = selected?.District || this.form.contact_city
      this.form.contact_state = selected?.StateName || this.form.contact_state
      this.calculateTotals()
    },
    loadDistrictLocalBodies () {
      const district = String(this.form.contact_city || '').trim()
      if (!district) return
      axios.get(`${this.$apiAdress}/api/address/local_bodies/${encodeURIComponent(district)}`, { timeout: 15000 })
        .then((response) => {
          const names = Array.isArray(response.data) ? response.data.filter(Boolean) : []
          this.localBodyOptions = [...new Set(names)].map((name) => ({
            local_body_name: name,
            local_body_type_name: this.form.local_body_type_name || ''
          }))
          if (this.form.local_body_name && !names.includes(this.form.local_body_name)) {
            this.form.local_body_name = ''
            this.selectedLocalBodyName = ''
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    onLocalBodySelect (value) {
      this.selectedLocalBodyName = value
      const selected = this.localBodyOptions.find((row) => row.local_body_name === value)
      if (!selected) {
        this.form.local_body_name = ''
        this.form.local_body_type_name = ''
        return
      }
      this.form.local_body_name = selected.local_body_name
      this.form.local_body_type_name = selected.local_body_type_name || ''
    },
    onCallTypeChange (value) {
      this.form.call_type = value
      // The Call Status vocabulary swaps with the call type (feedback calls
      // use the "SL …" statuses, others use the rest). Clear a now-hidden
      // selection so the form can't submit a status that no longer belongs.
      if (this.form.call_status && !this.visibleCallStatusOptions.some((opt) => opt.value === this.form.call_status)) {
        this.form.call_status = ''
      }
    },
    onCallStatusChange (value) {
      this.form.call_status = value
      // Followup Grade is only relevant for "Call Again - High Priority".
      // Drop a stale grade when the status moves to anything else so it's
      // never submitted alongside a non-high-priority call.
      if (value !== 'call_again_high') {
        this.form.followup_grade = ''
      }
    },
    onPaymentMethodChange (value) {
      this.form.payment_method = value
      this.loadChargeEstimate()
    },
    onShippingMethodChange (value) {
      this.form.shipping_method = value
      this.loadChargeEstimate()
    },
    addItem () {
      this.form.order_items.push({ item_id: '', name: '', sku: '', shortcode: '', quantity: 1, price: 0, total: 0 })
    },
    normalizeCropList (crops) {
      if (!Array.isArray(crops)) return []
      // API rows come from ContactHasCrops with the `crop` relation
      // eager-loaded as an object ({id, name, …}). `area` is stored
      // normalised to m²; reverse-convert it back to the persisted unit
      // so the table shows the area in the unit the customer entered.
      // Rows from before the `unit` column existed have unit === null
      // and stay displayed in m² (their canonical form).
      const toUnit = { cent: 40.46856, acre: 4046.856, hectare: 10000, metersquare: 1 }
      return crops.map((crop) => {
        const cropName = (crop.crop && typeof crop.crop === 'object')
          ? (crop.crop.name || '')
          : (crop.crop || crop.name || crop.crop_name || '')
        const unit = crop.unit || 'metersquare'
        const divisor = toUnit[unit] || 1
        return {
          crop: cropName,
          area: Number(crop.area || 0) / divisor,
          unit,
          count: Number(crop.count || 0)
        }
      }).filter((crop) => crop.crop)
    },
    applyPatientDetails (patient) {
      // org-2 only — searchCropsByPhone returns Patient with sessions
      // eager-loaded (user:id,name). Demographics live on `patient`;
      // every prior session becomes a row in the Case History
      // accordion so the next caller can see what was discussed and
      // by whom. The three medical input fields stay blank — each
      // session represents one distinct case, so the next staff
      // records fresh notes rather than editing the previous case
      // forward.
      //
      // Demographic fields still follow the fill-don't-clear
      // convention searchContact uses for name / address / pincode.
      // org-4 (Vet) reuses the same Case History list but has no
      // demographics section, so only the sessions are hydrated there.
      if (!this.showMedicalSection && !this.showVetSection) return

      this.patientSessions = []
      this.openSessionIds = []
      this.showAllSessions = false

      if (!patient) return

      // Demographics are org-2-only — the backend already scopes the
      // returned sessions to the active org, but `patient` (age / sex /
      // marital_status) is shared across departments, so guard the
      // hydration to avoid leaking human demographics onto a vet form.
      if (this.showMedicalSection) {
        if (patient.age != null && Number(patient.age) > 0) {
          this.form.age = String(patient.age)
        }
        if (patient.sex) this.form.gender = patient.sex
        if (patient.marital_status) this.form.marital_status = patient.marital_status
      }

      const sessions = Array.isArray(patient.patient_session) ? patient.patient_session : []
      this.patientSessions = sessions.slice().sort((a, b) => {
        const ta = this.sessionTimestamp(a)
        const tb = this.sessionTimestamp(b)
        if (tb !== ta) return tb - ta
        return Number(b.id || 0) - Number(a.id || 0)
      })
      if (this.patientSessions.length) {
        this.openSessionIds = [this.patientSessions[0].id]
      }
    },
    sessionTimestamp (session) {
      if (!session) return 0
      const raw = session.created_at
      if (raw == null || raw === '') return 0
      // PatientSession casts created_at to 'timestamp' (unix seconds)
      // but the API may also serialize as ISO string depending on
      // accessor path. Handle both.
      if (typeof raw === 'number') return raw * 1000
      const numeric = Number(raw)
      if (Number.isFinite(numeric) && String(numeric) === String(raw).trim()) {
        return numeric * 1000
      }
      const parsed = new Date(raw).getTime()
      return Number.isFinite(parsed) ? parsed : 0
    },
    formatSessionDate (raw) {
      const ts = this.sessionTimestamp({ created_at: raw })
      if (!ts) return ''
      const d = new Date(ts)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    sessionAuthor (session) {
      return session && session.user && session.user.name ? session.user.name : ''
    },
    sessionPreview (session) {
      const text = (session && (session.presenting_complaints || session.medical_history || session.treatment_history)) || ''
      const clean = String(text).replace(/\s+/g, ' ').trim()
      if (!clean) return 'No notes recorded.'
      return clean.length > 90 ? `${clean.slice(0, 87)}…` : clean
    },
    isSessionOpen (id) {
      return this.openSessionIds.includes(id)
    },
    toggleSession (id) {
      const idx = this.openSessionIds.indexOf(id)
      if (idx === -1) this.openSessionIds.push(id)
      else this.openSessionIds.splice(idx, 1)
    },
    buildPatientsData () {
      // Used by org-2 (full medical notes) and org-4 (single Disease
      // Detail textarea, bound to presenting_symptoms — demographics and
      // attachments stay empty for vet).
      // Demographic columns on `patients` are nullable as of the
      // 2026-06-03 migration, so the medical notes always persist —
      // even when the agent skipped Age / Gender / Marital Status. The
      // earlier "return null unless all three demographics are filled"
      // guard silently dropped the medical block and was the source of
      // the "medical history not storing" data-loss bug.
      const age = Number(this.form.age || 0)
      const sex = String(this.form.gender || '').trim()
      const maritalStatus = String(this.form.marital_status || '').trim()
      const presenting = String(this.form.presenting_symptoms || '').trim()
      const medical = String(this.form.medical_history || '').trim()
      const treatment = String(this.form.treatment_history || '').trim()
      const attachments = Array.isArray(this.form.medical_attachments) ? this.form.medical_attachments : []

      const hasAnyValue = age > 0 || sex || maritalStatus || presenting || medical || treatment || attachments.length
      if (!hasAnyValue) {
        return null
      }

      return {
        age: age > 0 ? age : null,
        // `patients.dob` is also nullable now — only derive a DOB when
        // the agent actually captured an age. Built from local
        // components so IST submissions near midnight don't end up a
        // day behind via toISOString()'s UTC conversion.
        dob: age > 0 ? this.deriveDobFromAge(age) : null,
        sex: sex || null,
        marital_status: maritalStatus || null,
        presenting_symptoms: this.form.presenting_symptoms || '',
        medical_history: this.form.medical_history || '',
        treatment_history: this.form.treatment_history || '',
        // Uploaded report references (already pushed to R2). Backend
        // re-validates the shape before persisting on the session.
        attachments: attachments.map((a) => ({
          url: a.url,
          key: a.key,
          name: a.name,
          mime: a.mime,
          size: a.size
        }))
      }
    },
    deriveDobFromAge (age) {
      const today = new Date()
      const year = today.getFullYear() - Number(age || 0)
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    isPdfAttachment (file) {
      if (!file) return false
      if (file.mime === 'application/pdf') return true
      return /\.pdf($|\?)/i.test(String(file.url || file.name || ''))
    },
    sessionAttachments (session) {
      // patient_sessions.attachments is cast to array on the backend, so a
      // session arrives with either an array or null. Guard against the
      // odd legacy row that stored a JSON string.
      const raw = session && session.attachments
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }
      return []
    },
    async onMedicalReportsSelected (event) {
      const input = event.target
      const files = Array.from(input.files || [])
      // Clear the native input straight away so picking the same file
      // again still fires @change.
      input.value = ''
      if (!files.length) return

      this.medicalUploadError = ''
      for (const file of files) {
        await this.uploadMedicalReport(file)
      }
    },
    async uploadMedicalReport (file) {
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
      if (!allowed.includes(file.type)) {
        this.medicalUploadError = `${file.name}: unsupported type. Upload an image or PDF.`
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.medicalUploadError = `${file.name}: exceeds the 10MB limit.`
        return
      }

      this.uploadingMedicalReports += 1
      try {
        const fd = new FormData()
        fd.append('file', file)
        const { data } = await axios.post(
          `${this.$apiAdress}/api/medical-reports/upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        )
        this.form.medical_attachments.push({
          url: data.url,
          key: data.key,
          name: data.name || file.name,
          mime: data.mime || file.type,
          size: data.size || file.size
        })
      } catch (error) {
        const message = error?.response?.data?.error
        this.medicalUploadError = (typeof message === 'string' && message)
          ? `${file.name}: ${message}`
          : `${file.name}: upload failed. Please try again.`
      } finally {
        this.uploadingMedicalReports -= 1
      }
    },
    removeMedicalAttachment (index) {
      // If the gallery being previewed is the one we're editing and the
      // removed item is at/after the open slide, keep the modal coherent.
      this.form.medical_attachments.splice(index, 1)
      if (this.showPreview && this.previewGallery === this.form.medical_attachments) {
        if (!this.previewGallery.length) {
          this.closePreview()
        } else if (this.previewIndex >= this.previewGallery.length) {
          this.previewIndex = this.previewGallery.length - 1
        }
      }
    },
    async onPaymentScreenshotsSelected (event) {
      const input = event.target
      const files = Array.from(input.files || [])
      // Clear the native input straight away so picking the same file again
      // still fires @change.
      input.value = ''
      if (!files.length) return

      this.paymentScreenshotError = ''
      for (const file of files) {
        await this.uploadPaymentScreenshot(file)
      }
    },
    async uploadPaymentScreenshot (file) {
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
      if (!allowed.includes(file.type)) {
        this.paymentScreenshotError = `${file.name}: unsupported type. Upload an image or PDF.`
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.paymentScreenshotError = `${file.name}: exceeds the 10MB limit.`
        return
      }

      this.uploadingPaymentScreenshots += 1
      try {
        const fd = new FormData()
        fd.append('file', file)
        const { data } = await axios.post(
          `${this.$apiAdress}/api/payment-screenshots/upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        )
        this.form.payment_screenshots.push({
          url: data.url,
          key: data.key,
          name: data.name || file.name,
          mime: data.mime || file.type,
          size: data.size || file.size
        })
      } catch (error) {
        const message = error?.response?.data?.error
        this.paymentScreenshotError = (typeof message === 'string' && message)
          ? `${file.name}: ${message}`
          : `${file.name}: upload failed. Please try again.`
      } finally {
        this.uploadingPaymentScreenshots -= 1
      }
    },
    removePaymentScreenshot (index) {
      // If the gallery being previewed is the one we're editing and the
      // removed item is at/after the open slide, keep the modal coherent.
      this.form.payment_screenshots.splice(index, 1)
      if (this.showPreview && this.previewGallery === this.form.payment_screenshots) {
        if (!this.previewGallery.length) {
          this.closePreview()
        } else if (this.previewIndex >= this.previewGallery.length) {
          this.previewIndex = this.previewGallery.length - 1
        }
      }
    },
    openPreview (gallery, index) {
      this.previewGallery = gallery
      this.previewIndex = index
      this.showPreview = true
      window.addEventListener('keydown', this.onPreviewKey)
    },
    closePreview () {
      this.showPreview = false
      this.previewGallery = []
      this.previewIndex = 0
      window.removeEventListener('keydown', this.onPreviewKey)
    },
    stepPreview (delta) {
      const len = this.previewGallery.length
      if (!len) return
      this.previewIndex = (this.previewIndex + delta + len) % len
    },
    onPreviewKey (event) {
      if (event.key === 'Escape') this.closePreview()
      else if (event.key === 'ArrowLeft') this.stepPreview(-1)
      else if (event.key === 'ArrowRight') this.stepPreview(1)
    },
    addCropByName (name) {
      const cropName = String(name || '').trim()
      if (!cropName) return
      const exists = this.allCrops.some((crop) => String(crop.crop || '').toLowerCase() === cropName.toLowerCase())
      if (exists) return
      // area/unit/count are no longer captured for org-1 — defaults are
      // kept only so the crops_data payload shape stays unchanged.
      this.existingCrops.push({ crop: cropName, area: 0, unit: 'cent', count: 0 })
    },
    removeCrop (index) {
      const contactLen = this.contactCrops.length
      if (index < contactLen) {
        this.contactCrops.splice(index, 1)
      } else {
        this.existingCrops.splice(index - contactLen, 1)
      }
    },
    addAnimalByName (name) {
      const animalName = String(name || '').trim()
      if (!animalName) return
      const exists = this.allAnimals.some((animal) => String(animal || '').toLowerCase() === animalName.toLowerCase())
      if (exists) return
      this.existingAnimals.push(animalName)
    },
    removeAnimal (index) {
      const contactLen = this.contactAnimals.length
      if (index < contactLen) {
        this.contactAnimals.splice(index, 1)
      } else {
        this.existingAnimals.splice(index - contactLen, 1)
      }
    },
    addDiseaseByName (name) {
      const diseaseName = String(name || '').trim()
      if (!diseaseName) return
      const exists = this.allDiseases.some((disease) => String(disease || '').toLowerCase() === diseaseName.toLowerCase())
      if (exists) return
      this.existingDiseases.push(diseaseName)
    },
    removeDisease (index) {
      const contactLen = this.contactDiseases.length
      if (index < contactLen) {
        this.contactDiseases.splice(index, 1)
      } else {
        this.existingDiseases.splice(index - contactLen, 1)
      }
    },
    addSelectedProduct () {
      const selected = this.productDraftItem
      const quantity = Number(this.productDraft.quantity || 0)

      if (!selected || quantity <= 0) {
        return
      }

      const existing = this.form.order_items.find((item) => String(item.item_id) === String(selected.value))
      if (existing) {
        existing.quantity = Number(existing.quantity || 0) + quantity
        existing.total = Number(existing.quantity || 0) * Number(existing.price || 0)
      } else {
        this.form.order_items.push({
          item_id: String(selected.value),
          name: selected.label || '',
          sku: selected.sku || '',
          shortcode: selected.shortcode || '',
          image: selected.image || '',
          quantity,
          price: Number(selected.price || 0),
          total: Number(selected.price || 0) * quantity
        })
      }

      this.productDraft.quantity = 1
      this.calculateTotals()
      this.scheduleChargeEstimate()
    },
    removeItem (index) {
      this.form.order_items.splice(index, 1)
      this.calculateTotals()
      this.scheduleChargeEstimate()
    },
    updateItem (index, itemId) {
      const selected = this.itemOptions.find((option) => option.value === String(itemId))
      const item = this.form.order_items[index]
      item.item_id = String(itemId)
      item.name = selected?.label || ''
      item.sku = selected?.sku || ''
      item.shortcode = selected?.shortcode || ''
      item.image = selected?.image || ''
      item.price = Number(selected?.price || 0)
      this.calculateTotals()
      this.scheduleChargeEstimate()
    },
    updateItemQuantity (index, quantity) {
      const item = this.form.order_items[index]
      item.quantity = Number(quantity || 0)
      this.calculateTotals()
      this.scheduleChargeEstimate()
    },
    startTimer () {
      if (this.timerHandle) clearInterval(this.timerHandle)
      this.timerHandle = setInterval(() => {
        this.timerSeconds += 1
      }, 1000)
    },
    startCall () {
      this.callRunning = true
      this.startTimer()
    },
    endCall () {
      this.callRunning = false
      if (this.timerHandle) {
        clearInterval(this.timerHandle)
        this.timerHandle = null
      }
    },
    saveCallLog () {
      this.errorMessage = ''
      this.successMessage = ''

      // Instead of failing silently on a disabled button, surface exactly
      // which fields are missing / wrongly formatted in the checklist modal.
      if (this.callLogChecklist.some((item) => !item.ok)) {
        this.checklistMode = 'callLog'
        this.showChecklistModal = true
        return
      }

      // Everything checks out — ask the agent to confirm before we write.
      this.showCallLogConfirm = true
    },
    cancelCallLogConfirm () {
      if (this.isSavingLog) return
      this.showCallLogConfirm = false
    },
    confirmSaveCallLog () {
      this.errorMessage = ''
      this.successMessage = ''
      this.isSavingLog = true

      const payload = {
        contact_name: this.form.contact_name || '-',
        contact_phone: this.fullContactPhone,
        // Alternate phone is collected on the form for every call but
        // was previously only sent on Convert-to-Sale, so an
        // address-only call log never persisted it.
        contact_phone_2: this.fullAlternatePhone,
        contact_address: this.form.contact_address,
        contact_pincode: this.form.contact_pincode,
        contact_postofficename: this.form.contact_postofficename,
        contact_district: this.form.contact_city,
        contact_state: this.form.contact_state,
        local_body_name: this.form.local_body_name,
        local_body_type_name: this.form.local_body_type_name,
        ward_no: Number(this.form.ward_no || 0),
        duration: Number(this.timerSeconds || 0),
        call_type: this.form.call_type || 'fresh_lead',
        status: this.form.call_status,
        follow_up: true,
        next_call: this.form.next_call,
        comments: this.form.comments,
        followup_grade: this.form.followup_grade,
        message_type: this.form.message_type,
        message_product_id: this.form.message_product_id,
        customer_type: (this.showCropsSection || this.showVetSection) ? (this.form.customer_type || null) : undefined,
        animals_data: this.showVetSection ? this.allAnimals.slice() : undefined,
        diseases_data: this.activeOrganisationId === 2 ? this.allDiseases.slice() : undefined,
        crops_data: this.allCrops.map((crop) => ({
          crop: crop.crop,
          area: Number(crop.area || 0),
          unit: crop.unit || 'cent',
          count: Number(crop.count || 0)
        })),
        patients_data: (this.showMedicalSection || this.showVetSection) ? this.buildPatientsData() : undefined
      }

      axios.post(`${this.$apiAdress}/api/calls`, payload)
        .then((response) => {
          if (response?.data?.error) {
            this.errorMessage = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error)
            this.showCallLogConfirm = false
            return
          }
          // Snapshot the saved call BEFORE resetForm() wipes the form, so the
          // success modal can echo back what was recorded.
          const status = (this.callStatusOptions.find((o) => o.value === this.form.call_status) || {}).label
          const wasFeedbackCall = this.form.call_type === 'feedback_call'
          this.lastSavedCallLog = {
            message: response?.data?.message || '',
            contactName: this.form.contact_name || '—',
            phone: this.fullContactPhone || '',
            status: status || this.form.call_status || '',
            duration: this.formattedTimer,
            nextCall: this.form.next_call || ''
          }
          this.resetForm(true)
          this.showCallLogConfirm = false
          if (wasFeedbackCall) {
            this.showFeedbackSuccess = true
            this.$nextTick(() => this.$refs.applause && this.$refs.applause.fire())
          } else {
            this.showCallLogSuccess = true
          }
          // Bring the freshly-reset form back to the top so the next
          // call is logged from the start of the form.
          this.scrollPageToTop()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to save the call log right now.'
          this.showCallLogConfirm = false
        })
        .finally(() => {
          this.isSavingLog = false
        })
    },
    closeCallLogSuccess () {
      this.showCallLogSuccess = false
      this.lastSavedCallLog = null
    },
    closeFeedbackSuccess () {
      // The applause stream runs infinitely while the modal is open, so
      // it has to be stopped explicitly on close.
      if (this.$refs.applause) this.$refs.applause.stop()
      this.showFeedbackSuccess = false
      this.lastSavedCallLog = null
    },
    convertToSale () {
      this.errorMessage = ''
      this.successMessage = ''

      // One source of truth for what blocks the sale: the checklist. If
      // anything is incomplete, open the modal that spells out each
      // missing / mis-formatted field instead of bouncing the agent with
      // a single terse error.
      if (this.convertChecklist.some((item) => !item.ok)) {
        this.checklistMode = 'convert'
        this.showChecklistModal = true
        return
      }

      // A unit heavier than the whole parcel cap can't be packed at all, so
      // no split rescues it — the method has to change. Say that here rather
      // than letting the server refuse it after the agent has hung up.
      if (this.unshippableParcelItems.length) {
        const names = this.unshippableParcelItems.map((line) => line.name || line.sku).join(', ')
        this.errorMessage = `A single unit of ${names} is over the ${this.parcelLimitLabel} parcel limit, `
          + `so this order can't go by ${this.formatLabel(this.form.shipping_method)}. Choose another shipping method.`
        return
      }

      // Over the cap: the sale has to ship as several parcels, which means
      // several orders. Show what that will look like — and what it costs —
      // before creating anything.
      if (this.needsParcelSplit) {
        this.openSplitPreview()
        return
      }

      this.showConvertConfirm = true
    },
    closeChecklist () {
      this.showChecklistModal = false
      // If the only blocker was the auto-calculated ₹0 shipping (missing
      // pincode / post office), drop the agent straight onto that field.
      if (this.checklistMode === 'convert' && this.shippingZeroReason) {
        this.focusContactPincode()
      }
    },
    // Close the checklist and drop the agent straight onto the offending
    // field — scroll it into view, focus its control, and pulse a ring so
    // it's unmistakable which input still needs attention.
    locateChecklistField (item) {
      this.showChecklistModal = false
      if (!item || !item.target) return
      // The pincode field has its own dedicated pulse mechanism.
      if (item.target === '[data-checklist="pincode"]') {
        this.focusContactPincode()
        return
      }
      this.$nextTick(() => {
        const el = this.$el.querySelector(item.target)
        if (!el) return
        if (el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        const control = el.querySelector('input:not([disabled]), textarea:not([disabled]), .app-dropdown__trigger')
        if (control && control.focus) {
          // Defer focus so it doesn't fight the smooth scroll on some browsers.
          setTimeout(() => control.focus({ preventScroll: true }), 320)
        }
        el.classList.add('tele-locate-pulse')
        setTimeout(() => el.classList.remove('tele-locate-pulse'), 2400)
      })
    },
    focusContactPincode () {
      this.pincodeAttentionPulse = false
      this.$nextTick(() => {
        const input = this.$refs.contactPincodeInput
        const el = input?.$el || input
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        if (input?.focus) input.focus()
        this.pincodeAttentionPulse = true
        setTimeout(() => { this.pincodeAttentionPulse = false }, 2500)
      })
    },
    scrollToMedicalDemographics () {
      this.$nextTick(() => {
        const anchor = this.$refs.demographicsAnchor
        const el = anchor?.$el || anchor
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    // Return the form to the very top. Used after saving a call log or
    // converting a sale (so the freshly-reset form starts at the top), and
    // on mount/activation so arriving from the Call Queue doesn't inherit
    // the previous page's scroll position.
    scrollPageToTop (smooth = true) {
      this.$nextTick(() => {
        if (this.$refs.pageTop && this.$refs.pageTop.scrollIntoView) {
          this.$refs.pageTop.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
        }
      })
    },
    // ── Parcel splitting ────────────────────────────────────────────────
    // Caps come from the server so raising them is a config change, not a
    // redeploy of the form. On failure we keep the bundled defaults rather
    // than treating the cart as uncapped — silently letting an oversized
    // order through is the outcome this whole feature exists to prevent.
    loadParcelLimits () {
      return axios.get(`${this.$apiAdress}/api/shipping/parcel-limits`, { timeout: 10000 })
        .then((response) => {
          const limits = response?.data?.limits
          if (limits && typeof limits === 'object') this.parcelLimitsMap = limits
        })
        .catch(() => {})
    },
    // Cut the cart and hand each parcel its share of the WHOLE cart's
    // discount, so a 150-bottle order keeps the 100+ tier that neither
    // parcel clears alone. Shipping starts unquoted — each parcel is a
    // separate booking and has to be priced on its own weight.
    // Value-proportional split of a manual discount, remainder onto the last
    // parcel so the shares total exactly what was keyed.
    apportionDiscountByValue (discount, parcels) {
      const cartTotal = parcels.reduce((sum, p) => sum + Number(p.total || 0), 0)
      if (!(discount > 0) || cartTotal <= 0) return parcels.map(() => 0)

      const shares = parcels.map((p) => Number(((discount * Number(p.total || 0)) / cartTotal).toFixed(2)))
      const drift = Number((discount - shares.reduce((a, b) => a + b, 0)).toFixed(2))
      if (drift !== 0) shares[shares.length - 1] = Number((shares[shares.length - 1] + drift).toFixed(2))
      return shares
    },
    buildSplitParcels () {
      const parcels = splitCartIntoParcels(this.activeParcelLimits, this.parcelCartLines)

      const wholeLines = this.parcelCartLines.map((line) => ({
        item_id: line.item_id,
        quantity: line.quantity,
        subtotal: Number((line.quantity * line.price).toFixed(2))
      }))

      // Mirrors the server's rule (orderController::storeSplit). A preset
      // discount divides by what each parcel's units earned; a manual
      // override (privileged roles only) has no ladder behind it and divides
      // by parcel value instead. Either way the shares sum to the cart's
      // discount, so the preview totals match the orders that get created.
      const discountAmt = Number(this.form.discount || 0)
      const breakdown = lineDiscountBreakdown(this.activeDiscountTiers, wholeLines)
      const presetTotal = breakdown.reduce((sum, b) => sum + Number(b.amount || 0), 0)
      const matchesPreset = discountAmt > 0 && Math.abs(presetTotal - discountAmt) <= 0.5

      const shares = matchesPreset
        ? apportionDiscount(parcels, wholeLines, breakdown, discountAmt)
        : this.apportionDiscountByValue(discountAmt, parcels)

      return parcels.map((parcel, i) => ({
        ...parcel,
        discount: Number(shares[i] || 0),
        shippingCharge: 0,
        codCharge: 0,
        shippingPending: true,
        grandTotal: Number((parcel.total - Number(shares[i] || 0)).toFixed(2))
      }))
    },
    openSplitPreview () {
      this.splitError = ''
      this.splitParcels = this.buildSplitParcels()
      this.showSplitPreview = true
      this.quoteSplitParcels()
    },
    // One quote per parcel, priced on that parcel's own weight and quantity —
    // the tariff is per consignment, so the cart's single-parcel quote is not
    // divisible and has to be thrown away.
    quoteSplitParcels () {
      if (!this.splitParcels.length) return Promise.resolve()
      this.isQuotingParcels = true

      return Promise.all(this.splitParcels.map((parcel) => axios.post(`${this.$apiAdress}/api/cal_cod_charges`, {
        organizationId: this.activeOrganisationId,
        pincode: this.form.contact_pincode,
        quantity: parcel.units,
        total: Number((parcel.total - parcel.discount).toFixed(2)),
        qtyAYS: 0,
        total_weight_grams: parcel.weightGrams,
        paymentMethod: this.form.payment_method,
        shippingMethod: this.form.shipping_method
      }).then((response) => ({
        shippingCharge: Number(response.data?.base_shipping ?? response.data?.cod_charge ?? 0),
        codCharge: Number(response.data?.cod_fee || 0)
      }))))
        .then((quotes) => {
          this.splitParcels = this.splitParcels.map((parcel, i) => {
            const charge = Number(quotes[i].shippingCharge || 0)
            const cod = Number(quotes[i].codCharge || 0)
            return {
              ...parcel,
              shippingCharge: charge,
              codCharge: cod,
              shippingPending: false,
              grandTotal: Number((parcel.total - parcel.discount + charge + cod).toFixed(2))
            }
          })
        })
        .catch(() => {
          // No usable quote means no defensible price for the parcels, so the
          // confirm button stays disabled rather than creating orders that
          // undercharge shipping.
          this.splitError = 'Shipping could not be quoted for these parcels. Check the pincode and try again.'
          this.splitParcels = this.splitParcels.map((parcel) => ({ ...parcel, shippingPending: true }))
        })
        .finally(() => {
          this.isQuotingParcels = false
        })
    },
    cancelSplitPreview () {
      if (this.isSubmitting) return
      this.showSplitPreview = false
      this.splitError = ''
    },
    cancelConvertConfirm () {
      if (this.isSubmitting) return
      this.showConvertConfirm = false
    },
    // Validates, flips the form into its submitting state and returns the
    // order payload — or null when the sale must not go out. Shared by the
    // ordinary single-order path and the split path, which post the SAME
    // payload to different endpoints (the split endpoint additionally takes
    // one shipping quote per parcel and cuts the cart itself).
    beginSaleSubmission () {
      // Defence-in-depth: convertToSale already validates, but if a caller
      // bypasses it (devtools, future programmatic trigger), bail out
      // instead of POSTing a telesale with ₹0 shipping.
      if (this.shippingZeroReason) {
        this.errorMessage = `Cannot convert to sale — ${this.shippingZeroMessage}`
        this.showConvertConfirm = false
        this.focusContactPincode()
        return null
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.isSubmitting = true

      const orderTypeMap = {
        tele: 'telesale',
        direct: 'direct_sale',
        ecom: 'ecommerce',
        agency: 'agency',
        store: 'store_sale',
        amazon: 'amazon'
      }

      const selectedOrderType = orderTypeMap[this.form.order_type] || 'telesale'
      // COD waits on delivery collection; approval-queue methods (bank/UPI
      // for everyone, gateway/marketplace/VPP for telesales-role users)
      // wait on an accountant confirming the credit (Inbox → Payment
      // Approvals). Everything else is paid on entry.
      const needsApproval = this.form.payment_method === 'cod'
        || this.paymentMethodNeedsApproval
      const paymentStatus = needsApproval ? 'payment_pending' : 'paid'

      const payload = {
        contact_id: this.form.contact_id || undefined,
        contact_name: this.form.contact_name,
        contact_phone: this.fullContactPhone,
        // Falls back to the primary if the biller didn't capture an
        // alternate — the order payload has always required a second
        // number, so we keep that contract intact.
        contact_phone_2: this.fullAlternatePhone || this.fullContactPhone,
        contact_address: this.form.contact_address,
        contact_pincode: this.form.contact_pincode,
        contact_postofficename: this.form.contact_postofficename,
        contact_district: this.form.contact_city,
        contact_state: this.form.contact_state || 'Kerala',
        // Only sent when staff ticked "Include GST IN" — a non-empty value is
        // what makes orderController::store issue a B2B invoice number.
        contact_gstn: this.includeGstin ? this.form.contact_gstn : null,
        local_body_name: this.form.local_body_name,
        local_body_type_name: this.form.local_body_type_name,
        ward_no: Number(this.form.ward_no || 0),
        // Billing address — only included when the agent toggled it
        // on. The backend treats the billing block as optional;
        // sending empty strings when disabled keeps payloads clean.
        billing_name: this.addBillingAddress ? this.form.billing_name : '',
        billing_address: this.addBillingAddress ? this.form.billing_address : '',
        billing_pincode: this.addBillingAddress ? this.form.billing_pincode : '',
        billing_postofficename: this.addBillingAddress ? this.form.billing_postofficename : '',
        billing_district: this.addBillingAddress ? this.form.billing_district : '',
        billing_state: this.addBillingAddress ? this.form.billing_state : '',
        country: this.countryCode,
        shipping_type: 'domestic',
        order_type: selectedOrderType,
        // Total bottles = sum of line quantities, not the number of product
        // lines — feeds the "Bottles Sold" tiles, leaderboard and exports.
        item_count: this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        weight: 0,
        total: this.form.total,
        discount: this.form.discount,
        total_after_discount: this.totalAfterDiscount,
        shipping_charge: this.form.shipping_charge,
        cod_charge: this.form.cod_fee,
        total_shipping_charge: Number(this.form.shipping_charge || 0) + Number(this.form.cod_fee || 0),
        grand_total: this.form.grand_total,
        gst_percentage: this.form.gst_percentage,
        cgst_amt: this.form.cgst_amt,
        sgst_amt: this.form.sgst_amt,
        igst_amt: this.form.igst_amt,
        gst_amt: this.form.gst_amt,
        gst_state_code: this.form.gst_state_code,
        gst_stin: '32',
        payment_method: this.form.payment_method,
        payment_status: paymentStatus,
        payment_date: this.form.payment_date || null,
        // Bank/UPI transfer proof — only meaningful for approval-queue methods.
        // Strip Vue reactivity to a plain array of { url, key, name, mime, size }.
        payment_screenshots: this.showPaymentScreenshotUpload
          ? this.form.payment_screenshots.map((f) => ({ url: f.url, key: f.key, name: f.name, mime: f.mime, size: f.size }))
          : [],
        // Written fallback when no screenshot was sent (UPI ref / "paid at
        // counter" / etc.). Only meaningful for approval-queue methods.
        payment_proof_note: this.showPaymentScreenshotUpload ? (this.form.payment_proof_note.trim() || null) : null,
        order_status: this.form.order_status || 'confirmed',
        shipping_method: this.form.shipping_method,
        shipping_status: 'for_print',
        order_items: this.form.order_items,
        is_call: 'yes',
        duration: this.timerSeconds,
        call_type: this.form.call_type || 'fresh_lead',
        status: this.form.call_status || 'closed',
        follow_up: true,
        next_call: this.form.next_call,
        comments: this.form.comments,
        customer_type: (this.showCropsSection || this.showVetSection) ? (this.form.customer_type || null) : undefined,
        animals_data: this.showVetSection ? this.allAnimals.slice() : undefined,
        diseases_data: this.activeOrganisationId === 2 ? this.allDiseases.slice() : undefined,
        crops_data: this.allCrops.map((crop) => ({
          crop: crop.crop,
          area: Number(crop.area || 0),
          unit: crop.unit || 'cent',
          count: Number(crop.count || 0)
        })),
        message_type: this.form.message_type,
        message_product_id: this.form.message_product_id,
        followup_grade: this.form.followup_grade,
        patients_data: (this.showMedicalSection || this.showVetSection) ? this.buildPatientsData() : undefined
      }

      return payload
    },
    confirmConvertToSale () {
      const payload = this.beginSaleSubmission()
      if (!payload) return
      this.postSale(`${this.$apiAdress}/api/orders`, payload)
    },
    // The oversized-cart path. Same payload, plus the per-parcel shipping the
    // agent just approved in the preview; the server re-cuts the cart itself
    // and refuses the submission if its own split disagrees with what was
    // previewed, so a stale preview can never mis-price a parcel.
    confirmSplitSale () {
      const payload = this.beginSaleSubmission()
      if (!payload) return
      payload.parcel_shipping = this.splitParcels.map((parcel) => ({
        shipping_charge: Number(parcel.shippingCharge || 0),
        cod_charge: Number(parcel.codCharge || 0)
      }))
      this.postSale(`${this.$apiAdress}/api/orders/split`, payload, this.splitParcels)
    },
    // One success/failure path for both endpoints — the split response
    // deliberately mirrors the single-order one (OrderId / order_number of
    // the first parcel), so only the snapshot needs to know the difference.
    postSale (url, payload, parcels) {
      axios.post(url, payload)
        .then((response) => {
          if (response?.data?.error) {
            const message = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error)
            // Keep the split preview open on failure: its numbers are the
            // context for the error, and closing it would drop the agent
            // back on a form that looks fine.
            if (parcels) this.splitError = message
            else this.errorMessage = message
            return
          }

          this.lastSaved = this.buildSaleSnapshot(
            response?.data?.OrderId,
            response?.data?.order_number,
            parcels ? (response?.data?.orders || []) : null
          )
          this.lastSavedRequiresApproval = Boolean(response?.data?.requires_approval)
          if (!this.lastSavedRequiresApproval) {
            // Bump the agent's running tally first so the modal can
            // greet them with "your Nth sale!". Pick the quote from
            // the same index so the banner is internally consistent.
            // A split cart is ONE sale however many parcels it ships in,
            // so it bumps the tally once.
            this.userSaleCount = this.bumpStoredSaleCount()
            this.saleQuote = this.pickSaleQuote(this.userSaleCount)
          } else {
            this.saleQuote = ''
          }
          this.showConvertConfirm = false
          this.showSplitPreview = false
          this.splitError = ''
          this.showSuccessModal = true
          this.$nextTick(() => this.$refs.confetti && this.$refs.confetti.fire())
          // Full reset (incl. contact + history). The success modal
          // reads from `lastSaved` (a snapshot taken above), so wiping
          // the live form doesn't affect what's shown — and staff are
          // immediately ready for the next customer.
          this.resetForm(true)
          // Bring the freshly-reset form back to the top so the next
          // customer is entered from the start of the form.
          this.scrollPageToTop()
        })
        .catch((error) => {
          console.log(error)
          const message = error?.response?.data?.message || 'Unable to create order right now.'
          if (parcels) this.splitError = message
          else this.errorMessage = message
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    // `splitOrders` is the server's per-parcel result (null for an ordinary
    // sale). When present the sale's real figures are the GROUP's, not the
    // form's — the form only ever held one parcel's shipping — so the totals
    // are re-derived from the parcels the agent confirmed.
    buildSaleSnapshot (orderId, orderNumber, splitOrders) {
      const parcels = Array.isArray(splitOrders) && splitOrders.length ? splitOrders : null
      const splitTotals = parcels
        ? {
          grandTotal: Number(this.splitParcels.reduce((sum, p) => sum + Number(p.grandTotal || 0), 0).toFixed(2)),
          shipping: Number(this.splitParcels.reduce((sum, p) => sum + Number(p.shippingCharge || 0), 0).toFixed(2)),
          codFee: Number(this.splitParcels.reduce((sum, p) => sum + Number(p.codCharge || 0), 0).toFixed(2))
        }
        : null

      return {
        orderId: orderId || null,
        // Every order number in the group, so the success modal and the
        // accounts hand-off name all the parcels rather than just the first.
        splitOrderNumbers: parcels ? parcels.map((o) => o.order_number).filter(Boolean) : [],
        splitOrderIds: parcels ? parcels.map((o) => o.OrderId).filter(Boolean) : [],
        parcelCount: parcels ? parcels.length : 1,
        // Business order number (MAP-/MVP-/MDI-…) freshly issued by the
        // backend. Distinct from the DB `orderId`, which the invoice URL
        // still needs. Falls back to `#<id>` only if the API didn't return
        // a number for some reason.
        orderNumber: orderNumber || (orderId ? `#${orderId}` : ''),
        contactName: this.form.contact_name,
        contactPhone: this.form.contact_phone,
        dialCode: this.dialCode,
        itemCount: this.form.order_items.length,
        grandTotal: splitTotals ? splitTotals.grandTotal : this.form.grand_total,
        items: this.form.order_items.map((item) => ({
          name: item.name,
          quantity: Number(item.quantity || 0),
          total: Number(item.total || 0)
        })),
        discount: Number(this.form.discount || 0),
        shipping: splitTotals ? splitTotals.shipping : Number(this.form.shipping_charge || 0),
        codFee: splitTotals ? splitTotals.codFee : Number(this.form.cod_fee || 0),
        gst: Number(this.form.gst_amt || 0),
        paymentMethod: this.form.payment_method,
        // Pre-build the accounts-team WhatsApp text now — the live form is
        // wiped by resetForm() right after the snapshot, so the success
        // modal must read from here. Includes the freshly-issued order
        // number so accounts can tie the message to the order.
        accountsMessage: this.buildWhatsappShareMessage(
          orderNumber || (orderId ? `#${orderId}` : ''),
          parcels
            ? {
              orderNumbers: parcels.map((o) => o.order_number).filter(Boolean),
              grandTotal: splitTotals.grandTotal,
              shipping: splitTotals.shipping + splitTotals.codFee
            }
            : null
        )
      }
    },
    closeSuccessModal () {
      if (this.$refs.confetti) this.$refs.confetti.stop()
      this.showSuccessModal = false
      this.lastSaved = null
      this.lastSavedRequiresApproval = false
      this.saleQuote = ''
    },
    ordinal (n) {
      const num = Number(n) || 0
      const lastTwo = num % 100
      if (lastTwo >= 11 && lastTwo <= 13) return `${num}th`
      const lastOne = num % 10
      const suffix = lastOne === 1 ? 'st' : lastOne === 2 ? 'nd' : lastOne === 3 ? 'rd' : 'th'
      return `${num}${suffix}`
    },
    saleCountStorageKey () {
      let userId = 'anon'
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        if (user && (user.id || user.user_id)) userId = String(user.id || user.user_id)
      } catch (e) { /* localStorage corrupt — fall back to anon */ }
      return `${SALE_COUNT_STORAGE_PREFIX}${userId}`
    },
    readStoredSaleCount () {
      try {
        const raw = localStorage.getItem(this.saleCountStorageKey())
        const num = raw ? parseInt(raw, 10) : 0
        return Number.isFinite(num) && num > 0 ? num : 0
      } catch (e) {
        return 0
      }
    },
    bumpStoredSaleCount () {
      const next = this.readStoredSaleCount() + 1
      try {
        localStorage.setItem(this.saleCountStorageKey(), String(next))
      } catch (e) { /* storage full / blocked — UI still works with in-memory value */ }
      return next
    },
    pickSaleQuote (count) {
      if (!SALE_QUOTES.length) return ''
      const idx = ((Number(count) || 1) - 1) % SALE_QUOTES.length
      return SALE_QUOTES[idx]
    },
    invoiceUrl () {
      if (!this.lastSaved?.orderId) return ''
      // A split sale has an invoice per parcel; the endpoint already takes a
      // comma-separated list, so all of them print in one go.
      const ids = this.lastSaved.splitOrderIds && this.lastSaved.splitOrderIds.length
        ? this.lastSaved.splitOrderIds.join(',')
        : this.lastSaved.orderId
      return `${this.$apiAdress}/invoices?orders=${ids}`
    },
    downloadInvoice () {
      const url = this.invoiceUrl()
      if (!url) return
      window.open(`${url}&autoprint=1`, '_blank', 'noopener')
    },
    buildSaleWhatsappMessage () {
      const snap = this.lastSaved
      if (!snap) return ''
      const customer = snap.contactName || 'Customer'
      const lines = [`Hello ${customer},`, '', 'Thank you for your order. Here are the details:']
      snap.items.forEach((item) => {
        lines.push(`${item.quantity} × ${item.name} — ${this.formatCurrency(item.total)}`)
      })
      if (snap.discount > 0) lines.push(`Discount: −${this.formatCurrency(snap.discount)}`)
      if (snap.shipping > 0) lines.push(`Shipping: ${this.formatCurrency(snap.shipping)}`)
      if (snap.codFee > 0) lines.push(`COD Fee: ${this.formatCurrency(snap.codFee)}`)
      if (snap.gst > 0) lines.push(`GST: ${this.formatCurrency(snap.gst)}`)
      lines.push(`*Total: ${this.formatCurrency(snap.grandTotal)}*`)
      if (snap.paymentMethod) lines.push(`Payment: ${this.formatLabel(snap.paymentMethod)}`)
      // No invoice link: staff use "Download Invoice" and attach the PDF to
      // the WhatsApp chat, so a public /invoices URL in the body is redundant.
      return lines.join('\n')
    },
    sendInvoiceWhatsapp () {
      const snap = this.lastSaved
      if (!snap?.contactPhone) return
      const phone = String(snap.contactPhone).replace(/\D/g, '')
      const dial = String(snap.dialCode || '91').replace(/\D/g, '')
      const message = this.buildSaleWhatsappMessage()
      const waUrl = `https://wa.me/${dial}${phone}?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    // Short text caption that rides alongside the estimate image. On mobile it
    // becomes the share-sheet message; on desktop it's the wa.me prefill so the
    // chat opens with context while the agent attaches the downloaded PNG.
    buildEstimateCaption () {
      const customer = this.estimateCustomerName || 'there'
      const lines = [
        `Hello ${customer},`,
        '',
        'Please find your estimate attached.',
        `Total: ${this.formatCurrency(this.form.grand_total)}`
      ]
      return lines.join('\n')
    },
    // Renders the off-screen estimate card to a PNG Blob via html2canvas.
    async generateEstimateImage () {
      const node = this.$refs.estimateCard
      if (!node) return null
      // Lazy-load so the ~200KB lib only ships when an agent actually sends an
      // estimate — code-split out of the initial TeleSales bundle.
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(node, {
        backgroundColor: '#ffffff',
        scale: 2, // 2× for a crisp image on retina / when zoomed in WhatsApp
        useCORS: true,
        logging: false
      })
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95)
      })
    },
    // Step 1: snapshot the estimate and pop a preview modal. We deliberately
    // DON'T download / share here — those must run inside a direct click
    // (user-gesture), and the await for html2canvas above would have already
    // burned the gesture from this button, so the browser silently blocks
    // window.open / navigator.share. The modal's own buttons are fresh
    // gestures, so Download and Send work reliably.
    async openEstimatePreview () {
      if (!this.hasEstimateItems || this.isGeneratingEstimate) return
      this.errorMessage = ''
      this.successMessage = ''
      this.isGeneratingEstimate = true
      try {
        // Wait a tick so the off-screen card reflects the latest cart/totals
        // before snapshotting.
        await this.$nextTick()
        const blob = await this.generateEstimateImage()
        if (!blob) {
          this.errorMessage = 'Could not generate the estimate image. Please try again.'
          return
        }
        if (this.estimatePreviewUrl) URL.revokeObjectURL(this.estimatePreviewUrl)
        const slug = (this.estimateCustomerName || 'customer').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'customer'
        this.estimateBlob = blob
        this.estimateFileName = `Estimate-${slug}.png`
        this.estimatePreviewUrl = URL.createObjectURL(blob)
        this.showEstimateModal = true
      } catch (err) {
        this.errorMessage = 'Could not prepare the estimate. Please try again.'
      } finally {
        this.isGeneratingEstimate = false
      }
    },
    // Step 2a: save the PNG. Called from a modal button = real user gesture, so
    // the browser honours the download (Save-As if the user has "ask where to
    // save" on, otherwise straight to Downloads).
    downloadEstimateImage () {
      if (!this.estimateBlob || !this.estimatePreviewUrl) return
      const link = document.createElement('a')
      link.href = this.estimatePreviewUrl
      link.download = this.estimateFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.successMessage = 'Estimate image saved to your downloads.'
    },
    // Step 2b: share. On mobile (and desktops that support file sharing) the
    // native share sheet opens straight to WhatsApp with the image attached.
    // Elsewhere we download the PNG and open wa.me with the caption so the agent
    // drags the saved image in — wa.me links can't carry a file themselves.
    async shareEstimateWhatsapp () {
      if (!this.estimateBlob) return
      const file = new File([this.estimateBlob], this.estimateFileName, { type: 'image/png' })
      const caption = this.buildEstimateCaption()

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: caption, title: 'Estimate' })
          this.successMessage = 'Estimate shared. Pick WhatsApp to send it.'
          this.closeEstimateModal()
          return
        } catch (shareErr) {
          if (shareErr && shareErr.name === 'AbortError') return
          // Other failures fall through to the download + wa.me path below.
        }
      }

      this.downloadEstimateImage()
      const phone = String(this.form.contact_phone || '').replace(/\D/g, '')
      const dial = String(this.dialCode || '91').replace(/\D/g, '')
      const waUrl = phone
        ? `https://wa.me/${dial}${phone}?text=${encodeURIComponent(caption)}`
        : `https://wa.me/?text=${encodeURIComponent(caption)}`
      window.open(waUrl, '_blank', 'noopener')
      this.successMessage = 'Estimate downloaded and WhatsApp opened — attach the saved image to send it.'
    },
    closeEstimateModal () {
      this.showEstimateModal = false
      if (this.estimatePreviewUrl) {
        URL.revokeObjectURL(this.estimatePreviewUrl)
        this.estimatePreviewUrl = ''
      }
      this.estimateBlob = null
    },
    // `splitSale` is set only when the cart shipped as several parcels:
    // { orderNumbers, grandTotal, shipping }. The products, MRP and discount
    // are the cart's either way — the split doesn't change what was sold —
    // but the money the customer actually pays does, because shipping is
    // charged per parcel. Accounts must see the group's figure, not one
    // parcel's, or the message won't reconcile against the invoices.
    buildWhatsappShareMessage (orderNumber = '', splitSale = null) {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      const date = `${dd}/${mm}/${yyyy}`

      // Logged-in user's display name — same source TheHeader uses for the
      // "Good evening, …" greeting, so the share copy reads consistently.
      let caller = ''
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        if (user && typeof user.name === 'string') {
          caller = user.name.trim().toUpperCase()
        }
      } catch (e) { /* localStorage corrupt — leave blank */ }

      const customerName = String(this.form.contact_name || '').trim().toUpperCase()
      const phone = this.form.contact_phone ? `+${this.dialCode}${this.form.contact_phone}` : ''
      const altPhone = this.form.contact_phone_2 ? `+${this.dialCode}${this.form.contact_phone_2}` : ''

      const addressParts = [
        this.form.contact_address,
        this.form.contact_postofficename,
        this.form.contact_city,
        this.form.contact_state
      ].map((part) => String(part || '').trim()).filter(Boolean)
      const pincode = this.form.contact_pincode ? String(this.form.contact_pincode).trim() : ''
      const address = (pincode ? [...addressParts, pincode] : addressParts).join(', ').toUpperCase()

      const totalQty = this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)

      // Resolve the discount each line actually earned so a mixed-discount
      // cart reads clearly (e.g. one product at 35%, another at 10%) instead
      // of collapsing to a single blended % that confuses the accounts team.
      // Only trust the split when its total reconciles with the discount
      // actually applied — otherwise the agent overrode the amount by hand
      // and a per-product breakdown would be fiction, so we tag nothing.
      const discountAmt = Number(this.form.discount || 0)
      const breakdown = lineDiscountBreakdown(
        this.activeDiscountTiers,
        this.form.order_items.map((l) => ({ item_id: l.item_id, quantity: l.quantity, subtotal: l.total }))
      )
      const breakdownTotal = breakdown.reduce((sum, b) => sum + Number(b.amount || 0), 0)
      // Within ₹1: per-line rounding can drift a few paise from form.discount.
      const breakdownMatches = discountAmt > 0 && Math.abs(breakdownTotal - discountAmt) <= 1
      const taggedPercents = breakdownMatches
        ? breakdown.filter((b) => Number(b.percent) > 0).map((b) => b.percent)
        : []
      const hasMixedDiscount = new Set(taggedPercents).size > 1

      const productNames = this.form.order_items
        .map((item, idx) => {
          const name = String(item.name || '').trim()
          if (!name) return ''
          const qty = Number(item.quantity || 0)
          const disc = breakdownMatches ? breakdown[idx] : null
          const tag = disc && Number(disc.percent) > 0 ? ` — ${disc.percent}% off` : ''
          // Line MRP (qty × rate), so the per-line figures sum to the MRP
          // total in the summary block below and accounts can reconcile.
          const lineMrp = Number(item.total || 0) || Number(item.price || 0) * qty
          const mrpTag = lineMrp > 0 ? ` — MRP ${Math.round(lineMrp)}` : ''
          return `${name} x ${qty}${tag}${mrpTag}`
        })
        .filter(Boolean)
        .join('\n')

      const paymentMethod = this.form.payment_method ? this.formatLabel(this.form.payment_method).toUpperCase() : ''
      const shippingMethod = this.form.shipping_method ? this.formatLabel(this.form.shipping_method).toUpperCase() : ''

      const lines = [
        `Date : ${date}`,
        `Tele caller : ${caller}`
      ]
      // After a sale is converted, surface the generated order number so
      // the accounts team can tie the WhatsApp message to the order.
      if (splitSale && splitSale.orderNumbers.length) {
        lines.push(`Order numbers : ${splitSale.orderNumbers.join(', ')}`)
        lines.push(`(one sale, shipped as ${splitSale.orderNumbers.length} parcels)`)
      } else if (orderNumber) {
        lines.push(`Order number : ${orderNumber}`)
      }
      lines.push(
        '',
        '',
        'Customer Name:',
        customerName,
        'Customer Phone:',
        phone,
        ...(altPhone ? ['Alternate Phone:', altPhone] : []),
        'Customer Address:',
        address,
        `Product quantity ${totalQty}`,
        '',
        'Product name :',
        productNames,
        ''
      )

      // Money block for the accounts team. Two totals, deliberately:
      //   Amount (Excl Shipping) = MRP − Discount  — the products figure
      //   Total Amount           = grand_total     — what the customer pays
      // MRP is the products subtotal (form.total), which never includes
      // shipping/COD; those enter only at grand_total. Splitting the two
      // lets accounts reconcile the product maths and still see the
      // collectible amount.
      // The % is derived from the resolved amount (form.discount) rather
      // than the raw input, so it's correct whether the agent entered ₹ or
      // %, or a preset was applied. When the cart carries mixed per-product
      // discounts (tagged inline on each line above), the % is flagged as
      // blended so nobody reads it as one flat rate applied to everything.
      const subtotal = Number(this.form.total || 0)
      if (subtotal > 0) {
        const discountPercent = discountAmt > 0
          ? Number(((discountAmt / subtotal) * 100).toFixed(2))
          : 0
        const netAmount = subtotal - discountAmt
        const grandTotal = splitSale
          ? Number(splitSale.grandTotal || 0)
          : (Number(this.form.grand_total || 0) || netAmount)
        // Derived from the two totals rather than read from shipping_charge
        // directly, so the block always closes — this figure is whatever
        // grand_total actually adds on top (shipping + any COD fee).
        const shippingCharge = grandTotal - netAmount
        lines.push(
          `MRP: ${Math.round(subtotal)}`,
          `Discount Amount: ${discountAmt > 0 ? '-' : ''}${Math.round(discountAmt)}`,
          `Discount %: ${discountPercent}${hasMixedDiscount ? ' (mixed)' : ''}`,
          `Amount (Excl Shipping): ${Math.round(netAmount)}`,
          `Shipping Charge: ${Math.round(shippingCharge)}`,
          `Total Amount: ${Math.round(grandTotal)}`,
          ''
        )
      }

      lines.push(
        `Payment method. ${paymentMethod}`,
        `Shipping method. ${shippingMethod}`
      )

      return lines.join('\n')
    },
    async copyAndShareWhatsapp () {
      if (!this.form.contact_phone) return

      const message = this.buildWhatsappShareMessage()
      this.errorMessage = ''
      this.successMessage = ''

      // Try the modern Clipboard API first. It can fail silently on
      // insecure contexts (http) or when permission is denied — fall
      // through to opening WhatsApp regardless so the share still
      // happens even if copy didn't.
      let copied = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(message)
          copied = true
        }
      } catch (e) {
        copied = false
      }

      this.successMessage = copied
        ? 'Details copied to clipboard. WhatsApp opened — pick a contact to send.'
        : 'WhatsApp opened — pick a contact to send.'

      // wa.me without a phone opens WhatsApp's share-target picker, so
      // the agent can forward the message to a colleague (or the
      // customer) without committing to a recipient up-front.
      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    async copyAccountsTeamMessage () {
      // Post-sale variant of copyAndShareWhatsapp: the form is already
      // reset, so it reads the pre-built message (with order number) from
      // the saved snapshot instead of this.form.
      const message = this.lastSaved && this.lastSaved.accountsMessage
      if (!message) return

      this.errorMessage = ''
      this.successMessage = ''

      let copied = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(message)
          copied = true
        }
      } catch (e) {
        copied = false
      }

      this.successMessage = copied
        ? 'Details copied to clipboard. WhatsApp opened — pick a contact to send.'
        : 'WhatsApp opened — pick a contact to send.'

      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    resetForm (resetContact = true) {
      this.endCall()
      this.timerSeconds = 0
      this.amountToPay = 0
      this.form.order_items = []
      this.productDraft.item_id = ''
      this.productDraft.quantity = 1
      this.existingCrops = []
      this.existingAnimals = []
      this.postOfficeRecords = []
      this.postOfficeOptions = []
      this.localBodyOptions = []
      this.selectedLocalBodyName = ''
      this.form.contact_state = ''
      this.form.local_body_name = ''
      this.form.local_body_type_name = ''
      this.form.ward_no = 0
      if (resetContact) {
        this.form.contact_name = ''
        this.form.contact_phone = ''
        this.form.contact_phone_2 = ''
        this.form.contact_address = ''
        this.form.contact_pincode = ''
        this.form.contact_postofficename = ''
        this.form.contact_city = ''
        // Demographics are per-customer attributes — they were
        // omitted from the reset, so after saving and clearing the
        // form the previous customer's age/gender/marital_status
        // stayed visible while every other field went blank. The
        // next save would then re-submit those stale values.
        this.form.age = ''
        this.form.gender = ''
        this.form.marital_status = ''
        this.form.customer_type = ''
        this.contactCalls = []
        this.contactOrders = []
        this.contactCrops = []
        this.contactAnimals = []
        this.existingAnimals = []
        this.contactDiseases = []
        this.existingDiseases = []
        this.contactHmsVisits = []
        this.patientSessions = []
        this.openSessionIds = []
        this.codBlocked = false
        this.contactGstinOnFile = ''
      }
      this.includeGstin = false
      this.form.contact_gstn = ''
      this.addBillingAddress = false
      this.form.billing_name = ''
      this.form.billing_address = ''
      this.form.billing_pincode = ''
      this.form.billing_postofficename = ''
      this.form.billing_district = ''
      this.form.billing_state = ''
      this.form.payment_method = ''
      this.form.payment_date = ''
      this.form.shipping_method = ''
      this.form.comments = ''
      this.form.call_type = ''
      this.form.call_status = ''
      this.form.followup_grade = ''
      this.form.next_call = new Date().toISOString().slice(0, 10)
      this.form.quantity = 1
      this.form.discount = 0
      this.form.shipping_charge = 0
      this.form.cod_fee = 0
      this.clearShippingRateContext()
      this.form.presenting_symptoms = ''
      this.form.medical_history = ''
      this.form.treatment_history = ''
      this.form.medical_attachments = []
      this.medicalUploadError = ''
      this.form.payment_screenshots = []
      this.form.payment_proof_note = ''
      this.paymentScreenshotError = ''
      this.closePreview()
      this.discountMode = 'amount'
      this.discountInput = 0
      this.applyPreset = false
      this.calculateTotals()
    }
  }
}
</script>

<style scoped>
.tele-sales-page {
  padding: 0.25rem 0;
}

.tele-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.tele-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.tele-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tele-toolbar__accent {
  width: 4px;
  height: 22px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.tele-toolbar__meta {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-size: 0.82rem;
}

.tele-toolbar__updated {
  color: #6b7280;
}

.tele-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.tele-grid {
  row-gap: 1rem;
}

.tele-main {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tele-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.tele-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.tele-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.01em;
}

.tele-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.tele-head-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  cursor: pointer;
  user-select: none;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.tele-head-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #16a07a;
  cursor: pointer;
  margin: 0;
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tele-section__body {
  padding: 1rem;
}

.tele-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.tele-label__required {
  margin-left: 0.2rem;
  color: #dc2626;
  font-weight: 700;
}

.tele-label-inline {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.15rem;
}

.tele-cod-block-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 500;
  line-height: 1.3;
}

.tele-cod-block-note svg {
  flex: 0 0 auto;
  color: #b91c1c;
}

.tele-shipping-zero {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.4rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  color: #78350f;
  border-radius: 8px;
  font-size: 0.74rem;
  line-height: 1.35;
}

.tele-shipping-zero > svg {
  flex: 0 0 auto;
  color: #b45309;
  margin-top: 0.15rem;
}

.tele-shipping-zero__body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1 1 auto;
  min-width: 0;
}

.tele-shipping-zero__body strong {
  font-weight: 700;
  color: #92400e;
}

.tele-shipping-zero__btn {
  flex: 0 0 auto;
  align-self: center;
  padding: 0.32rem 0.7rem;
  border-radius: 6px;
  border: 1px solid #f59e0b;
  background: #ffffff;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.tele-shipping-zero__btn:hover {
  background: #f59e0b;
  border-color: #d97706;
  color: #ffffff;
}

.tele-shipping-zero__btn:focus { outline: none; }
.tele-shipping-zero__btn::-moz-focus-inner { border: 0; }
.tele-shipping-zero__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.32);
}

.is-attention-pulse >>> .app-input__field {
  animation: telePincodeAttention 0.6s ease-in-out 0s 3;
  border-color: #f59e0b;
}

@keyframes telePincodeAttention {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
    border-color: #f59e0b;
  }
  50% {
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.35);
    border-color: #d97706;
  }
}

/* Generic ring used when "Take me there" jumps to any checklist field. */
.tele-locate-pulse {
  position: relative;
  border-radius: 12px;
  animation: teleLocateAttention 0.7s ease-in-out 0s 3;
}

@keyframes teleLocateAttention {
  0%,
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
  50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.4); }
}

.tele-recent-order-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0.55rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  border-radius: 10px;
  font-size: 0.78rem;
  line-height: 1.4;
}

.tele-recent-order-note svg {
  flex: 0 0 auto;
  color: #b45309;
  margin-top: 1px;
}

.tele-recent-order-note__text {
  min-width: 0;
}

.tele-recent-order-note__text strong {
  color: #78350f;
  font-weight: 700;
  margin-right: 0.2rem;
}

.tele-recent-order-note__list {
  display: inline;
  margin-left: 0.25rem;
}

.tele-recent-order-note__chip {
  display: inline;
  margin-right: 0.3rem;
}

.tele-recent-order-note__chip-id {
  font-weight: 700;
  color: #78350f;
}

.tele-recent-order-note__chip-date {
  color: #92400e;
  margin-left: 0.2rem;
}

.tele-high-priority-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0.55rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 10px;
  font-size: 0.78rem;
  line-height: 1.4;
}

.tele-high-priority-note svg {
  flex: 0 0 auto;
  color: #dc2626;
  margin-top: 1px;
}

.tele-high-priority-note__text {
  min-width: 0;
}

.tele-high-priority-note__text strong {
  color: #7f1d1d;
  font-weight: 700;
}

.tele-sales-page .form-group {
  margin-bottom: 0.85rem;
}

.tele-sales-page .form-control {
  font-size: 0.85rem;
  border-radius: 8px;
  min-height: 36px;
  height: 36px;
  border-color: #d8dde6;
}

.tele-sales-page .form-control:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.tele-sales-page select.form-control {
  padding: 0.3rem 0.6rem;
  line-height: 1.2;
  color: #2f3640;
}

.tele-sales-page textarea.form-control {
  min-height: 64px;
  height: auto;
}

.tele-sales-page .btn {
  border-radius: 8px;
  font-weight: 600;
}

/* Phone search row */
.phone-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.phone-row__country {
  flex: 0 0 130px;
}

.phone-row__input {
  flex: 1 1 200px;
}

.phone-row__btn {
  min-width: 120px;
  height: 36px;
}

.phone-row__hint {
  display: block;
  margin-top: 0.4rem;
  min-height: 1rem;
  font-size: 0.78rem;
}

.tele-label__status {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: none;
  letter-spacing: 0;
}

.tele-label__status--warn {
  color: #b45309;
}

/* Product add bar */
.product-add {
  display: grid;
  grid-template-columns: 1fr 110px 1fr auto;
  gap: 0.6rem;
  align-items: end;
  padding: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.25rem;
}

.product-add__field {
  display: flex;
  flex-direction: column;
}

.product-add__field--qty {
  max-width: 110px;
}

.product-add__action {
  min-width: 100px;
}

.product-add__action .btn {
  height: 36px;
}

/* Item list */
.item-list {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.item-list__row {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 110px 110px auto;
  gap: 0.85rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.item-list__row:hover {
  border-color: #c7d2e0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.item-list__thumb {
  width: 48px;
  height: 48px;
}

.item-list__thumb-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e6eaf3;
  background: #f8fafc;
}

.item-list__thumb-img--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aab2c0;
}

.item-list__main {
  min-width: 0;
}

.item-list__name {
  font-weight: 600;
  color: #1f2a44;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-list__meta {
  font-size: 0.76rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

.item-list__qty {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.item-list__total {
  display: flex;
  flex-direction: column;
}

.item-list__total strong {
  color: #1f2a44;
  font-size: 0.95rem;
}

.item-list__remove {
  white-space: nowrap;
}

.tele-empty {
  text-align: center;
  padding: 1.25rem;
  color: #6b7280;
  font-size: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.85rem;
}

.tele-field-hint {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.74rem;
  color: #6b7280;
}

.tele-section__hint--required {
  color: #b45309;
  font-weight: 600;
}

.crop-required-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding: 0.6rem 0.75rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  color: #9a3412;
  font-size: 0.8rem;
  line-height: 1.4;
}

.crop-required-note svg {
  flex: 0 0 auto;
  margin-top: 1px;
  color: #ea580c;
}

/* Crop multi-select chips */
.crop-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.crop-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.32rem 0.4rem 0.32rem 0.75rem;
  background: #f0faf6;
  border: 1px solid #cdeadd;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #0e5b46;
}

.crop-chip__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(14, 91, 70, 0.1);
  color: #0e5b46;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.crop-chip__remove:hover {
  background: #dc2626;
  color: #ffffff;
}

.crop-chip__remove:focus {
  outline: none;
}

.crop-chip__remove::-moz-focus-inner {
  border: 0;
}

.crop-chip__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.crop-empty {
  margin-top: 0.85rem;
}

/* Discount */
.discount-row {
  display: flex;
  gap: 0.5rem;
}

.discount-row.is-locked { opacity: 0.85; }

.discount-row__mode {
  max-width: 80px;
}

/* Eligible-discount banner — mirrors OrderForm.vue so non-privileged
   tele-sales staff see the same lock + toggle UI everywhere. */
.preset-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 0.7rem 0.95rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  border: 1px solid #c7ebda;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.06);
  flex-wrap: wrap;
}

.preset-banner__info { display: flex; align-items: center; gap: 0.65rem; min-width: 0; }

.preset-banner__dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #16a07a;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.18);
  flex-shrink: 0;
}

.preset-banner__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.preset-banner__info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #c7ebda;
  background: #ffffff;
  color: #0e5b46;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.preset-banner__info-btn:hover {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.12);
}

.preset-banner__info-btn:focus { outline: none; }
.preset-banner__info-btn::-moz-focus-inner { border: 0; }
.preset-banner__info-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.preset-banner__sub { font-size: 0.75rem; color: #4b5563; margin-top: 0.1rem; }

.preset-banner__toggle {
  display: inline-flex; align-items: center; gap: 0.55rem;
  margin: 0; cursor: pointer; user-select: none;
}

.preset-banner__toggle input {
  position: absolute; width: 1px; height: 1px;
  opacity: 0; pointer-events: none;
}

.preset-banner__track {
  position: relative;
  width: 38px; height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.18s ease;
  flex-shrink: 0;
}

.preset-banner__thumb {
  position: absolute; top: 2px; left: 2px;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  transition: left 0.18s ease;
}

.preset-banner__toggle.is-on .preset-banner__track {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.preset-banner__toggle.is-on .preset-banner__thumb { left: 18px; }

.preset-banner__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  min-width: 50px;
}

/* Over-the-parcel-cap notice. Amber, not green: this is a constraint the
   agent has to act on, not a reward — the brand gradient is reserved for
   primary CTAs. */
.split-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
}

.split-banner__icon {
  flex: 0 0 auto;
  margin-top: 0.05rem;
  color: #b45309;
  display: inline-flex;
}

.split-banner__text {
  font-size: 0.82rem;
  line-height: 1.5;
  color: #92400e;
}

.split-banner__text strong { color: #78350f; font-weight: 700; }

.preset-pill {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.06rem 0.4rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  background: #dff5ea;
  color: #0e5b46;
  letter-spacing: 0.02em;
}

.preset-pill--muted { background: #f1f5f9; color: #475569; }

.summary-line__label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.summary-distance-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  border: 1px solid #c7ebda;
  background: #ecfdf5;
  color: #0e5b46;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.summary-distance-chip:hover {
  background: #dff5ea;
  border-color: #16a07a;
  color: #0e5b46;
}

.summary-distance-chip:focus { outline: none; }
.summary-distance-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.25);
}

.summary-distance-chip svg { flex: 0 0 auto; opacity: 0.85; }

/* Charge tiles */
.amount-due {
  margin-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
}

.amount-due__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.amount-due__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.amount-due__value {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
  line-height: 1.15;
}

.amount-due__hint {
  font-size: 0.72rem;
  font-weight: 500;
  color: #6b7280;
  text-align: right;
}

/* Aside / sticky */
.tele-aside {
  display: flex;
  flex-direction: column;
}

.tele-sticky {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1rem;
}

/* Call section */
.call-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #cbd5e1;
}

.call-dot.is-on {
  background: #16a07a;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.2);
  animation: tele-pulse 1.4s infinite;
}

@keyframes tele-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.2); }
  50%      { box-shadow: 0 0 0 8px rgba(22, 160, 122, 0); }
}

.call-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  align-items: stretch;
}

.call-timer {
  font-size: 2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1f2a44;
  letter-spacing: 0.04em;
  text-align: center;
  padding: 0.5rem 0;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef1f7;
}

.call-actions {
  display: flex;
  gap: 0.5rem;
}

/* Summary */
.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #eef1f7;
  font-size: 0.85rem;
  color: #4b5563;
}

.summary-line strong {
  color: #1f2a44;
}

.summary-line--total {
  margin-top: 0.4rem;
  padding-top: 0.6rem;
  border-top: 2px solid #1f2a44;
  border-bottom: 0;
  font-size: 0.95rem;
}

.summary-line--total strong {
  font-size: 1.1rem;
}

.summary-line--note {
  margin-top: 0.15rem;
  padding: 0.25rem 0 0;
  border-bottom: 0;
  color: #6b7280;
  font-size: 0.74rem;
  font-style: italic;
}

.summary-line--note strong {
  color: #6b7280;
  font-weight: 600;
  font-style: normal;
}

.summary-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.summary-actions__primary {
  height: 44px;
  font-size: 0.95rem;
}

/* Form not yet complete: the button stays clickable (clicking surfaces
   the "what's missing" checklist) but is visually softened so agents
   read it as "not ready" rather than "broken". */
.summary-actions__primary--pending {
  filter: saturate(0.55) brightness(1.02);
  opacity: 0.92;
}

.summary-actions__primary--pending:hover {
  filter: saturate(0.8) brightness(1.03);
  opacity: 1;
}

.summary-actions__whatsapp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  height: 40px;
  padding: 0 0.95rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #15a25b 0%, #25d366 100%);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.summary-actions__whatsapp:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(37, 211, 102, 0.38);
  filter: brightness(1.05);
}

.summary-actions__whatsapp:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.summary-actions__whatsapp:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.summary-actions__reset {
  margin-top: 0.25rem;
}

/* Contact history */
.history-section .tele-section__head h2 {
  color: #0e5b46;
}

.history-section__head {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.history-section__head:hover {
  background: #f4f8f6;
}

.history-section__head:focus {
  outline: none;
  background: #f0fbf6;
}

.history-section__head-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}

.history-section__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.75rem;
  border: 1px solid #d6ebe1;
  transition: transform 0.2s ease;
}

.history-section__chevron.is-open {
  transform: rotate(180deg);
}

.history-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.85rem;
}

.history-stat {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0fbf6 0%, #e7f5ee 100%);
  border: 1px solid #d6ebe1;
}

.history-stat__label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}

.history-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0e5b46;
}

.history-stat__value--small {
  font-size: 0.82rem;
  line-height: 1.2;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.history-col {
  min-width: 0;
}

.history-title {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.history-item {
  padding: 0.55rem 0.7rem;
  border: 1px solid #e6eaf3;
  border-radius: 8px;
  background: #ffffff;
}

.history-item--call {
  border-left-width: 3px;
}

.history-item--call.is-success { border-left-color: #16a07a; }
.history-item--call.is-info    { border-left-color: #2563eb; }
.history-item--call.is-warn    { border-left-color: #d97706; }
.history-item--call.is-muted   { border-left-color: #94a3b8; }
.history-item--call.is-default { border-left-color: #cbd5e1; }
.history-item--call.is-danger  { border-left-color: #dc2626; }

.history-item--call.is-high-priority {
  background: #fef2f2;
  border-color: #fecaca;
  border-left-color: #dc2626;
}

/* A "sold" call paired with its recent order — both rows go green. */
.history-item.is-sold-match {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.history-item--call.is-sold-match {
  border-left-color: #16a07a;
}

/* A recent order (placed inside the duplicate-order window) → red card with
   white text, regardless of any matching sold call. Placed after the
   sold-match rules so red wins over green. */
.history-item.is-recent-order {
  background: #d9534f;
  border-color: #d9534f;
}

.history-item.is-recent-order .history-item__strong,
.history-item.is-recent-order .history-item__amount,
.history-item.is-recent-order .history-item__sub,
.history-item.is-recent-order .history-dot,
.history-item.is-recent-order .history-products__name,
.history-item.is-recent-order .history-products__qty {
  color: #ffffff;
}

/* Payment-status spans use CoreUI's .text-success / .text-warning utilities,
   which set color with !important — override them so they read white on red. */
.history-item.is-recent-order .history-item__sub span {
  color: #ffffff !important;
}

.history-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.history-item__date {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}

.history-item__time {
  margin-left: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
}

.history-item__strong {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
}

.history-item__amount {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
}

.history-item__sub {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.history-dot {
  margin: 0 0.25rem;
  color: #cbd5e1;
}

.history-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.history-pill.is-success {
  background: #dff5ea;
  color: #0e5b46;
}

.history-pill.is-info {
  background: #e0edff;
  color: #1e40af;
}

.history-pill.is-warn {
  background: #fef3c7;
  color: #92400e;
}

.history-pill.is-muted {
  background: #f1f5f9;
  color: #64748b;
}

.history-pill.is-default {
  background: #eef1f7;
  color: #4b5563;
}

.history-products {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0.4rem 0 0;
  border-top: 1px dashed #eef1f7;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.history-products li {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: #4b5563;
}

.history-products__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-products__qty {
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
}

.history-empty {
  padding: 0.85rem;
  text-align: center;
  font-size: 0.8rem;
  color: #6b7280;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
}

/* Medical demographics heads-up — informational, never blocking */
.med-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  margin-bottom: 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
}

.med-warning__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #3b82f6;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.8rem;
  font-family: Georgia, serif;
}

.med-warning__body {
  flex: 1 1 auto;
  min-width: 0;
}

.med-warning__title {
  display: block;
  font-size: 0.82rem;
  color: #1e3a8a;
}

.med-warning__text {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: #1e40af;
  line-height: 1.4;
}

.med-warning__action {
  flex: 0 0 auto;
  padding: 0.35rem 0.8rem;
  background: #ffffff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  color: #1e40af;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.med-warning__action:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.med-warning__action:focus {
  outline: none;
}

.med-warning__action:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.32);
}

/* Medical case history accordion */
.med-history {
  margin-bottom: 1.1rem;
}

.med-history__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.med-history__title {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.med-history__count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0e5b46;
}

.med-history__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.med-history__item {
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.med-history__item.is-open {
  border-color: #b9dfcd;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.med-history__item-head {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  min-height: 54px;
  padding: 0.6rem 0.85rem;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.med-history__item-head:hover {
  background: #f4f8f6;
}

.med-history__item-head:focus {
  outline: none;
}

.med-history__item-head:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.med-history__item.is-open .med-history__item-head {
  background: #f0fbf6;
}

.med-history__item-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.med-history__item-meta {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
}

.med-history__date {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  white-space: nowrap;
}

.med-history__author {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 18rem;
}

.med-history__preview {
  font-size: 0.8rem;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.med-history__chevron {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.78rem;
  border: 1px solid #d6ebe1;
  transition: transform 0.2s ease;
}

.med-history__chevron.is-open {
  transform: rotate(180deg);
}

.med-history__item-body {
  padding: 0.75rem 0.95rem 0.95rem;
  border-top: 1px solid #e6eaf3;
  background: #fbfcfe;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.med-history__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.med-history__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}

.med-history__text {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #1f2a44;
  white-space: pre-wrap;
  word-break: break-word;
}

.med-history__empty {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.med-history__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.6rem;
  padding: 0.4rem 0.85rem;
  background: #ffffff;
  border: 1px solid #d6ebe1;
  border-radius: 999px;
  color: #0e5b46;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.med-history__more:hover {
  background: #f0fbf6;
  border-color: #b9dfcd;
}

.med-history__more:focus {
  outline: none;
}

.med-history__more:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.med-current--with-history {
  padding-top: 1rem;
  margin-top: 1rem;
  border-top: 1px dashed #e5e9f2;
}

.med-current__title {
  margin: 0 0 0.6rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

/* Medical report attachments */
.med-attach {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.med-attach__picker {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  align-self: flex-start;
  height: 36px;
  padding: 0 0.9rem;
  background: #ffffff;
  border: 1px dashed #b9dfcd;
  border-radius: 8px;
  color: #0e5b46;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.med-attach__picker:hover {
  background: #f0fbf6;
  border-color: #16a07a;
}

.med-attach__picker.is-busy {
  opacity: 0.7;
  cursor: progress;
}

.med-attach__picker:focus-within {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.med-attach__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.med-attach__error {
  margin: 0;
  font-size: 0.78rem;
  color: #b91c1c;
}

/* Payment proof block — a prominent attention card under the Amount To Pay
   tile, only for bank/UPI approval-queue methods. Amber + pulsing while no
   proof is attached so no staff member can miss it; flips green once a
   screenshot lands. Reuses the med-attach gallery internals. */
.pay-proof {
  margin-top: 1rem;
  padding: 0.95rem 1rem 1rem;
  border-radius: 12px;
  border: 2px solid transparent;
}

.pay-proof--empty {
  background: linear-gradient(180deg, #fffbeb 0%, #fff7e6 100%);
  border-color: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12);
  animation: payProofPulse 2.2s ease-in-out infinite;
}

.pay-proof--ready {
  background: linear-gradient(180deg, #f0fdf6 0%, #ecfdf3 100%);
  border-color: #16a07a;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.1);
}

@keyframes payProofPulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12); }
  50%      { box-shadow: 0 0 0 7px rgba(245, 158, 11, 0.22); }
}

@media (prefers-reduced-motion: reduce) {
  .pay-proof--empty { animation: none; }
}

.pay-proof__head {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-bottom: 0.8rem;
}

.pay-proof__badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: #ffffff;
}

.pay-proof--empty .pay-proof__badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 10px rgba(217, 119, 6, 0.3);
}

.pay-proof--ready .pay-proof__badge {
  background: linear-gradient(135deg, #16a07a 0%, #0e5b46 100%);
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28);
}

.pay-proof__head-text {
  min-width: 0;
}

.pay-proof__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 0.25rem;
  font-size: 0.98rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #1f2a44;
}

.pay-proof__pill {
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
}

.pay-proof__pill--req {
  color: #92400e;
  background: #fde68a;
  border: 1px solid #f59e0b;
}

.pay-proof__pill--ok {
  color: #0e5b46;
  background: #d1fae5;
  border: 1px solid #16a07a;
}

.pay-proof__subtitle {
  margin: 0;
  font-size: 0.79rem;
  line-height: 1.4;
  color: #5b6472;
}

.pay-proof__picker {
  height: 40px;
  font-weight: 700;
}

.pay-proof--empty .pay-proof__picker {
  border-color: #f59e0b;
  color: #92400e;
  background: #fffdf6;
}

.pay-proof--empty .pay-proof__picker:hover {
  border-color: #d97706;
  background: #fff7e6;
}

/* "OR" divider between the screenshot uploader and the written note */
.pay-proof__or {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0.85rem 0 0.65rem;
}

.pay-proof__or::before,
.pay-proof__or::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(15, 23, 42, 0.1);
}

.pay-proof__or span {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.pay-proof__note {
  margin-top: 0.1rem;
}

/* Squared thumbnail gallery */
.med-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 0.5rem;
}

.med-thumb {
  position: relative;
  aspect-ratio: 1 / 1;
}

.med-thumb__btn {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #f7faf9;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.med-thumb__btn:hover {
  border-color: #16a07a;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.16);
  transform: translateY(-1px);
}

.med-thumb__btn:focus {
  outline: none;
}

.med-thumb__btn:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.med-thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.med-thumb__pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  height: 100%;
  padding: 0.4rem;
}

.med-thumb__pdf-badge {
  padding: 0.1rem 0.4rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-radius: 4px;
  color: #ffffff;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.med-thumb__pdf-name {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.66rem;
  font-weight: 600;
  color: #475569;
}

.med-thumb__remove {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 50%;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.16);
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.med-thumb__remove:hover {
  background: #fef2f2;
  border-color: #ef4444;
  color: #b91c1c;
}

.med-thumb__remove:focus {
  outline: none;
}

.med-thumb__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.3);
}

/* Lightbox preview modal */
.med-preview {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.72);
}

.med-preview__dialog {
  display: flex;
  flex-direction: column;
  width: min(960px, 100%);
  max-height: calc(100vh - 3rem);
  background: #0f172a;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.med-preview__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  background: #111c33;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.med-preview__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #e2e8f0;
  font-size: 0.85rem;
  font-weight: 600;
}

.med-preview__bar-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
}

.med-preview__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.08);
  border: 0;
  border-radius: 8px;
  color: #e2e8f0;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.med-preview__icon-btn:hover {
  background: rgba(255, 255, 255, 0.18);
}

.med-preview__icon-btn:focus {
  outline: none;
}

.med-preview__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.45);
}

.med-preview__stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.75rem;
}

.med-preview__img {
  max-width: 100%;
  max-height: calc(100vh - 9rem);
  object-fit: contain;
  border-radius: 6px;
}

.med-preview__pdf {
  width: 100%;
  height: calc(100vh - 9rem);
  border: 0;
  border-radius: 6px;
  background: #ffffff;
}

.med-preview__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  color: #ffffff;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.med-preview__nav:hover {
  background: rgba(22, 160, 122, 0.85);
}

.med-preview__nav:focus {
  outline: none;
}

.med-preview__nav:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.45);
}

.med-preview__nav--prev {
  left: 0.6rem;
}

.med-preview__nav--next {
  right: 0.6rem;
}

.med-preview__count {
  padding: 0.5rem 0.9rem;
  background: #111c33;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #94a3b8;
  font-size: 0.78rem;
  text-align: center;
}

.med-preview-fade-enter-active,
.med-preview-fade-leave-active {
  transition: opacity 0.18s ease;
}

.med-preview-fade-enter,
.med-preview-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1199px) {
  .history-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .tele-sticky {
    position: static;
  }

  .history-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .history-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .tele-toolbar {
    align-items: flex-start;
  }

  .product-add {
    grid-template-columns: 1fr 1fr;
  }

  .product-add__field--qty {
    max-width: none;
  }

  .product-add__action {
    grid-column: 1 / -1;
  }

  .item-list__row {
    grid-template-columns: 48px 1fr 1fr;
    gap: 0.5rem 0.85rem;
  }

  .item-list__thumb {
    grid-row: 1 / 3;
  }

  .item-list__main {
    grid-column: 2 / -1;
  }

  .item-list__remove {
    grid-column: 1 / -1;
  }

  .phone-row__country {
    flex: 1 1 120px;
  }

  .phone-row__btn {
    flex: 1 1 100%;
  }

  .amount-due {
    flex-wrap: wrap;
  }

  .amount-due__hint {
    width: 100%;
    text-align: left;
  }
}

/* Sale success modal */
.sale-success {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.sale-success__panel {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.85rem 1.5rem 1.4rem;
  text-align: center;
  animation: sale-success-pop 0.32s cubic-bezier(0.18, 1.2, 0.4, 1) both;
}

.sale-success__close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sale-success__close:hover {
  background: #f3f4f6;
  color: #1f2a44;
}

.sale-success__animation {
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sale-success__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(22, 160, 122, 0.22);
  transform: scale(0);
  animation: sale-success-ring 1.4s ease-out 0.05s infinite;
}

.sale-success__ring--delay {
  animation-delay: 0.7s;
}

.sale-success__check {
  position: relative;
  width: 96px;
  height: 96px;
  filter: drop-shadow(0 4px 10px rgba(22, 160, 122, 0.28));
}

.sale-success__check-circle {
  fill: none;
  stroke: #16a07a;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 176;
  stroke-dashoffset: 176;
  transform-origin: center;
  transform: rotate(-90deg);
  animation: sale-success-draw 0.55s ease-out 0.1s forwards;
}

.sale-success__check-tick {
  fill: none;
  stroke: #16a07a;
  stroke-width: 4.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: sale-success-draw-tick 0.32s ease-out 0.55s forwards;
}

.sale-success__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
}

.sale-success__subtitle {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: #4b5563;
}

.sale-success__notice {
  margin: 0 0 1rem;
  padding: 0.55rem 0.85rem;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  text-align: left;
}

.sale-success__celebrate {
  margin: 0 0 1.1rem;
  padding: 0.9rem 0.95rem 0.95rem;
  background: linear-gradient(135deg, rgba(22, 160, 122, 0.10) 0%, rgba(37, 211, 102, 0.14) 100%);
  border: 1px solid rgba(22, 160, 122, 0.28);
  border-radius: 12px;
  text-align: left;
  animation: sale-success-celebrate-in 0.45s ease-out 0.35s both;
}

.sale-success__celebrate-badge {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.sale-success__celebrate-emoji {
  font-size: 1.7rem;
  line-height: 1;
  flex-shrink: 0;
  animation: sale-success-celebrate-pop 0.6s ease-out 0.4s both;
}

.sale-success__celebrate-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.sale-success__celebrate-line {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0e5b46;
  letter-spacing: -0.005em;
}

.sale-success__celebrate-line strong {
  color: #0a4a39;
  font-weight: 800;
}

.sale-success__celebrate-sub {
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.35;
}

.sale-success__quote {
  margin: 0.7rem 0 0;
  padding-top: 0.7rem;
  border-top: 1px dashed rgba(22, 160, 122, 0.32);
  font-size: 0.82rem;
  font-style: italic;
  color: #1f2a44;
  line-height: 1.45;
}

@keyframes sale-success-celebrate-in {
  0%   { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes sale-success-celebrate-pop {
  0%   { transform: scale(0.4) rotate(-12deg); }
  60%  { transform: scale(1.25) rotate(8deg); }
  100% { transform: scale(1) rotate(0); }
}

@media (prefers-reduced-motion: reduce) {
  .sale-success__celebrate,
  .sale-success__celebrate-emoji {
    animation: none;
  }
}

.sale-success__meta {
  margin: 0 auto 1.25rem;
  padding: 0.6rem 0.85rem;
  background: #f8fafc;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  display: grid;
  gap: 0.35rem;
  text-align: left;
  font-size: 0.82rem;
}

.sale-success__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.sale-success__meta-row dt {
  margin: 0;
  color: #6b7280;
  font-weight: 500;
}

.sale-success__meta-row dd {
  margin: 0;
  color: #1f2a44;
  font-weight: 600;
}

.sale-success__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.sale-success__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 0.9rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.sale-success__btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sale-success__btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.sale-success__btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f0fbf6;
}

.sale-success__btn--primary {
  background: linear-gradient(135deg, #15a25b 0%, #25d366 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.32);
}

.sale-success__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4);
  filter: brightness(1.05);
}

.sale-success__btn--primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

/* Second action row: a single full-width button, spaced from the row above. */
.sale-success__actions + .sale-success__actions {
  margin-top: 0.6rem;
  grid-template-columns: 1fr;
}

.sale-success__btn--accounts {
  background: #ffffff;
  border-color: #16a07a;
  color: #0e5b46;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.sale-success__btn--accounts:hover:not(:disabled) {
  transform: translateY(-1px);
  background: #f0fbf6;
  box-shadow: 0 4px 12px rgba(22, 160, 122, 0.22);
}

.sale-success__btn--accounts:active:not(:disabled) {
  transform: translateY(0);
}

.sale-success__btn--accounts:focus { outline: none; }
.sale-success__btn--accounts::-moz-focus-inner { border: 0; }
.sale-success__btn--accounts:focus-visible {
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sale-success-fade-enter-active,
.sale-success-fade-leave-active {
  transition: opacity 0.18s ease;
}

.sale-success-fade-enter,
.sale-success-fade-leave-to {
  opacity: 0;
}

@keyframes sale-success-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes sale-success-draw {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-draw-tick {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-ring {
  0%   { transform: scale(0.4); opacity: 0.55; }
  80%  { opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
}

@media (max-width: 480px) {
  .sale-success__actions {
    grid-template-columns: 1fr;
  }
}

/* ---- Feedback-call celebration ------------------------------------ */
/* Same overlay geometry as .sale-success, but the backdrop carries an
   endless applause stream (EmojiRise) rising bottom → top around the
   panel, which stays fully readable on top of it. */
.fb-success {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.fb-success__panel {
  position: relative;
  /* Above the EmojiRise canvas (z-index 1300) so the copy never sits
     behind a passing emoji. */
  z-index: 1400;
  width: 100%;
  max-width: 460px;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.85rem 1.5rem 1.4rem;
  text-align: center;
  animation: sale-success-pop 0.32s cubic-bezier(0.18, 1.2, 0.4, 1) both;
}

.fb-success__close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.fb-success__close:hover {
  background: #f1f5f9;
  color: #1f2a44;
}

.fb-success__close:focus { outline: none; }
.fb-success__close::-moz-focus-inner { border: 0; }
.fb-success__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fb-success__clap {
  position: relative;
  width: 84px;
  height: 84px;
  margin: 0 auto 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fb-success__clap-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(22, 160, 122, 0.45);
  animation: sale-success-ring 1.8s ease-out infinite;
}

.fb-success__clap-ring--delay {
  animation-delay: 0.9s;
}

.fb-success__clap-glyph {
  font-size: 2.6rem;
  line-height: 1;
  animation: fb-success-clap 1.8s ease-in-out infinite;
}

.fb-success__title {
  margin: 0 0 0.55rem;
  font-size: 1.18rem;
  font-weight: 700;
  color: #1f2a44;
}

.fb-success__message {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  line-height: 1.55;
  color: #334155;
  background: #f0fbf6;
  border: 1px solid #cdebdd;
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  text-align: left;
}

.fb-success__meta {
  margin: 0 0 0.85rem;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
  text-align: left;
}

.fb-success__row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eef1f7;
}

.fb-success__row:last-child { border-bottom: none; }
.fb-success__row:nth-child(odd) { background: #fafbfd; }

.fb-success__row dt {
  margin: 0;
  font-size: 0.76rem;
  font-weight: 600;
  color: #6b7280;
}

.fb-success__row dd {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  word-break: break-word;
}

.fb-success__note {
  margin: 0 0 1rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.fb-success__actions {
  display: grid;
  grid-template-columns: 1fr;
}

.fb-success__btn {
  height: 38px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.24);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.fb-success__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(14, 91, 70, 0.32);
  filter: brightness(1.05);
}

.fb-success__btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.fb-success__btn:focus { outline: none; }
.fb-success__btn::-moz-focus-inner { border: 0; }
.fb-success__btn:focus-visible {
  outline: none;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.24), 0 0 0 3px rgba(22, 160, 122, 0.35);
}

.fb-success-fade-enter-active,
.fb-success-fade-leave-active {
  transition: opacity 0.18s ease;
}

.fb-success-fade-enter,
.fb-success-fade-leave-to {
  opacity: 0;
}

/* Scale-only pulse — the glyph is never rotated or tilted. */
@keyframes fb-success-clap {
  0%, 100% { transform: scale(1); }
  25%      { transform: scale(1.12); }
  50%      { transform: scale(1); }
  75%      { transform: scale(1.12); }
}

@media (prefers-reduced-motion: reduce) {
  .fb-success__clap-ring,
  .fb-success__clap-glyph {
    animation: none;
  }
}

/* Save Call Log success modal body (rendered inside AppConfirmDialog slot) */
.cls-success {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.cls-success__lead {
  margin: 0;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.45;
}

.cls-success__lead strong {
  color: #1f2a44;
}

.cls-success__meta {
  margin: 0;
  padding: 0.55rem 0.8rem;
  background: #f8fafc;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  display: grid;
  gap: 0.3rem;
  font-size: 0.82rem;
}

.cls-success__row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.cls-success__row dt {
  margin: 0;
  color: #6b7280;
  font-weight: 500;
}

.cls-success__row dd {
  margin: 0;
  color: #1f2a44;
  font-weight: 600;
}

.cls-success__note {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

/* ---- Estimate CTA (in summary actions) ---- */
.estimate-cta__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 1.1rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #128c4b 0%, #25d366 100%);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(18, 140, 75, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.estimate-cta__btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(18, 140, 75, 0.3);
}

.estimate-cta__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.estimate-cta__btn:focus { outline: none; }
.estimate-cta__btn::-moz-focus-inner { border: 0; }
.estimate-cta__btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(18, 140, 75, 0.22), 0 0 0 3px rgba(37, 211, 102, 0.3);
}

.estimate-cta__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: estimate-spin 0.7s linear infinite;
}

@keyframes estimate-spin {
  to { transform: rotate(360deg); }
}

/* ---- Off-screen estimate card (snapshotted by html2canvas) ----
   Positioned off-canvas but fully laid out so html2canvas can render it.
   Keep to solid colors + system fonts so the snapshot is deterministic. */
.estimate-capture {
  position: fixed;
  left: -10000px;
  top: 0;
  z-index: -1;
  width: 600px;
  padding: 28px;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1f2a44;
  box-sizing: border-box;
}

.est-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 3px solid #16a07a;
}

.est-brand {
  font-size: 22px;
  font-weight: 700;
  color: #0e5b46;
}

.est-badge {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #ffffff;
  background: #0e5b46;
  padding: 6px 12px;
  border-radius: 6px;
}

.est-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
  font-size: 13px;
  color: #4f5560;
}

.est-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 18px;
  font-size: 13px;
}

.est-table th {
  text-align: left;
  padding: 9px 10px;
  background: #f1f5f4;
  color: #4f5560;
  font-weight: 600;
  border-bottom: 1px solid #e5e9f2;
}

.est-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #eef1f7;
  color: #1f2a44;
}

.est-c-sl { width: 36px; text-align: center; }
.est-c-qty { width: 50px; text-align: center; }
.est-c-rate, .est-c-amt { width: 100px; text-align: right; }
.est-table th.est-c-qty { text-align: center; }
.est-table th.est-c-rate, .est-table th.est-c-amt { text-align: right; }

.est-totals {
  margin-top: 16px;
  margin-left: auto;
  width: 280px;
}

.est-total-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: #4f5560;
}

.est-total-row--discount { color: #b91c1c; }

.est-total-row--grand {
  margin-top: 6px;
  padding-top: 10px;
  border-top: 2px solid #16a07a;
  font-size: 16px;
  font-weight: 700;
  color: #0e5b46;
}

.est-foot {
  margin-top: 22px;
  padding-top: 12px;
  border-top: 1px solid #eef1f7;
  font-size: 11px;
  color: #9098a5;
  line-height: 1.5;
}

/* Manakkal Drugs India — blue accent variant. Agro Pharma / Veterinary keep
   the default green above. */
.estimate-capture--blue .est-head { border-bottom-color: #2563eb; }
.estimate-capture--blue .est-brand { color: #1e3a8a; }
.estimate-capture--blue .est-badge { background: #1e3a8a; }
.estimate-capture--blue .est-total-row--grand {
  border-top-color: #2563eb;
  color: #1e3a8a;
}

/* ---- Estimate preview modal ---- */
.estimate-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.estimate-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow: auto;
}

.estimate-modal >>> .modal-footer {
  background: #ffffff;
  border-top: 1px solid #eef1f7;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.estimate-modal__body {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.estimate-modal__img {
  width: 100%;
  height: auto;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  display: block;
}

.estimate-modal__hint {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.45;
}

.estimate-modal__btn {
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.estimate-modal__btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.estimate-modal__btn--ghost:hover {
  background: #f8fafc;
}

.estimate-modal__btn--primary {
  border: none;
  color: #ffffff;
  background: linear-gradient(135deg, #128c4b 0%, #25d366 100%);
  box-shadow: 0 2px 6px rgba(18, 140, 75, 0.22);
}

.estimate-modal__btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(18, 140, 75, 0.3);
}

.estimate-modal__btn:focus { outline: none; }
.estimate-modal__btn::-moz-focus-inner { border: 0; }
.estimate-modal__btn--ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.estimate-modal__btn--primary:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(18, 140, 75, 0.22), 0 0 0 3px rgba(37, 211, 102, 0.3);
}
</style>
